import React, { useState, useEffect, useRef, useCallback } from "react";
import { AreaChart, Area, BarChart, Bar, ComposedChart, LineChart, Line, ScatterChart, Scatter, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";
import { supabase, db } from "./supabaseClient";
import TabRaceBook from "./pages/TabRaceBook";
import TabRacePlan from "./pages/TabRacePlan";
import CoachPage from "./pages/CoachPage";
import { generateTrainingPlan, PLAN_TEMPLATES, TAPER_DEPTHS, PHASE_NAMES, PHASE_COLORS, PHASE_BG, raceTypeFromDistance } from "./lib/trail";
import { withRateLimit, RateLimitError } from "./lib/rateLimiter";
/* ═══════════════════════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════════════════════ */
/* ── Design Tokens injected via JS ─────────────────────────────── */
const T = {
  // ── Backgrounds ────────────────────────────────────────────────────────
  bg:           "#F6F1E8",   // Parchment — fond principal
  surface:      "#FFFFFF",   // White — cards, panels
  card:         "#FFFFFF",
  surfaceSub:   "#EDE8DD",   // Surface secondaire légèrement plus sombre
  border:       "#EAE4D8",   // Sand Border — séparateurs, bordures cards
  borderStrong: "#D8D0C0",

  // ── Brand ──────────────────────────────────────────────────────────────
  navy:         "#1A1510",   // Void Warm — fond sombre, textes primaires, wordmark
  navyMid:      "#3A3530",   // Void mid — bordures sombres
  gold:         "#C49A3C",   // Warm Gold — CTA, symbole, KPIs critiques
  goldLight:    "#FAF3E4",   // Gold teinté clair
  goldDark:     "#9A7828",   // Gold foncé — hover, ombres CTA
  lime:         "#C49A3C",   // alias gold (compatibilité)
  limeDeep:     "#9A7828",   // alias goldDark (compatibilité)
  accent:       "#C49A3C",

  // ── Sémantique ─────────────────────────────────────────────────────────
  green:        "#5A9E38",   // Trail Green — validation, positif, D+
  greenLight:   "#EEF6E8",
  orange:       "#C85C2A",   // Ochre Alert — effort, zones critiques, alertes
  orangeLight:  "#FCEEE6",
  red:          "#C85C2A",   // même ochre
  redLight:     "#FCEEE6",
  blue:         "#2563EB",   // bleu système (graphiques, zones)
  blueLight:    "#EFF6FF",
  purple:       "#7C3AED",
  purpleLight:  "#F5F3FF",

  // ── Textes ─────────────────────────────────────────────────────────────
  text:         "#1A1510",   // Void Warm — texte primaire
  textMid:      "#9A8F7E",   // Flint — labels, descriptions, metadata
  textLight:    "#B0A090",   // Pebble — placeholders, sec-labels, hints

  // ── Ombres ─────────────────────────────────────────────────────────────
  shadow:    "0 1px 0 rgba(26,21,16,0.06), 0 2px 8px rgba(26,21,16,0.07), 0 0 0 0.5px rgba(26,21,16,0.07)",
  shadowMd:  "0 2px 0 rgba(26,21,16,0.07), 0 6px 20px rgba(26,21,16,0.10), 0 0 0 0.5px rgba(26,21,16,0.07)",
  shadowLg:  "0 4px 0 rgba(26,21,16,0.08), 0 16px 48px rgba(26,21,16,0.14)",
  shadowInset: "inset 0 1px 3px rgba(26,21,16,0.08)",

  // ── Typographie ────────────────────────────────────────────────────────
  fontUI:   "'Bricolage Grotesque', sans-serif",   // Interface, navigation, textes
  fontData: "'Barlow Condensed', sans-serif",       // Données chiffrées, KPIs, temps
};

/* ── Global base styles + fonts ── */
const _apexGlobalStyle = (() => {
  if (typeof document === "undefined" || document.getElementById("apex-gs")) return;
  // Google Fonts fallback (fontsource local overrides this in production)
  const lk = document.createElement("link");
  lk.rel = "stylesheet";
  lk.href = "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,500;12..96,600;12..96,700&family=Barlow+Condensed:wght@500;600;700&display=swap";
  document.head.appendChild(lk);
  const st = document.createElement("style");
  st.id = "apex-gs";
  st.textContent = [
    "body{font-family:'Bricolage Grotesque',sans-serif;background:#F6F1E8;color:#1A1510;",
    "-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;}",
    "input,select,textarea,button{font-family:inherit;}",
    "::-webkit-scrollbar{width:5px;height:5px;}",
    "::-webkit-scrollbar-track{background:transparent;}",
    "::-webkit-scrollbar-thumb{background:#D8D0C0;border-radius:3px;}",
    "::-webkit-scrollbar-thumb:hover{background:#C49A3C;}",
  ].join("");
  document.head.appendChild(st);
})();

/* ═══════════════════════════════════════════════════════════════════════════
   SEED DATA
═══════════════════════════════════════════════════════════════════════════ */
const SEED_PROFILE = {
  prenom: "Alex", nom: "Martin", age: 32, sexe: "homme",
  taille: 178, poids: 73, masseGraisseuse: 14, travail: "bureau",
  niveauActivite: "modere", experienceCourse: "3ans",
  objectifPrincipal: "semi", objectifChrono: "1:45:00",
  objectifDate: "2026-04-05",
  fc_max: 192, fc_repos: 52, vma: 16.5, volumeCible: 50,
  onboarded: true,
};

const SEED_SLEEP = [
  { id:1, date:"2026-03-03", duree:7.2, qualite:78, profond:1.8, rem:1.4, heureCoucher:"23:10", heureLever:"06:22", note:"" },
  { id:2, date:"2026-03-04", duree:6.5, qualite:61, profond:1.2, rem:1.1, heureCoucher:"23:50", heureLever:"06:20", note:"Stress travail" },
  { id:3, date:"2026-03-05", duree:8.1, qualite:89, profond:2.1, rem:1.9, heureCoucher:"22:45", heureLever:"06:51", note:"" },
  { id:4, date:"2026-03-06", duree:7.8, qualite:84, profond:1.9, rem:1.7, heureCoucher:"22:55", heureLever:"06:45", note:"" },
  { id:5, date:"2026-03-07", duree:7.0, qualite:72, profond:1.5, rem:1.3, heureCoucher:"23:20", heureLever:"06:20", note:"" },
  { id:6, date:"2026-03-08", duree:8.3, qualite:91, profond:2.2, rem:2.0, heureCoucher:"22:30", heureLever:"06:48", note:"Récup trail" },
  { id:7, date:"2026-03-09", duree:7.5, qualite:80, profond:1.8, rem:1.6, heureCoucher:"23:00", heureLever:"06:30", note:"" },
  { id:8, date:"2026-03-10", duree:7.9, qualite:86, profond:2.0, rem:1.8, heureCoucher:"22:50", heureLever:"06:40", note:"" },
];

/* Journal bien-être quotidien
   poids: kg · energie: 1-10 · stress: 1-10 · courbatures: 1-10
   humeur: 1-5 · hydratation: litres · alcool: 0/1/2+ (unités la veille)
   motivation: 1-10 */
const SEED_WELLNESS = [
  { id:1,  date:"2026-02-25", poids:73.8, energie:7, stress:4, courbatures:3, humeur:3, hydratation:2.2, alcool:0, motivation:6, note:"" },
  { id:2,  date:"2026-02-26", poids:73.5, energie:6, stress:5, courbatures:4, humeur:3, hydratation:2.0, alcool:1, motivation:5, note:"" },
  { id:3,  date:"2026-02-27", poids:73.4, energie:7, stress:3, courbatures:2, humeur:4, hydratation:2.4, alcool:0, motivation:7, note:"" },
  { id:4,  date:"2026-02-28", poids:73.1, energie:7, stress:3, courbatures:4, humeur:4, hydratation:2.5, alcool:1, motivation:7, note:"" },
  { id:5,  date:"2026-03-01", poids:73.0, energie:8, stress:2, courbatures:3, humeur:5, hydratation:2.8, alcool:0, motivation:8, note:"Sortie longue ok" },
  { id:6,  date:"2026-03-02", poids:72.9, energie:6, stress:4, courbatures:5, humeur:3, hydratation:2.1, alcool:0, motivation:6, note:"Jambes lourdes" },
  { id:7,  date:"2026-03-03", poids:72.8, energie:7, stress:3, courbatures:3, humeur:4, hydratation:2.3, alcool:0, motivation:7, note:"" },
  { id:8,  date:"2026-03-04", poids:72.7, energie:5, stress:6, courbatures:4, humeur:3, hydratation:2.0, alcool:1, motivation:5, note:"Stress boulot" },
  { id:9,  date:"2026-03-05", poids:72.6, energie:8, stress:2, courbatures:2, humeur:5, hydratation:2.6, alcool:0, motivation:9, note:"" },
  { id:10, date:"2026-03-06", poids:72.5, energie:8, stress:2, courbatures:2, humeur:5, hydratation:2.5, alcool:0, motivation:8, note:"" },
  { id:11, date:"2026-03-07", poids:72.4, energie:7, stress:3, courbatures:3, humeur:4, hydratation:2.4, alcool:0, motivation:7, note:"" },
  { id:12, date:"2026-03-08", poids:72.5, energie:8, stress:2, courbatures:2, humeur:5, hydratation:2.7, alcool:0, motivation:8, note:"" },
  { id:13, date:"2026-03-09", poids:72.4, energie:8, stress:2, courbatures:1, humeur:5, hydratation:2.6, alcool:0, motivation:9, note:"" },
  { id:14, date:"2026-03-10", poids:72.3, energie:8, stress:2, courbatures:1, humeur:5, hydratation:2.5, alcool:0, motivation:8, note:"" },
];


const ACTIVITY_TYPES = [
  // ── Course à pied ──────────────────────────────────────────────────────
  { id: "fond",       label: "Endurance",    icon: "🏃", color: T.blue,    sport: "run"   },
  { id: "fractionne", label: "Fractionné",   icon: "⚡", color: "#C49A3C", sport: "run"   },
  { id: "tempo",      label: "Tempo",        icon: "🔥", color: T.orange,  sport: "run"   },
  { id: "longrun",    label: "Long run",     icon: "🛤️", color: T.purple,  sport: "run"   },
  { id: "recup",      label: "Récup course", icon: "🌿", color: T.green,   sport: "run"   },
  { id: "trail",      label: "Trail",        icon: "⛰️", color: T.textMid, sport: "run"   },
  { id: "race",       label: "Compétition",  icon: "🏆", color: T.red,     sport: "run"   },
  // ── Vélo ───────────────────────────────────────────────────────────────
  { id: "velo",       label: "Vélo route",   icon: "🚴", color: "#0EA5E9", sport: "velo"  },
  { id: "vtt",        label: "VTT / Gravel", icon: "🚵", color: "#0369A1", sport: "velo"  },
  { id: "home_trainer",label:"Home trainer", icon: "🏠", color: "#7DD3FC", sport: "velo"  },
  // ── Natation ───────────────────────────────────────────────────────────
  { id: "nage",       label: "Natation",     icon: "🏊", color: "#06B6D4", sport: "swim"  },
  { id: "open_water", label: "Open water",   icon: "🌊", color: "#0891B2", sport: "swim"  },
  // ── Renforcement ───────────────────────────────────────────────────────
  { id: "muscu",      label: "Musculation",  icon: "🏋️", color: "#8B5CF6", sport: "gym"   },
  { id: "yoga",       label: "Yoga / Mob.",  icon: "🧘", color: "#A78BFA", sport: "gym"   },
  { id: "core",       label: "Gainage / PPG",icon: "💪", color: "#7C3AED", sport: "gym"   },
  // ── Autre ──────────────────────────────────────────────────────────────
  { id: "marche",     label: "Marche / Rando",icon:"🥾", color: "#84CC16", sport: "other" },
  { id: "autre",      label: "Autre sport",  icon: "🎯", color: T.textLight,sport: "other" },
];
// Alias rétrocompatibilité (anciennes données)
const RUN_TYPES = ACTIVITY_TYPES;

// Coefficients TRIMP par sport
// La course = 1.0 (référence). Autres sports calibrés sur dépense réelle.
const TRIMP_SPORT_COEFF = {
  run: 1.00, velo: 0.75, swim: 0.85, gym: 0.55, other: 0.70,
};

const getSportFromType = (typeId) => {
  const found = ACTIVITY_TYPES.find(t => t.id === typeId);
  return found ? found.sport : "run";
};

// Détecte le type STRATA depuis la valeur "Activity Type" du CSV Garmin
const garminTypeToStrata = (garminType) => {
  const t = (garminType || "").toLowerCase();
  if (t.includes("trail"))                          return "trail";
  if (t.includes("run") || t.includes("course"))   return "fond";
  if (t.includes("treadmill") || t.includes("tapis")) return "fond";
  if (t.includes("vtt") || t.includes("mountain bike")) return "vtt";
  if (t.includes("cycling") || t.includes("vélo") || t.includes("velo") || t.includes("bike") || t.includes("road")) return "velo";
  if (t.includes("virtual") || t.includes("indoor cycling") || t.includes("home trainer")) return "home_trainer";
  if (t.includes("swim") || t.includes("nage") || t.includes("natation")) return "nage";
  if (t.includes("open water"))                     return "open_water";
  if (t.includes("strength") || t.includes("muscul") || t.includes("weight")) return "muscu";
  if (t.includes("yoga"))                           return "yoga";
  if (t.includes("walk") || t.includes("marche") || t.includes("hiking") || t.includes("rando")) return "marche";
  return "autre";
};

const SEED_RUNS = [
  { id:1,  date:"2026-02-10", type:"fond",       distance:10.1, duree:62,  fc_moy:122, fc_max:145, denivele:88,  ressenti:2, note:"EF Paris",           trimp:28.7,  cadence:172, surface:"route",  shoes_id:2, calories:620,  temp:9  },
  { id:2,  date:"2026-02-11", type:"fond",       distance:6.3,  duree:37,  fc_moy:130, fc_max:157, denivele:36,  ressenti:2, note:"EF Paris",           trimp:20.5,  cadence:174, surface:"route",  shoes_id:2, calories:392,  temp:8  },
  { id:3,  date:"2026-02-13", type:"longrun",    distance:14.7, duree:79,  fc_moy:144, fc_max:161, denivele:74,  ressenti:3, note:"Sortie longue",      trimp:52.7,  cadence:176, surface:"route",  shoes_id:1, calories:890,  temp:7  },
  { id:4,  date:"2026-02-15", type:"longrun",    distance:15.9, duree:87,  fc_moy:140, fc_max:162, denivele:83,  ressenti:3, note:"Sortie longue",      trimp:58.5,  cadence:175, surface:"route",  shoes_id:1, calories:952,  temp:10 },
  { id:5,  date:"2026-02-19", type:"fractionne", distance:11.0, duree:55,  fc_moy:147, fc_max:181, denivele:55,  ressenti:4, note:"VO2max 8x400m",      trimp:37.9,  cadence:184, surface:"piste",  shoes_id:1, calories:694,  temp:6  },
  { id:6,  date:"2026-02-22", type:"trail",      distance:26.9, duree:183, fc_moy:145, fc_max:172, denivele:541, ressenti:4, note:"Bronvaux Trail",     trimp:123.9, cadence:170, surface:"trail",  shoes_id:4, calories:1630, temp:5  },
  { id:7,  date:"2026-02-27", type:"tempo",      distance:12.4, duree:61,  fc_moy:146, fc_max:175, denivele:28,  ressenti:3, note:"Paris Run seuil",    trimp:41.4,  cadence:180, surface:"route",  shoes_id:1, calories:756,  temp:11 },
  { id:8,  date:"2026-02-28", type:"fond",       distance:7.9,  duree:43,  fc_moy:136, fc_max:155, denivele:53,  ressenti:2, note:"EF Paris",           trimp:26.5,  cadence:173, surface:"route",  shoes_id:2, calories:488,  temp:12 },
  { id:9,  date:"2026-03-01", type:"longrun",    distance:20.1, duree:111, fc_moy:151, fc_max:168, denivele:327, ressenti:3, note:"Sortie longue",      trimp:79.7,  cadence:177, surface:"route",  shoes_id:1, calories:1218, temp:8  },
  { id:10, date:"2026-03-04", type:"fond",       distance:6.3,  duree:34,  fc_moy:134, fc_max:157, denivele:61,  ressenti:2, note:"EF Paris",           trimp:20.5,  cadence:174, surface:"route",  shoes_id:2, calories:390,  temp:9  },
  { id:11, date:"2026-03-06", type:"tempo",      distance:14.3, duree:66,  fc_moy:158, fc_max:181, denivele:27,  ressenti:4, note:"Seuil 3x3km",        trimp:50.8,  cadence:182, surface:"route",  shoes_id:1, calories:882,  temp:10 },
  { id:12, date:"2026-03-08", type:"trail",      distance:20.6, duree:136, fc_moy:131, fc_max:151, denivele:359, ressenti:3, note:"Meudon Trail",       trimp:78.9,  cadence:171, surface:"trail",  shoes_id:4, calories:1244, temp:13 },
];


const SEED_HRV = [
  { date:"2026-03-03", hrv:74 }, { date:"2026-03-04", hrv:71 }, { date:"2026-03-05", hrv:78 },
  { date:"2026-03-06", hrv:82 }, { date:"2026-03-07", hrv:76 }, { date:"2026-03-08", hrv:79 },
  { date:"2026-03-09", hrv:86 }, { date:"2026-03-10", hrv:92 },
];


const SEED_EQUIP = [
  { id:1, categorie:"Chaussures route", nom:"Nike Vaporfly 3",      marque:"Nike",   km:312, kmMax:600,  achat:"2025-06-01", couleur:"#FF4444", actif:true },
  { id:2, categorie:"Chaussures route", nom:"Brooks Ghost 15",      marque:"Brooks", km:489, kmMax:800,  achat:"2025-02-15", couleur:"#2563EB", actif:true },
  { id:3, categorie:"Montre GPS",       nom:"Garmin Forerunner 965", marque:"Garmin", km:801, kmMax:null, achat:"2024-11-10", couleur:"#7C3AED", actif:true },
  { id:4, categorie:"Chaussures trail", nom:"Hoka Speedgoat 5",     marque:"Hoka",   km:156, kmMax:500,  achat:"2026-01-10", couleur:"#EA580C", actif:true },
];


/* ═══════════════════════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════════════════════ */
const fmtDate = (d, opts = { day: "2-digit", month: "short" }) =>
  new Date(d).toLocaleDateString("fr-FR", opts);
const daysUntil = (d) => Math.ceil((new Date(d) - new Date()) / 86400000);
// Parse "YYYY-MM-DD" as LOCAL midnight (not UTC) to avoid timezone shifts
const parseLocalDate = (s) => { if (!s) return new Date(NaN); const [y,m,d] = s.split("-").map(Number); return new Date(y, m-1, d); };
const allureFromPace = (distKm, dureeMin) =>
  distKm > 0 ? `${Math.floor(dureeMin / distKm)}:${String(Math.round((dureeMin / distKm % 1) * 60)).padStart(2, "0")}` : "--:--";
const timeToSec = (s) => {
  const p = s.replace("h", ":").split(":").map(Number);
  return p.length === 3 ? p[0] * 3600 + p[1] * 60 + p[2] : p.length === 2 ? p[0] * 60 + p[1] : 0;
};
const riegelPredict = (kd, kts, td) => {
  const t = kts * Math.pow(td / kd, 1.06);
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = Math.round(t % 60);
  return h > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
};
const getTypeInfo = (id) => RUN_TYPES.find(r => r.id === id) || RUN_TYPES[0];

/* ── STRATA RACE ESTIMATE ENGINE ─────────────────────────────────────────────
   Calcule un temps réaliste indépendant de l'objectif utilisateur.
   Sources : performances récentes + VMA + forme du moment (CTL/TSB).

   Logique :
   1. Cherche la meilleure performance récente (90j) sur distance similaire
      → Riegel pour extrapoler à la distance cible
   2. Si pas de perf récente proche → utilise VMA directement (% VMA par distance)
   3. Ajuste selon la forme : TSB positif = léger bonus, TSB très négatif = malus
   4. Retourne { estSec, estTime, source, confidence, fitnessAdj }
─────────────────────────────────────────────────────────────────────────────── */
const calcStrataEstimate = (runs, vma, targetDistKm, fcMax, fcRepos) => {
  if (!targetDistKm || targetDistKm <= 0) return null;

  // ── 1. VMA → temps de base (% VMA calibré par distance) ──────────────────
  // Sources : Daniels, Billat. Fraction de VMA utilisable en course.
  // Fractions calibrées sur Billat (2003) et Daniels' Running Formula
  // VMA 17.5 → 5km ~18:20 (95% VMA) · 10km ~38min (90%) · semi ~1h28 (82%) · marathon ~3h10 (80%)
  const vmaFraction =
    targetDistKm <= 1    ? 0.98 :
    targetDistKm <= 3    ? 0.96 :
    targetDistKm <= 5    ? 0.95 :
    targetDistKm <= 10   ? 0.90 :
    targetDistKm <= 21.1 ? 0.82 :
    targetDistKm <= 42.2 ? 0.80 :
    0.68; // ultra

  const vmaPaceSec = vma && vma > 0 ? (60 / (vma * vmaFraction)) * 60 : 0; // s/km
  const vmaEstSec  = vmaPaceSec > 0 ? Math.round(vmaPaceSec * targetDistKm) : 0;

  // ── 2. Meilleure perf récente (90j, activités de course uniquement) ───────
  const runOnly = (runs || []).filter(r => {
    const sport = getSportFromType(r.type || "fond");
    return sport === "run" && r.distance > 0 && r.duree > 0;
  });

  // Projette chaque course récente (180j) vers la distance cible via Riegel
  // Garde la MEILLEURE projection (temps le plus court = meilleure forme)
  // Distance entre 30% et 300% de la cible pour avoir assez de référence
  const candidates = runOnly
    .filter(r => {
      const daysAgo = Math.abs(daysUntil(r.date));
      return daysAgo <= 180
        && r.distance >= targetDistKm * 0.30
        && r.distance <= targetDistKm * 3.0;
    })
    .map(r => {
      const refSec    = r.duree * 60;
      const projected = Math.round(refSec * Math.pow(targetDistKm / r.distance, 1.06));
      // Pénalité légère si la course de référence est très loin en distance
      // (extrapolation moins fiable d'un 5km vers un marathon)
      const distRatio  = r.distance / targetDistKm;
      const reliability = distRatio >= 0.6 && distRatio <= 1.5 ? 1.0
                        : distRatio >= 0.4 && distRatio <= 2.0 ? 0.95
                        : 0.88;
      return { run: r, projected, reliability, adjProjected: Math.round(projected / reliability) };
    })
    // Trie par meilleure projection ajustée (le plus rapide = meilleure perf)
    .sort((a, b) => a.adjProjected - b.adjProjected);

  let riegelEstSec = 0;
  let refRun = null;
  if (candidates.length > 0) {
    // Prend la meilleure projection
    refRun       = candidates[0].run;
    riegelEstSec = candidates[0].projected;
  }

  // ── 3. Choix de la meilleure source ──────────────────────────────────────
  let baseSec, source, confidence;
  if (riegelEstSec > 0 && refRun) {
    // Pondération selon la proximité de distance de la course de référence :
    // - Course similaire (50-150% de la cible) : Riegel fiable → 60% Riegel / 40% VMA
    // - Course éloignée : VMA plus fiable → 20% Riegel / 80% VMA
    const distRatio = refRun.distance / targetDistKm;
    const isClose   = distRatio >= 0.5 && distRatio <= 1.5;
    const riegelW   = isClose ? 0.60 : 0.20;
    const vmaW      = 1 - riegelW;
    baseSec    = vmaEstSec > 0
      ? Math.round(riegelEstSec * riegelW + vmaEstSec * vmaW)
      : riegelEstSec;
    source     = `Riegel ${refRun.distance}km (${Math.round(riegelW*100)}%) + VMA (${Math.round(vmaW*100)}%)`;
    confidence = isClose ? "haute" : "moyenne";
  } else if (vmaEstSec > 0) {
    baseSec    = vmaEstSec;
    source     = `VMA ${vma} km/h`;
    confidence = "bonne";
  } else {
    return null;
  }

  // ── 4. Ajustement forme — CTL/TSB ────────────────────────────────────────
  const pmc = calcPMC(runOnly, 84); // 2× CTL window for proper seed
  const lastPMC = pmc.length > 0 ? pmc[pmc.length - 1] : null;
  let fitnessAdj = 1.0;
  let fitnessLabel = "Forme neutre";
  if (lastPMC) {
    const tsb = lastPMC.tsb || 0;
    const ctl = lastPMC.ctl || 0;
    if (tsb >= 15 && ctl >= 30)      { fitnessAdj = 0.975; fitnessLabel = "Forme de pointe 🟢 (TSB +" + Math.round(tsb) + ")"; }
    else if (tsb >= 5)               { fitnessAdj = 0.990; fitnessLabel = "Bonne forme 🟢 (TSB +" + Math.round(tsb) + ")"; }
    else if (tsb >= -5)              { fitnessAdj = 1.000; fitnessLabel = "Forme neutre 🟡"; }
    else if (tsb >= -20)             { fitnessAdj = 1.015; fitnessLabel = "Légère fatigue 🟠 (TSB " + Math.round(tsb) + ")"; }
    else                             { fitnessAdj = 1.035; fitnessLabel = "Fatigue accumulée 🔴 (TSB " + Math.round(tsb) + ")"; }
  }

  const finalSec = Math.round(baseSec * fitnessAdj);

  const fmt = (s) => {
    const h = Math.floor(s/3600), m = Math.floor((s%3600)/60), ss = Math.round(s%60);
    return h > 0 ? `${h}h${String(m).padStart(2,"0")}:${String(ss).padStart(2,"0")}` : `${m}:${String(ss).padStart(2,"0")}`;
  };

  return {
    estSec: finalSec,
    estTime: fmt(finalSec),
    source,
    confidence,
    fitnessAdj,
    fitnessLabel,
    vmaEstSec,
    riegelEstSec,
    refRun,
  };
};

// TRIMP monotone: durée × facteur FC
const calcTrimp = (dureeMin, fc_moy, fc_repos, fc_max, typeId = "fond") => {
  const sport = getSportFromType ? getSportFromType(typeId) : "run";
  const coeff = TRIMP_SPORT_COEFF ? (TRIMP_SPORT_COEFF[sport] || 1.0) : 1.0;
  if (!fc_repos || !fc_max || !fc_moy) return Math.round(dureeMin * 0.7 * coeff);
  const hrr = (fc_moy - fc_repos) / (fc_max - fc_repos);
  return Math.round(dureeMin * hrr * (0.64 * Math.exp(1.92 * hrr)) * coeff);
};

// ACWR (Acute:Chronic Workload Ratio)
const calcACWR = (activities) => {
  // Accepte toutes les activités (course, vélo, nage…) — TRIMP déjà pondéré par sport
  const now = new Date();
  const getTrimp = (r) => r.trimp || calcTrimp(r.duree, r.fc_moy, 52, 192, r.type||"fond");
  const acute = activities.filter(r => { const d = (now - new Date(r.date)) / 86400000; return d >= 0 && d <= 7; })
    .reduce((s, r) => s + getTrimp(r), 0);
  const chronic4w = activities.filter(r => { const d = (now - new Date(r.date)) / 86400000; return d >= 0 && d <= 28; })
    .reduce((s, r) => s + getTrimp(r), 0) / 4;
  return chronic4w > 0 ? +(acute / chronic4w).toFixed(2) : 1.0;
};

const acwrLabel = (v) => v < 0.8 ? ["Sous-charge", T.blue] : v <= 1.3 ? ["Zone optimale ✓", T.green] : v <= 1.5 ? ["Surcharge ⚠️", T.orange] : ["Danger 🔴", T.red];
const acwrColor = (v) => v < 0.8 ? T.blue : v <= 1.3 ? T.green : v <= 1.5 ? T.orange : T.red;

const recoveryLabel = (s) => s >= 80 ? ["Excellent", T.green] : s >= 60 ? ["Bon", T.blue] : s >= 40 ? ["Modéré", T.orange] : ["Fatigué", T.red];

// Weekly TRIMP grouping
const getWeeklyStats = (activities) => {
  const weeks = {};
  activities.forEach(r => {
    const d = parseLocalDate(r.date);
    const monday = new Date(d); monday.setDate(d.getDate() - ((d.getDay()+6)%7));
    const key = `${monday.getFullYear()}-${String(monday.getMonth()+1).padStart(2,"0")}-${String(monday.getDate()).padStart(2,"0")}`;
    if (!weeks[key]) weeks[key] = { label: fmtDate(key, {day:"2-digit",month:"short"}), km:0, trimp:0, sessions:0, dplus:0 };
    const sport = getSportFromType(r.type||"fond");
    if (sport === "run") weeks[key].km += r.distance; // km = course uniquement
    weeks[key].trimp += r.trimp || calcTrimp(r.duree, r.fc_moy, 52, 192, r.type||"fond");
    weeks[key].dplus += r.denivele || 0;
    weeks[key].sessions++;
  });
  return Object.values(weeks).slice(-6).map(w => ({ ...w, km:+w.km.toFixed(1), trimp:Math.round(w.trimp) }));
};

/* ── PMC : Performance Management Chart (Banister 1991) ───────────────────
   CTL (Chronic Training Load / "Fitness")  : τ = 42 jours
   ATL (Acute Training Load  / "Fatigue")   : τ = 7  jours
   TSB (Training Stress Balance / "Forme")  = CTL − ATL
   ΔctlFactor = 1 − e^(−1/τ)   →   CTL(t) = CTL(t−1)·e^(−1/τ) + TRIMP(t)·Δ
────────────────────────────────────────────────────────────────────────── */
const calcPMC = (activities, days = 90) => {
  const kCTL = Math.exp(-1 / 42), kATL = Math.exp(-1 / 7);
  const dCTL = 1 - kCTL, dATL = 1 - kATL;
  // Build day-level TRIMP map — toutes activités confondues, TRIMP pondéré par sport
  const trimpByDay = {};
  activities.forEach(r => {
    const t = r.trimp || calcTrimp(r.duree, r.fc_moy, 52, 192, r.type||"fond");
    trimpByDay[r.date] = (trimpByDay[r.date] || 0) + t;
  });
  const today = new Date();
  let ctl = 0, atl = 0;
  const series = [];
  for (let i = days; i >= 0; i--) {
    const d = new Date(today); d.setDate(today.getDate() - i);
    const key = d.toISOString().split("T")[0];
    const trimp = trimpByDay[key] || 0;
    ctl = ctl * kCTL + trimp * dCTL;
    atl = atl * kATL + trimp * dATL;
    series.push({
      date: key,
      label: fmtDate(key, { day:"2-digit", month:"short" }),
      ctl: +ctl.toFixed(1),
      atl: +atl.toFixed(1),
      tsb: +(ctl - atl).toFixed(1),
      trimp,
    });
  }
  return series;
};

/* ── Efficiency Factor (EF) ────────────────────────────────────────────────
   EF = vitesse (m/min) / FC_moy
   Un EF croissant sur des séances de même type → amélioration aérobie
─────────────────────────────────────────────────────────────────────────── */
const calcEF = (distKm, dureeMin, fc_moy) => {
  if (!fc_moy || !dureeMin || !distKm) return null;
  return +((distKm * 1000 / dureeMin) / fc_moy).toFixed(3);
};

/* ── Monotonie & Strain (Bannister) ───────────────────────────────────────
   Monotonie = TRIMP_moy_7j / écart-type_7j   (>2 = risque surentraînement)
   Strain     = TRIMP_total_7j × monotonie
─────────────────────────────────────────────────────────────────────────── */
const calcMonotonyStrain = (runs) => {
  const trimpDay = {};
  const now = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(now); d.setDate(now.getDate() - i);
    trimpDay[d.toISOString().split("T")[0]] = 0;
  }
  runs.forEach(r => {
    const days = (now - new Date(r.date)) / 86400000;
    if (days >= 0 && days < 7) {
      trimpDay[r.date] = (trimpDay[r.date] || 0) + (r.trimp || calcTrimp(r.duree, r.fc_moy, 52, 192));
    }
  });
  const vals = Object.values(trimpDay);
  const mean = vals.reduce((s, v) => s + v, 0) / 7;
  const variance = vals.reduce((s, v) => s + (v - mean) ** 2, 0) / 7;
  const sd = Math.sqrt(variance);
  const monotony = sd > 0 ? +(mean / sd).toFixed(2) : 0;
  const strain = +(vals.reduce((s, v) => s + v, 0) * monotony).toFixed(0);
  return { monotony, strain };
};

/* ═══════════════════════════════════════════════════════════════════════════
   ATOM COMPONENTS
═══════════════════════════════════════════════════════════════════════════ */
const Pill = ({ children, color = T.blue, bg, style = {} }) => (
  <span style={{
    background: bg || `${color}15`,
    color,
    padding: "3px 10px",
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.5,
    fontFamily: T.fontUI,
    border: `0.5px solid ${color}35`,
    ...style,
  }}>
    {children}
  </span>
);

const Btn = ({ children, onClick, variant = "primary", size = "md", style = {}, disabled = false }) => {
  const sizes = {
    sm: { padding: "6px 14px", fontSize: 12 },
    md: { padding: "9px 20px", fontSize: 13 },
    lg: { padding: "13px 28px", fontSize: 15 },
  };
  const variants = {
    primary: {
      background: "#1A1510",
      color: "#F2ECD8",
      border: "0.5px solid #1A1510",
      boxShadow: "0 2px 0 rgba(26,21,16,0.35), 0 1px 4px rgba(26,21,16,0.15)",
    },
    lime: {
      background: "#C49A3C",
      color: "#fff",
      border: "0.5px solid #9A7828",
      boxShadow: "0 2px 0 rgba(154,120,40,0.4), 0 1px 4px rgba(0,0,0,0.10)",
    },
    ghost: {
      background: "#FFFFFF",
      color: "#9A8F7E",
      border: "0.5px solid #EAE4D8",
      boxShadow: "0 1px 0 rgba(26,21,16,0.04), 0 1px 4px rgba(26,21,16,0.05)",
    },
    danger: {
      background: "#FCEEE6",
      color: "#C85C2A",
      border: "0.5px solid #C85C2A44",
      boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
    },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...sizes[size], ...variants[variant],
      borderRadius: 6, fontWeight: 700, fontFamily: "inherit",
      letterSpacing: 0.2,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.45 : 1,
      transition: "transform 0.08s, box-shadow 0.08s, background 0.1s",
      display: "inline-flex", alignItems: "center", gap: 6, ...style,
    }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.filter = "brightness(1.06)"; }}
      onMouseLeave={e => { e.currentTarget.style.filter = ""; e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = variants[variant].boxShadow; }}
      onMouseDown={e => { if (!disabled) { e.currentTarget.style.transform = "translateY(1px)"; e.currentTarget.style.boxShadow = "0 0 0 rgba(0,0,0,0.1)"; } }}
      onMouseUp={e => { e.currentTarget.style.transform = ""; }}
    >{children}</button>
  );
};

const Card = ({ children, style = {}, onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => onClick && setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: T.surface,
        borderRadius: 12,
        border: `0.5px solid ${hov && onClick ? T.borderStrong : T.border}`,
        boxShadow: hov && onClick
          ? "0 2px 0 rgba(26,21,16,0.07), 0 8px 24px rgba(26,21,16,0.10), 0 0 0 0.5px rgba(26,21,16,0.06)"
          : T.shadow,
        padding: 20,
        cursor: onClick ? "pointer" : "default",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease, transform 0.15s ease",
        transform: hov && onClick ? "translateY(-1px)" : "none",
        ...style,
      }}
    >{children}</div>
  );
};

const Label = ({ children }) => (
  <div style={{ fontSize: 10, fontWeight: 500, color: T.textLight, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6, fontFamily: T.fontUI }}>
    {children}
  </div>
);

const Field = ({ label, value, onChange, type = "text", options, placeholder, unit, hint, style = {} }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4, ...style }}>
    {label && <Label>{label}</Label>}
    {hint && <div style={{ fontSize: 12, color: T.textLight, marginBottom: 4 }}>{hint}</div>}
    {options ? (
      <select value={value} onChange={e => onChange(e.target.value)} style={{
        background: T.surfaceSub,
        border: "1px solid #D8D8D8",
        borderRadius: 6,
        padding: "9px 12px",
        color: T.text,
        fontSize: 13,
        fontFamily: "inherit",
        cursor: "pointer",
        appearance: "none",
        outline: "none",
        boxShadow: T.shadowInset,
        transition: "border-color 0.12s, box-shadow 0.12s",
      }}
        onFocus={e => { e.target.style.borderColor = "#C49A3C"; e.target.style.boxShadow = "inset 0 1px 3px rgba(0,0,0,0.08), 0 0 0 3px rgba(242,88,34,0.12)"; }}
        onBlur={e => { e.target.style.borderColor = "#D8D8D8"; e.target.style.boxShadow = T.shadowInset; }}
      >
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    ) : (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{
          flex: 1,
          background: "#EDE8DD",
          border: "1px solid #D8D8D8",
          borderRadius: 6,
          padding: "9px 12px",
          color: T.text,
          fontSize: 13,
          fontFamily: "inherit",
          outline: "none",
          boxShadow: T.shadowInset,
          transition: "border-color 0.12s, box-shadow 0.12s",
        }}
          onFocus={e => { e.target.style.borderColor = "#C49A3C"; e.target.style.boxShadow = "inset 0 1px 3px rgba(0,0,0,0.08), 0 0 0 3px rgba(242,88,34,0.12)"; }}
          onBlur={e => { e.target.style.borderColor = "#D8D8D8"; e.target.style.boxShadow = T.shadowInset; }}
        />
        {unit && <span style={{ color: T.textLight, fontSize: 12, whiteSpace: "nowrap", fontWeight: 600 }}>{unit}</span>}
      </div>
    )}
  </div>
);

const SliderField = ({ label, value, onChange, min, max, step = 1, unit, color = T.navy }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Label>{label}</Label>
        <span style={{ fontSize: 15, fontWeight: 800, color, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{value}{unit}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(+e.target.value)}
        style={{ width: "100%", accentColor: color || "#C49A3C", height: 4, cursor: "pointer" }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", color: T.textLight, fontSize: 11 }}>
        <span>{min}{unit}</span><span>{max}{unit}</span>
      </div>
    </div>
  );
};

const ProgressRing = ({ score, size = 120, stroke = 12 }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const [label, color] = recoveryLabel(score);
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.border} strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={circ * (1 - score / 100)}
          strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s ease" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: size * 0.22, fontWeight: 700, color, lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: 11, color: T.textLight, fontWeight: 600 }}>{label}</span>
      </div>
    </div>
  );
};

const Toast = ({ msg, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, []);
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24,
      background: T.navy,
      color: "#fff",
      padding: "11px 18px",
      borderRadius: 8,
      boxShadow: "0 3px 0 rgba(0,0,0,0.4), 0 12px 32px rgba(0,0,0,0.3)",
      fontSize: 13, fontWeight: 600, zIndex: 9999,
      display: "flex", alignItems: "center", gap: 10,
      animation: "slideUp 0.25s ease",
      borderLeft: "3px solid #C49A3C",
    }}>
      <span style={{ color: "#C49A3C" }}>✓</span> {msg}
      <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", cursor: "pointer", fontSize: 15, marginLeft: 4 }}>✕</button>
    </div>
  );
};

/* ── GLOSSAIRE ────────────────────────────────────────────────────────────── */
const GLOSSARY = {
  "ACWR": "Acute:Chronic Workload Ratio — rapport entre la charge aiguë des 7 derniers jours et la charge chronique (moyenne sur 4 semaines). Zone verte : 0,8–1,3. En dessous = sous-entraîné, au-dessus = risque blessure.",
  "TRIMP": "Training Impulse — score de charge d'entraînement calculé à partir de la durée × l'intensité FC (méthode Banister). Permet de comparer des séances de types différents sur une même échelle.",
  "HRV": "Heart Rate Variability — variabilité de la fréquence cardiaque. Mesure l'espace entre chaque battement. Un HRV élevé le matin indique une bonne récupération du système nerveux autonome.",
  "VMA": "Vitesse Maximale Aérobie — la vitesse à laquelle vous atteignez votre VO₂max. Base de calcul de toutes vos allures d'entraînement (Z1 à Z5).",
  "VO₂max": "Volume maximal d'oxygène consommé par kg de poids corporel par minute. Indicateur de la capacité aérobie. S'estime à partir de la VMA : VO₂max ≈ VMA × 3,5.",
  "Allure": "Vitesse de course exprimée en min:sec par kilomètre. Plus le chiffre est bas, plus vous courez vite (ex: 4:30/km est plus rapide que 5:30/km).",
  "FC repos": "Fréquence cardiaque au repos, mesurée le matin avant de se lever. Plus elle est basse, meilleur est généralement le niveau aérobie (athlètes d'endurance : 40–55 bpm).",
  "FC max": "Fréquence cardiaque maximale — le nombre maximal de battements par minute que votre cœur peut atteindre à l'effort maximal. Diminue avec l'âge (~220 – âge en première estimation).",
  "Zones FC": "Découpage de l'intensité d'effort en 5 zones basées sur le % de FC max ou de réserve cardiaque (méthode Karvonen). Z1 = récup, Z2 = fondamental, Z3 = tempo, Z4 = seuil, Z5 = VO₂max.",
  "Karvonen": "Méthode de calcul des zones FC utilisant la fréquence cardiaque de réserve (FC max − FC repos). Plus précise que le simple % de FC max car elle tient compte de la FC de repos.",
  "IMC": "Indice de Masse Corporelle = poids (kg) / taille² (m). Indicateur de corpulence, mais limité pour les sportifs car il ne distingue pas masse grasse et masse musculaire.",
  "Riegel": "Formule de prédiction de performance sur d'autres distances à partir d'un temps de référence : T2 = T1 × (D2/D1)^1.06. Valable pour des distances entre 3km et marathon.",
  "D+": "Dénivelé positif — somme des montées en mètres sur un parcours. Indicateur de difficulté en trail running. À même distance, un D+ élevé augmente fortement la dépense énergétique.",
  "Z2": "Zone 2 — endurance fondamentale, ~60–70% FC max. Intensité à laquelle vous pouvez tenir une conversation. Base de tout plan d'entraînement aérobie (80% du volume).",
  "Seuil lactique": "Intensité à partir de laquelle le lactate s'accumule plus vite qu'il n'est éliminé (~85–90% FC max). L'allure seuil correspond à votre tempo de compétition sur 1h.",
  "Affûtage": "Phase de réduction progressive du volume d'entraînement (15–20% par semaine) dans les 2–3 semaines précédant une course, tout en maintenant l'intensité pour arriver reposé au départ.",
  "CHO": "Glucides (CarboHydrates en anglais). Principale source d'énergie lors d'efforts intenses. Objectif en course longue : ~30–60g/h pour prévenir l'hypoglycémie.",
  "FC moy": "Fréquence cardiaque moyenne sur l'ensemble de la séance. Indicateur de l'intensité globale de l'effort.",
  "Recovery score": "Score de récupération estimé (0–100) à partir de la qualité du sommeil la nuit précédente (60%) et de la charge d'entraînement des 3 derniers jours (40%). Guide l'intensité de la séance du jour.",
  "CTL": "Chronic Training Load — charge d'entraînement chronique, calculée sur 42 jours par décroissance exponentielle du TRIMP. Représente votre niveau de forme (fitness) accumulé. Une CTL haute = une meilleure condition physique.",
  "ATL": "Acute Training Load — charge d'entraînement aiguë sur 7 jours. Représente la fatigue accumulée récemment. Une ATL très haute par rapport à la CTL = signal de fatigue.",
  "TSB": "Training Stress Balance (Forme) = CTL − ATL. Positif = reposé et en forme. Négatif = fatigué. Idéal le jour d'une compétition : TSB entre +5 et +25 après une phase d'affûtage.",
  "EF": "Efficiency Factor — indicateur d'économie de course. EF = vitesse (m/min) / FC moyenne. Un EF croissant sur des sorties comparables indique une amélioration de l'efficacité aérobie.",
  "Monotonie": "Indicateur de Bannister : TRIMP moyen 7 jours / écart-type. Une monotonie >2 signale un entraînement trop uniforme, facteur de surentraînement même avec une charge modérée.",
  "Strain": "Charge hebdomadaire × monotonie. Indice de risque de surentraînement combinant volume et régularité. À surveiller si Strain > 4000 sur 7 jours.",
  "Cadence": "Nombre de foulées par minute (spm). La cadence optimale pour la plupart des coureurs se situe entre 170 et 185 spm. Une cadence basse (<165) est souvent associée à une technique moins économique.",
  "PMC": "Performance Management Chart — graphique de forme basé sur CTL, ATL et TSB. Outil fondamental en entraînement structuré pour planifier les phases de charge et d'affûtage.",
  "EF": "Efficiency Factor — ratio vitesse/FC. Mesure l'économie de course : à même effort cardiaque, une vitesse plus élevée = meilleure efficacité.",
  "Score bien-être": "Score composite (0–100) calculé à partir de l'énergie, du stress, des courbatures, de l'humeur, de l'hydratation et de la consommation d'alcool. Corrélé à la performance et au risque de blessure.",
  "Sortie longue": "La séance phare de la semaine en endurance — longue distance à allure Z2. Développe l'endurance musculaire, l'efficacité des lipides et la résistance mentale.",
  "Fractionné": "Séance d'intervalles alternant des phases d'effort intense et des phases de récupération (ex: 10×400m). Développe la VMA et la capacité lactique.",
  "Tempo": "Séance à allure seuil lactique, soutenue mais contrôlée (20–40 min en continu ou en blocs). Repousse le seuil d'accumulation du lactate.",
};

function InfoTip({ term, children }) {
  const [visible, setVisible] = useState(false);
  const def = GLOSSARY[term];
  if (!def) return children || <span>{term}</span>;
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 3 }}>
      {children || <span style={{ fontWeight: "inherit", color: "inherit" }}>{term}</span>}
      <span
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onClick={() => setVisible(v => !v)}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 14, height: 14, borderRadius: "50%",
          background: "#EBEBEB",
          border: "1px solid #D2D2D2",
          color: "#888",
          fontSize: 8, fontWeight: 800, cursor: "pointer",
          flexShrink: 0, userSelect: "none",
          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
          transition: "background 0.12s, border-color 0.12s",
        }}
        onMouseEnter={e => { setVisible(true); e.currentTarget.style.background = "#C49A3C"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#9A7828"; }}
        onMouseLeave={e => { setVisible(false); e.currentTarget.style.background = "#EBEBEB"; e.currentTarget.style.color = "#888"; e.currentTarget.style.borderColor = "#D2D2D2"; }}
        title={term}
      >i</span>
      {visible && (
        <span style={{
          position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)",
          background: T.navy,
          color: "#E8E8E8",
          borderRadius: 6,
          padding: "10px 14px",
          fontSize: 11, lineHeight: 1.65, width: 280, zIndex: 500,
          boxShadow: "0 2px 0 rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.3)",
          pointerEvents: "none",
          border: "1px solid rgba(255,255,255,0.07)",
        }}>
          <span style={{ fontWeight: 700, color: "#C49A3C", display: "block", marginBottom: 5, fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase" }}>{term}</span>
          {def}
          <span style={{
            position: "absolute", bottom: -5, left: "50%", transform: "translateX(-50%)",
            width: 10, height: 10, background: T.navy, rotate: "45deg",
            borderRight: "1px solid rgba(255,255,255,0.07)",
            borderBottom: "1px solid rgba(255,255,255,0.07)",
          }}/>
        </span>
      )}
    </span>
  );
}

const Modal = ({ title, children, onClose, width = 560 }) => (
  <div style={{
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.28)",
    backdropFilter: "blur(4px)",
    zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
  }} onClick={e => e.target === e.currentTarget && onClose()}>
    <div style={{
      background: "#FFFFFF",
      borderRadius: 10,
      width: "100%", maxWidth: width,
      boxShadow: "0 4px 0 rgba(0,0,0,0.10), 0 20px 60px rgba(0,0,0,0.20), 0 0 0 1px rgba(0,0,0,0.06)",
      maxHeight: "90vh", overflowY: "auto",
      animation: "popIn 0.22s cubic-bezier(.34,1.36,.64,1)",
    }}>
      {/* Orange top accent stripe */}
      <div style={{ height: 3, background: "#C49A3C", borderRadius: "10px 10px 0 0" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #EBEBEB" }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: T.text, letterSpacing: 0.1 }}>{title}</h2>
        <button onClick={onClose} style={{
          background: T.bg,
          border: "1px solid #EAE4D8",
          borderRadius: 6,
          width: 30, height: 30,
          cursor: "pointer",
          fontSize: 14, color: T.textMid,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 1px 0 rgba(0,0,0,0.06)",
          transition: "background 0.1s",
        }}
          onMouseEnter={e => e.currentTarget.style.background = "#EAEAEA"}
          onMouseLeave={e => e.currentTarget.style.background = T.bg}
        >✕</button>
      </div>
      <div style={{ padding: 24 }}>{children}</div>
    </div>
  </div>
);

const SectionHead = ({ title, action }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, paddingBottom: 12, borderBottom: "1px solid #EBEBEB" }}>
    <h3 style={{ fontSize: 13, fontWeight: 700, color: T.text, letterSpacing: 0.2, textTransform: "uppercase" }}>{title}</h3>
    {action}
  </div>
);

const EmptyState = ({ icon, title, desc, action }) => (
  <div style={{ textAlign: "center", padding: "48px 20px", borderTop: `1px dashed ${T.border}` }}>
    <div style={{ fontSize: 36, marginBottom: 14, opacity: 0.35 }}>{icon}</div>
    <div style={{ fontWeight: 600, fontSize: 14, color: T.textMid, marginBottom: 6, letterSpacing: 0.1 }}>{title}</div>
    <div style={{ color: T.textLight, fontSize: 12, marginBottom: 20, lineHeight: 1.6 }}>{desc}</div>
    {action}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: T.navy,
      borderRadius: 6,
      padding: "9px 12px",
      boxShadow: "0 2px 0 rgba(0,0,0,0.4), 0 8px 20px rgba(0,0,0,0.3)",
      border: "1px solid rgba(255,255,255,0.08)",
    }}>
      <div style={{ color: "#888", fontSize: 10, marginBottom: 5, letterSpacing: 0.5, textTransform: "uppercase" }}>{label}</div>
      {payload.map((p, i) => <div key={i} style={{ color: "#fff", fontSize: 12, fontWeight: 500, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}><span style={{ color: "#C49A3C" }}>■</span> {p.name}: {p.value}</div>)}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   ONBOARDING
═══════════════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════════════
   ONBOARDING — 15 questions · 9 étapes
═══════════════════════════════════════════════════════════════════════════ */
function ChoiceGrid({ options, value, onChange, cols = 2 }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols},1fr)`, gap: 10 }}>
      {options.map(o => (
        <div key={o.v} onClick={() => onChange(o.v)} style={{
          padding: "14px 12px", borderRadius: 14, cursor: "pointer", textAlign: "center",
          background: value === o.v ? (o.color || "#C49A3C") : T.bg,
          border: `2px solid ${value === o.v ? (o.color || "#C49A3C") : T.border}`,
          transition: "all 0.15s", position: "relative",
        }}>
          {o.icon && <div style={{ fontSize: 26, marginBottom: 6 }}>{o.icon}</div>}
          <div style={{ fontWeight: 800, fontSize: 14, color: value === o.v ? "#fff" : T.text }}>{o.l}</div>
          {o.d && <div style={{ fontSize: 11, color: value === o.v ? "rgba(255,255,255,0.75)" : T.textLight, marginTop: 3, lineHeight: 1.4 }}>{o.d}</div>}
          {value === o.v && <span style={{ position: "absolute", top: 8, right: 10, color: T.lime, fontSize: 14, fontWeight: 700 }}>✓</span>}
        </div>
      ))}
    </div>
  );
}

function MultiChoice({ options, value = [], onChange }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
      {options.map(o => {
        const sel = value.includes(o.v);
        return (
          <div key={o.v} onClick={() => toggle(o.v)} style={{
            padding: "10px 16px", borderRadius: 20, cursor: "pointer",
            background: sel ? T.navy : T.bg,
            border: `2px solid ${sel ? T.navy : T.border}`,
            transition: "all 0.15s", display: "flex", alignItems: "center", gap: 6,
          }}>
            {o.icon && <span style={{ fontSize: 16 }}>{o.icon}</span>}
            <span style={{ fontSize: 13, fontWeight: 700, color: sel ? "#fff" : T.text }}>{o.l}</span>
          </div>
        );
      })}
    </div>
  );
}

function Onboarding({ onComplete }) {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [data, setData] = useState({
    // Q1-2 Identité
    prenom: "", sexe: "homme",
    // Q3-5 Morphologie
    age: 28, taille: 170, poids: 70,
    // Q6-7 Expérience
    experience: "1an", volumeActuel: 30,
    // Q8-9 Agenda
    seancesParSemaine: 3, joursDispos: [],
    // Q10-11 Physiologie
    fc_max: 190, fc_repos: 55,
    // Q12 Performance
    vma: 14, chronoRef: "", distRef: "10k",
    // Q13 Objectif
    objectif: "semi", objectifDate: "", objectifChrono: "",
    // Q14 Santé
    blessures: [],
    // Q15 Style
    styleEntrainement: "mixte",
  });
  const set = (k) => (v) => setData(d => ({ ...d, [k]: v }));

  // Auto-estimate FC max from age
  const fcMaxEstime = Math.round(220 - data.age);
  const vmaEstimee = data.experience === "debutant" ? 10 : data.experience === "1an" ? 13 : data.experience === "3ans" ? 15.5 : 17;

  const steps = [
    /* ── ÉTAPE 1 : Bienvenue ── */
    {
      title: "Bienvenue sur GRAVV",
      sub: "Votre coach data personnel pour la course à pied. 2 minutes pour créer votre profil.",
      questions: ["Q1 — Prénom", "Q2 — Sexe biologique"],
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
            <div style={{ width: 72, height: 72, borderRadius: 20, background: T.navy, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
              <svg width="36" height="44" viewBox="0 0 44 52" fill="none">
                <path d="M4 6 L22 28 L40 6" stroke="#F2ECD8" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4 24 L22 46 L40 24" stroke="#C49A3C" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p style={{ color: T.textMid, fontSize: 14, maxWidth: 340, margin: "0 auto", lineHeight: 1.7 }}>
              GRAVV calcule votre ACWR, suit votre HRV, prédit vos performances et guide chaque séance selon votre état réel.
            </p>
          </div>
          <Field label="Votre prénom" value={data.prenom} onChange={set("prenom")} placeholder="ex: Thomas" />
          <div>
            <Label>Sexe biologique <span style={{ color: T.textLight, fontWeight: 400 }}>(influence le calcul des zones FC)</span></Label>
            <ChoiceGrid cols={3} value={data.sexe} onChange={set("sexe")} options={[
              { v: "homme", l: "Homme", icon: "♂️" },
              { v: "femme", l: "Femme", icon: "♀️" },
              { v: "autre", l: "Autre", icon: "⚧️" },
            ]} />
          </div>
        </div>
      ),
      canNext: () => data.prenom.trim().length >= 2,
    },

    /* ── ÉTAPE 2 : Morphologie ── */
    {
      title: `Bonjour ${data.prenom || "athlète"} ! 📏`,
      sub: "Ces mesures calibrent votre IMC, vos dépenses énergétiques et vos prédictions.",
      questions: ["Q3 — Âge", "Q4 — Taille", "Q5 — Poids"],
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <SliderField label="Âge" value={data.age} onChange={set("age")} min={14} max={80} unit=" ans" color={T.blue} />
          <SliderField label="Taille" value={data.taille} onChange={set("taille")} min={140} max={220} unit=" cm" color={T.purple} />
          <SliderField label="Poids" value={data.poids} onChange={set("poids")} min={40} max={150} unit=" kg" color={T.orange} />
          <div style={{ background: T.bg, borderRadius: 12, padding: "12px 16px", display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13, color: T.textMid }}>IMC calculé</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: T.navy, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>
              {(data.poids / (data.taille / 100) ** 2).toFixed(1)}
            </span>
          </div>
        </div>
      ),
      canNext: () => true,
    },

    /* ── ÉTAPE 3 : Expérience & Volume ── */
    {
      title: "Votre pratique actuelle 🏃",
      sub: "Soyez honnête — cela détermine l'intensité de vos plans.",
      questions: ["Q6 — Niveau d'expérience", "Q7 — Volume hebdomadaire actuel"],
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div>
            <Label>Niveau d'expérience en course</Label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
              {[
                { v: "debutant", l: "Débutant", d: "Moins d'un an · Je construis mes bases", icon: "🌱", vma: "~10 km/h" },
                { v: "1an", l: "Intermédiaire", d: "1–3 ans de pratique régulière", icon: "🏃", vma: "~13 km/h" },
                { v: "3ans", l: "Confirmé", d: "3–5 ans · Je cours plus d'une fois par semaine", icon: "⚡", vma: "~15,5 km/h" },
                { v: "expert", l: "Expert", d: "5+ ans · Compétitions et objectifs ambitieux", icon: "🏆", vma: "~17+ km/h" },
              ].map(o => (
                <div key={o.v} onClick={() => { set("experience")(o.v); set("vma")(o.v === "debutant" ? 10 : o.v === "1an" ? 13 : o.v === "3ans" ? 15.5 : 17); }} style={{
                  display: "flex", alignItems: "center", gap: 14, padding: "13px 16px",
                  background: data.experience === o.v ? T.navy : T.bg,
                  border: `2px solid ${data.experience === o.v ? T.navy : T.border}`,
                  borderRadius: 12, cursor: "pointer", transition: "all 0.15s",
                }}>
                  <span style={{ fontSize: 22 }}>{o.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: data.experience === o.v ? "#fff" : T.text }}>{o.l}</div>
                    <div style={{ fontSize: 12, color: data.experience === o.v ? "#aac" : T.textLight }}>{o.d}</div>
                  </div>
                  <span style={{ fontSize: 11, color: data.experience === o.v ? T.lime : T.textLight, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>VMA {o.vma}</span>
                  {data.experience === o.v && <span style={{ color: T.lime, fontSize: 16 }}>✓</span>}
                </div>
              ))}
            </div>
          </div>
          <SliderField label="Volume hebdomadaire actuel" value={data.volumeActuel} onChange={set("volumeActuel")} min={0} max={150} step={5} unit=" km/sem" color={T.green} />
        </div>
      ),
      canNext: () => true,
    },

    /* ── ÉTAPE 4 : Agenda ── */
    {
      title: "Votre agenda d'entraînement 📅",
      sub: "Pour bâtir un plan réaliste autour de votre vie.",
      questions: ["Q8 — Séances par semaine", "Q9 — Jours disponibles"],
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <SliderField label="Nombre de séances par semaine" value={data.seancesParSemaine} onChange={set("seancesParSemaine")} min={1} max={10} unit=" séances" color={T.blue} />
          <div>
            <Label>Jours préférés pour courir <span style={{ color: T.textLight, fontWeight: 400 }}>(sélection multiple)</span></Label>
            <div style={{ marginTop: 10 }}>
              <MultiChoice value={data.joursDispos} onChange={set("joursDispos")} options={[
                { v: "lun", l: "Lun" }, { v: "mar", l: "Mar" }, { v: "mer", l: "Mer" },
                { v: "jeu", l: "Jeu" }, { v: "ven", l: "Ven" }, { v: "sam", l: "Sam" }, { v: "dim", l: "Dim" },
              ]} />
            </div>
          </div>
          <div style={{ background: T.bg, borderRadius: 12, padding: "12px 16px" }}>
            <div style={{ fontSize: 12, color: T.textLight, marginBottom: 4 }}>Volume cible recommandé</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.navy, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>
              {data.seancesParSemaine * (data.experience === "debutant" ? 5 : data.experience === "1an" ? 8 : data.experience === "3ans" ? 10 : 13)} km/sem
            </div>
            <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>Basé sur votre niveau et votre fréquence</div>
          </div>
        </div>
      ),
      canNext: () => true,
    },

    /* ── ÉTAPE 5 : Physiologie ── */
    {
      title: "Vos données cardiaques ❤️",
      sub: "Ces valeurs permettent de calculer vos zones FC précises. On peut les estimer si vous ne les connaissez pas.",
      questions: ["Q10 — FC maximale", "Q11 — FC de repos"],
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <div>
            <SliderField label="FC maximale" value={data.fc_max} onChange={set("fc_max")} min={150} max={220} unit=" bpm" color={T.red} />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
              <span style={{ fontSize: 11, color: T.textLight }}>Estimation automatique selon votre âge :</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.red, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{fcMaxEstime} bpm</span>
            </div>
            <button onClick={() => set("fc_max")(fcMaxEstime)} style={{ marginTop: 8, background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12, color: T.textMid, cursor: "pointer" }}>
              Utiliser l'estimation ({fcMaxEstime} bpm)
            </button>
          </div>
          <SliderField label="FC de repos (mesurée le matin au réveil)" value={data.fc_repos} onChange={set("fc_repos")} min={35} max={90} unit=" bpm" color={T.blue} />
          <div style={{ background: T.bg, borderRadius: 12, padding: "12px 16px" }}>
            <div style={{ fontSize: 12, color: T.textLight, marginBottom: 6 }}>Réserve cardiaque (méthode Karvonen)</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.navy, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{data.fc_max - data.fc_repos} bpm</div>
            <div style={{ fontSize: 11, color: T.textLight }}>Plus elle est haute, plus vos zones sont larges et précises</div>
          </div>
        </div>
      ),
      canNext: () => true,
    },

    /* ── ÉTAPE 6 : VMA & Chrono ── */
    {
      title: "Vos références de performance 🚀",
      sub: "Ces données alimentent les prédictions Riegel et vos allures cibles.",
      questions: ["Q12 — VMA ou dernier chrono de référence"],
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <div>
            <SliderField label="VMA estimée (Vitesse Maximale Aérobie)" value={data.vma} onChange={set("vma")} min={8} max={25} step={0.5} unit=" km/h" color={T.navy} />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
              <span style={{ fontSize: 11, color: T.textLight }}>Estimation selon votre niveau :</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.navy, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{vmaEstimee} km/h</span>
            </div>
            <button onClick={() => set("vma")(vmaEstimee)} style={{ marginTop: 8, background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12, color: T.textMid, cursor: "pointer" }}>
              Utiliser l'estimation ({vmaEstimee} km/h)
            </button>
          </div>
          <div>
            <Label>Meilleur chrono récent <span style={{ color: T.textLight, fontWeight: 400 }}>(optionnel — améliore les prédictions)</span></Label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
              <Field label="Distance" value={data.distRef} onChange={set("distRef")} options={[
                { v: "5k", l: "5 km" }, { v: "10k", l: "10 km" }, { v: "semi", l: "Semi (21,1 km)" }, { v: "marathon", l: "Marathon (42,2 km)" },
              ]} />
              <Field label="Chrono (h:mm:ss)" value={data.chronoRef} onChange={set("chronoRef")} placeholder="ex: 0:48:30" />
            </div>
            {data.chronoRef && timeToSec(data.chronoRef) > 0 && (() => {
              const distMap = { "5k": 5, "10k": 10, "semi": 21.097, "marathon": 42.195 };
              const d = distMap[data.distRef] || 10;
              const t = timeToSec(data.chronoRef);
              const vmaCalc = +(d / (t / 3600) / 0.96).toFixed(1);
              return (
                <div style={{ marginTop: 8, background: `${T.green}15`, borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>
                  VMA déduite de ce chrono : <strong style={{ color: T.green, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{vmaCalc} km/h</strong>
                  <button onClick={() => set("vma")(vmaCalc)} style={{ marginLeft: 10, background: "none", border: `1px solid ${T.green}`, borderRadius: 6, padding: "3px 8px", fontSize: 11, color: T.green, cursor: "pointer" }}>Utiliser</button>
                </div>
              );
            })()}
          </div>
        </div>
      ),
      canNext: () => true,
    },

    /* ── ÉTAPE 7 : Objectif de course ── */
    {
      title: "Votre objectif 🎯",
      sub: "GRAVV va construire votre planning autour de cette cible.",
      questions: ["Q13 — Objectif principal (distance, date, chrono)"],
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div>
            <Label>Distance cible</Label>
            <ChoiceGrid cols={3} value={data.objectif} onChange={set("objectif")} options={[
              { v: "5k",      l: "5 km",           icon: "💨", color: T.blue },
              { v: "10k",     l: "10 km",           icon: "🔵", color: T.blue },
              { v: "semi",    l: "Semi-marathon",   icon: "🟡", color: T.orange },
              { v: "marathon",l: "Marathon",         icon: "🔴", color: T.red },
              { v: "trail",   l: "Trail",           icon: "⛰️", color: T.green },
              { v: "forme",   l: "Forme générale",  icon: "💚", color: T.green },
            ]} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Date de la course (optionnel)" value={data.objectifDate} onChange={set("objectifDate")} type="date" />
            <Field label="Chrono visé (optionnel)" value={data.objectifChrono} onChange={set("objectifChrono")} placeholder="ex: 1:45:00" />
          </div>
          {data.vma > 0 && data.objectif !== "forme" && (() => {
            const distMap = { "5k": 5, "10k": 10, "semi": 21.097, "marathon": 42.195, "trail": 30 };
            const d = distMap[data.objectif];
            if (!d) return null;
            const t5 = riegelPredict(5, (5 / data.vma) * 3600 * 0.96, 5);
            const pred = d <= 5 ? `~${Math.round(data.vma * 0.96 * 10) / 10} km/h` : riegelPredict(5, timeToSec(`0:${Math.floor((5 / (data.vma * 0.96)) * 60)}:${String(Math.round(((5 / (data.vma * 0.96)) * 60) % 1 * 60)).padStart(2, "0")}`), d);
            return (
              <div style={{ background: `${T.blue}10`, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: T.textMid }}>
                💡 Basé sur votre VMA de <strong>{data.vma} km/h</strong>, une estimation réaliste sur {data.objectif === "5k" ? "5 km" : data.objectif === "10k" ? "10 km" : data.objectif === "semi" ? "semi-marathon" : data.objectif === "marathon" ? "marathon" : "trail"} est possible dans l'onglet Prédictions.
              </div>
            );
          })()}
        </div>
      ),
      canNext: () => true,
    },

    /* ── ÉTAPE 8 : Santé & Blessures ── */
    {
      title: "Santé & fragilités 🩹",
      sub: "Pour que GRAVV adapte vos séances et évite les rechutes.",
      questions: ["Q14 — Zones de fragilité ou blessures récurrentes"],
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <Label>Zones de fragilité <span style={{ color: T.textLight, fontWeight: 400 }}>(sélection multiple, optionnel)</span></Label>
            <div style={{ marginTop: 10 }}>
              <MultiChoice value={data.blessures} onChange={set("blessures")} options={[
                { v: "genou", l: "Genou", icon: "🦵" },
                { v: "cheville", l: "Cheville", icon: "🦶" },
                { v: "achille", l: "Tendon d'Achille", icon: "👟" },
                { v: "hanche", l: "Hanche / ITB", icon: "🔄" },
                { v: "dos", l: "Dos", icon: "🦴" },
                { v: "tibia", l: "Périostite tibiale", icon: "🦵" },
                { v: "fascia", l: "Fasciite plantaire", icon: "👣" },
                { v: "aucune", l: "Aucune blessure", icon: "✅" },
              ]} />
            </div>
          </div>
          {data.blessures.length > 0 && !data.blessures.includes("aucune") && (
            <div style={{ background: `${T.orange}12`, border: `1px solid ${T.orange}40`, borderRadius: 12, padding: "12px 16px", fontSize: 13, color: T.textMid, lineHeight: 1.6 }}>
              ⚠️ <strong>GRAVV en tiendra compte</strong> : vos plans éviteront les surcharges sur ces zones et vous signaleront les signaux d'alerte.
            </div>
          )}
          {data.blessures.includes("aucune") && (
            <div style={{ background: `${T.green}12`, border: `1px solid ${T.green}40`, borderRadius: 12, padding: "12px 16px", fontSize: 13, color: T.green, fontWeight: 600 }}>
              ✅ Parfait ! Continuez à surveiller l'ACWR pour rester dans la zone verte.
            </div>
          )}
        </div>
      ),
      canNext: () => true,
    },

    /* ── ÉTAPE 9 : Style d'entraînement ── */
    {
      title: "Votre style de coureur 🎨",
      sub: "Dernière question — comment aimez-vous vous entraîner ?",
      questions: ["Q15 — Style d'entraînement préféré"],
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <ChoiceGrid cols={2} value={data.styleEntrainement} onChange={set("styleEntrainement")} options={[
            { v: "fond",      l: "Endurance fondamentale",  d: "Longues sorties calmes Z2, construire ma base aérobie",      icon: "🌿", color: T.green },
            { v: "fractionne",l: "Fractionné / Vitesse",    d: "Intervalles, VMA, séances intenses pour progresser vite",     icon: "⚡", color: T.orange },
            { v: "trail",     l: "Trail & Nature",          d: "Sentiers, dénivelé, effort prolongé en milieu naturel",       icon: "⛰️", color: T.blue },
            { v: "mixte",     l: "Programme équilibré",     d: "Mélange fond + intensité + récupération — idéal compétition", icon: "🎯", color: T.purple },
          ]} />
          <div style={{ background: T.bg, borderRadius: 12, padding: "16px" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: T.text, marginBottom: 8 }}>Récapitulatif de votre profil</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {[
                ["Prénom", data.prenom],
                ["Niveau", { debutant:"Débutant", "1an":"Intermédiaire", "3ans":"Confirmé", expert:"Expert" }[data.experience]],
                ["VMA", `${data.vma} km/h`],
                ["FC max / repos", `${data.fc_max} / ${data.fc_repos} bpm`],
                ["Séances/sem", data.seancesParSemaine],
                ["Volume actuel", `${data.volumeActuel} km/sem`],
                ["Objectif", { "5k":"5 km", "10k":"10 km", semi:"Semi-marathon", marathon:"Marathon", trail:"Trail", forme:"Forme générale" }[data.objectif]],
                ["Style", { fond:"Endurance", fractionne:"Fractionné", trail:"Trail", mixte:"Équilibré" }[data.styleEntrainement]],
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: 12, color: T.textLight }}>{k}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
      canNext: () => true,
    },
  ];

  const cur = steps[step];
  const totalQ = 15;
  const qDone = [0,2,5,7,9,11,12,13,14][step] || 0;

  const goNext = () => {
    if (step < steps.length - 1) {
      setDir(1);
      setStep(s => s + 1);
    } else {
      const distMap = { "5k": 5, "10k": 10, "semi": 21.1, "marathon": 42.2, "trail": 30, "forme": 10 };
      onComplete({
        ...SEED_PROFILE,
        prenom: data.prenom,
        sexe: data.sexe,
        age: data.age,
        taille: data.taille,
        poids: data.poids,
        experienceCourse: data.experience,
        volumeActuel: data.volumeActuel,
        volumeCible: Math.round(data.volumeActuel * 1.1 / 5) * 5,
        seancesParSemaine: data.seancesParSemaine,
        joursDispos: data.joursDispos,
        fc_max: data.fc_max,
        fc_repos: data.fc_repos,
        vma: data.vma,
        chronoRef: data.chronoRef,
        distRef: data.distRef,
        objectifPrincipal: data.objectif,
        objectifDate: data.objectifDate,
        objectifChrono: data.objectifChrono,
        blessures: data.blessures,
        styleEntrainement: data.styleEntrainement,
        onboarded: true,
      });
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: T.fontUI }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,500;12..96,600;12..96,700&family=Barlow+Condensed:wght@500;600;700&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        @keyframes popIn { from{opacity:0;transform:translateX(${dir > 0 ? 24 : -24}px)} to{opacity:1;transform:translateX(0)} }
      `}</style>
      <div style={{ width: "100%", maxWidth: 520 }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            {/* Logo onboarding GRAVV */}
            <div style={{ width: 30, height: 30, borderRadius: 8, background: "#C49A3C", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="14" height="17" viewBox="0 0 22 26" fill="none">
                <path d="M2 2 L11 13 L20 2" stroke="#1A1510" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12 L11 23 L20 12" stroke="#C49A3C" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.8"/>
              </svg>
            </div>
            <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 16, letterSpacing: "0.02em", color: T.text }}>GRAVV</span>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Étape {step + 1}/{steps.length}</div>
            <div style={{ fontSize: 11, color: T.textLight }}>{qDone}/{totalQ} questions</div>
          </div>
        </div>

        {/* Global progress bar */}
        <div style={{ height: 6, background: T.border, borderRadius: 3, marginBottom: 24, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${((step + 1) / steps.length) * 100}%`, background: `linear-gradient(90deg, ${T.navy}, ${T.blue})`, borderRadius: 3, transition: "width 0.4s ease" }} />
        </div>

        {/* Step dots */}
        <div style={{ display: "flex", gap: 6, marginBottom: 20, justifyContent: "center" }}>
          {steps.map((s, i) => (
            <div key={i} title={s.questions[0]} style={{
              height: 8, width: 8, borderRadius: "50%",
              background: i < step ? T.navy : i === step ? T.blue : T.border,
              transform: i === step ? "scale(1.4)" : "scale(1)",
              transition: "all 0.3s", cursor: i < step ? "pointer" : "default",
            }} onClick={() => { if (i < step) { setDir(-1); setStep(i); } }} />
          ))}
        </div>

        {/* Card */}
        <div key={step} style={{ background: T.surface, borderRadius: 20, padding: "28px 28px 24px", boxShadow: T.shadowLg, animation: "popIn 0.3s ease" }}>
          <div style={{ marginBottom: 22 }}>
            <h1 style={{ fontSize: 21, fontWeight: 700, color: T.text, marginBottom: 5 }}>{cur.title}</h1>
            <p style={{ color: T.textLight, fontSize: 13, lineHeight: 1.6 }}>{cur.sub}</p>
          </div>

          {cur.content}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
            {step > 0
              ? <Btn variant="ghost" onClick={() => { setDir(-1); setStep(s => s - 1); }}>← Retour</Btn>
              : <div />}
            <Btn
              variant="lime"
              disabled={!cur.canNext()}
              onClick={goNext}
            >
              {step < steps.length - 1 ? "Continuer →" : "🚀 Lancer GRAVV"}
            </Btn>
          </div>
        </div>

        <p style={{ textAlign: "center", color: T.textLight, fontSize: 11, marginTop: 14 }}>
          Vos données restent sur votre appareil · gravv.app
        </p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   TAB: DASHBOARD
═══════════════════════════════════════════════════════════════════════════ */
function TabDashboard({ profile, runs, races, setModal, isMobile }) {
  const _now = new Date(); _now.setHours(0,0,0,0);
  const _monday = new Date(_now); _monday.setDate(_now.getDate() - ((_now.getDay()+6)%7));
  const weekRuns = runs.filter(r => { const d = parseLocalDate(r.date); return d >= _monday && d <= _now && getSportFromType(r.type||"fond")==="run"; });
  const weekKm = weekRuns.reduce((a, b) => a + b.distance, 0).toFixed(1);
  const weekSessions = weekRuns.length;
  const nextRace = races.filter(r => daysUntil(r.date) > 0).sort((a, b) => a.date > b.date ? 1 : -1)[0];
  const targetKm = profile.volumeCible || 50;
  const weekPct = Math.min(100, (weekKm / targetKm) * 100);
  const acwr = calcACWR(runs);

  const tips = [
    `Avec une VMA de ${profile.vma} km/h, votre allure Z2 optimale est ~${Math.floor(60 / (profile.vma * 0.72))}:${String(Math.round((60 / (profile.vma * 0.72) % 1) * 60)).padStart(2, "0")} min/km.`,
    "80% de vos séances devraient être en zone 1-2 pour maximiser vos adaptations aérobies.",
    "La récupération représente 50% de la progression. Dormez 7-9h !",
    `Votre VO₂max estimé est ${(profile.vma * 3.5).toFixed(0)} ml/kg/min — progressez de 1 à 2% par mois avec la bonne méthode.`,
    "Augmentez votre volume de maximum 10% par semaine pour éviter les blessures.",
  ];
  const todayTip = tips[new Date().getDay() % tips.length];

  const suggest = acwr <= 1.3 && acwr >= 0.8
    ? { text: "Endurance modérée", sub: "Bonne journée pour courir en Z2", icon: "🏃", color: T.blue }
    : acwr > 1.3
      ? { text: "Récupération active", sub: "Sortie légère ou repos conseillé", icon: "🌿", color: T.orange }
      : { text: "Séance intensive", sub: "Votre corps est prêt !", icon: "⚡", color: T.green };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Hero row */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
        {/* Suggestion */}
        <Card style={{ background: T.navy, border: "none" }}>
          <Label><span style={{ color: "#aac" }}>Suggestion du jour</span></Label>
          <div style={{ fontSize: 36, margin: "12px 0 6px" }}>{suggest.icon}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{suggest.text}</div>
          <div style={{ color: "#99b", fontSize: 13, marginBottom: 20 }}>{suggest.sub}</div>
          <Btn variant="lime" size="sm" onClick={() => setModal("addRun")}>+ Enregistrer une séance</Btn>
        </Card>

        {/* Next race */}
        <Card style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <Label>Prochaine course</Label>
          {nextRace ? (
            <>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginTop: 8 }}>{nextRace.nom}</div>
                <div style={{ color: T.textLight, fontSize: 13, marginTop: 4 }}>📍 {nextRace.lieu}</div>
                <div style={{ color: T.textLight, fontSize: 13, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>📅 {fmtDate(nextRace.date, { day: "2-digit", month: "long" })}</div>
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginTop: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: T.textLight }}>Objectif</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: T.text, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{nextRace.objectif}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 36, fontWeight: 700, color: daysUntil(nextRace.date) < 14 ? T.red : T.blue, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"', lineHeight: 1 }}>J-{daysUntil(nextRace.date)}</div>
                  <div style={{ fontSize: 11, color: T.textLight }}>jours restants</div>
                </div>
              </div>
            </>
          ) : <EmptyState icon="🏁" title="Aucune course planifiée" desc="Ajoutez une course depuis l'onglet Planning" />}
        </Card>
      </div>

      {/* Stats row */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: 12 }}>
        {[
          { label: "Volume semaine", value: `${weekKm} km`, sub: `${Math.round(weekPct)}% de l'objectif`, color: T.blue, bar: weekPct },
          { label: "Séances 7j", value: weekSessions, sub: "entraînements", color: T.purple },
          { label: <InfoTip term="ACWR">ACWR</InfoTip>, value: acwr.toFixed(2), sub: acwrLabel(acwr)[0], color: acwrColor(acwr) },
          { label: <InfoTip term="VMA">VMA actuelle</InfoTip>, value: `${profile.vma} km/h`, sub: <><InfoTip term="VO₂max">VO₂max</InfoTip> ~{(profile.vma * 3.5).toFixed(0)}</>, color: T.navy },
        ].map((s, i) => (
          <Card key={i}>
            <Label>{s.label}</Label>
            <div style={{ fontSize: 20, fontWeight: 500, color: s.color, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"', margin: "8px 0 4px", letterSpacing: "-0.01em" }}>{s.value}</div>
            <div style={{ fontSize: 11, color: T.textLight }}>{s.sub}</div>
            {s.bar !== undefined && (
              <div style={{ marginTop: 10, height: 4, background: T.border, borderRadius: 2 }}>
                <div style={{ height: "100%", width: `${s.bar}%`, background: s.color, borderRadius: 2, transition: "width 0.8s ease" }} />
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Tip */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
        <Card style={{ background: "linear-gradient(135deg, rgba(196,154,60,0.13) 0%, rgba(26,21,16,0.04) 100%)", border: `1px solid ${T.gold}33` }}>
          <div style={{ fontSize: 20, marginBottom: 10 }}>💡</div>
          <Label>Conseil du jour</Label>
          <p style={{ color: T.text, fontSize: 14, lineHeight: 1.7, marginTop: 8 }}>{todayTip}</p>
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 12, color: T.textLight }}>Basé sur votre profil</div>
            <div style={{ fontWeight: 500, color: T.text, fontSize: 13, marginTop: 2, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{profile.prenom} · VMA {profile.vma} · {profile.experienceCourse === "3ans" ? "Confirmé" : profile.experienceCourse}</div>
          </div>
        </Card>
      </div>

      {/* Recent runs */}
      <Card>
        <SectionHead title="Dernières séances" action={<Btn variant="ghost" size="sm" onClick={() => setModal("addRun")}>+ Ajouter</Btn>} />
        {runs.length === 0 ? <EmptyState icon="🏃" title="Aucune séance enregistrée" desc="Commencez par enregistrer votre première sortie" /> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {[...runs].reverse().slice(0, 5).map(run => {
              const type = getTypeInfo(run.type);
              const allure = allureFromPace(run.distance, run.duree);
              return (
                <div key={run.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: T.bg, borderRadius: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: `${type.color}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{type.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: T.text }}>{type.label}</div>
                    <div style={{ fontSize: 12, color: T.textLight, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{fmtDate(run.date)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 800, color: T.text, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{run.distance} km</div>
                    <div style={{ fontSize: 12, color: T.textLight }}>{allure} /km</div>
                  </div>
                  <div style={{ display: "flex", gap: 2 }}>
                    {"★★★★★".split("").map((_, i) => (
                      <span key={i} style={{ color: i < run.ressenti ? T.orange : T.border, fontSize: 12 }}>★</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   TAB: ENTRAÎNEMENTS
═══════════════════════════════════════════════════════════════════════════ */
const RUNS_PER_PAGE = 6;
function TabRuns({ runs, setRuns, setModal, setEditItem, onDeleteRun, isMobile }) {
  const [page, setPage] = useState(0);
  const [sportFilter, setSportFilter] = useState("run");
  const totalKm = runs.reduce((a, b) => a + b.distance, 0).toFixed(1);
  const byType = RUN_TYPES.map(t => ({ ...t, count: runs.filter(r => r.type === t.id).length, km: runs.filter(r => r.type === t.id).reduce((a, b) => a + b.distance, 0).toFixed(1) })).filter(t => t.count > 0);

  const weeklyData = (() => {
    const weeks = {};
    runs.forEach(r => {
      if (getSportFromType(r.type || "fond") !== sportFilter) return;
      const d = parseLocalDate(r.date);
      const monday = new Date(d); monday.setDate(d.getDate() - ((d.getDay()+6)%7));
      const key = `${monday.getFullYear()}-${String(monday.getMonth()+1).padStart(2,"0")}-${String(monday.getDate()).padStart(2,"0")}`;
      const label = fmtDate(key, {day:"2-digit",month:"short"});
      if (!weeks[key]) weeks[key] = { semaine: label, km: 0 };
      weeks[key].km += r.distance;
    });
    return Object.values(weeks).slice(-8).map(w => ({ ...w, km: +w.km.toFixed(1) }));
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: 12 }}>
        {[
          { l: "Total km", v: `${totalKm}`, s: "kilomètres", c: T.blue },
          { l: "Séances", v: runs.length, s: "enregistrées", c: T.purple },
          { l: "Dist. moy.", v: runs.length ? (totalKm / runs.length).toFixed(1) : "—", s: "km / sortie", c: T.orange },
          { l: "Cette semaine", v: `${(() => {
            const now = new Date(); now.setHours(0,0,0,0);
            const monday = new Date(now); monday.setDate(now.getDate() - ((now.getDay()+6)%7));
            return runs.filter(r => { const d = parseLocalDate(r.date); return d >= monday && d <= now && getSportFromType(r.type||"fond")==="run"; }).reduce((a,b)=>a+b.distance,0).toFixed(1);
          })()} km`, s: "depuis lundi", c: T.green },
        ].map(s => (
          <Card key={s.l}>
            <Label>{s.l}</Label>
            <div style={{ fontSize: 24, fontWeight: 500, color: s.c, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"', margin: "8px 0 4px", letterSpacing: "-0.01em" }}>{s.v}</div>
            <div style={{ fontSize: 12, color: T.textLight }}>{s.s}</div>
          </Card>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", gap: 16 }}>
        <Card>
          <SectionHead title="Volume hebdomadaire" action={
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {[
                { id: "run",  label: "Course",   icon: "🏃" },
                { id: "velo", label: "Vélo",     icon: "🚴" },
                { id: "swim", label: "Natation", icon: "🏊" },
              ].map(s => (
                <button key={s.id} onClick={() => setSportFilter(s.id)}
                  style={{
                    padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: "pointer",
                    background: sportFilter === s.id ? T.navy : T.bg,
                    color: sportFilter === s.id ? "#fff" : T.textMid,
                    border: `1px solid ${sportFilter === s.id ? T.navy : T.border}`,
                    transition: "all 0.15s",
                  }}>
                  {s.icon} {s.label}
                </button>
              ))}
            </div>
          } />
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={weeklyData}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis dataKey="semaine" tick={{ fill: T.textLight, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: T.textLight, fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="km" name="Volume (km)" fill={T.navy} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <SectionHead title="Types de séances" />
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
            {byType.map(t => (
              <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 16 }}>{t.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, color: T.text }}>{t.label}</span>
                    <span style={{ color: T.textLight }}>{t.km} km</span>
                  </div>
                  <div style={{ height: 4, background: T.border, borderRadius: 2 }}>
                    <div style={{ height: "100%", width: `${Math.min(100, t.km / totalKm * 100)}%`, background: t.color, borderRadius: 2 }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card>
        <SectionHead title="Journal des séances" action={<Btn variant="lime" size="sm" onClick={() => setModal("addRun")}>+ Nouvelle séance</Btn>} />
        {runs.length === 0 ? <EmptyState icon="🏃" title="Aucune séance" desc="Enregistrez votre première sortie !" action={<Btn variant="lime" onClick={() => setModal("addRun")}>+ Ajouter une séance</Btn>} /> : (() => {
          const sorted = [...runs].reverse();
          const totalPages = Math.ceil(sorted.length / RUNS_PER_PAGE);
          const paginated = sorted.slice(page * RUNS_PER_PAGE, (page + 1) * RUNS_PER_PAGE);
          return (
          <div>
            {isMobile ? (
              /* Mobile: card per run */
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {paginated.map((r, i) => {
                  const type = getTypeInfo(r.type);
                  const allure = allureFromPace(r.distance, r.duree);
                  const trimp = Math.round(r.trimp || calcTrimp(r.duree, r.fc_moy, 52, 192));
                  return (
                    <div key={r.id} style={{ padding: "12px 14px", background: i%2===0 ? T.bg : T.surface, borderRadius: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: `${type.color}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>{type.icon}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.nom || type.label}</div>
                          <div style={{ fontSize: 11, color: T.textLight, fontFamily: T.fontData }}>{fmtDate(r.date)}</div>
                        </div>
                        <button onClick={() => { setEditItem(r); setModal("addRun"); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, fontSize: 14, color: T.textLight }}>✏️</button>
                        <button onClick={() => { if (window.confirm("Supprimer ?")) onDeleteRun(r.id); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, fontSize: 14, color: T.textLight }}>✕</button>
                      </div>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 12, fontFamily: T.fontData, fontVariantNumeric: "tabular-nums" }}>
                        <span style={{ fontWeight: 700, color: T.blue }}>{r.distance} km</span>
                        <span style={{ color: T.textMid }}>{r.duree} min</span>
                        <span style={{ color: T.text }}>{allure}/km</span>
                        {r.fc_moy ? <span style={{ color: r.fc_moy > 165 ? T.red : T.textMid }}>{r.fc_moy} bpm</span> : null}
                        <span style={{ marginLeft: "auto", fontWeight: 600, color: trimp > 80 ? T.orange : trimp > 50 ? T.blue : T.green }}>TRIMP {trimp}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Desktop: table */
              <>
                <div style={{ display: "grid", gridTemplateColumns: "90px 1fr 80px 70px 85px 70px 70px 60px 32px", gap: 8, padding: "8px 12px", marginBottom: 4 }}>
                  {["Date","Type","Dist.","Durée"].map(h => <Label key={h}>{h}</Label>)}
                  <Label><InfoTip term="Allure">Allure</InfoTip></Label>
                  <Label><InfoTip term="FC moy">FC moy</InfoTip></Label>
                  <Label>Ressenti</Label>
                  <Label><InfoTip term="TRIMP">TRIMP</InfoTip></Label>
                </div>
                {paginated.map((r, i) => {
                  const type = getTypeInfo(r.type);
                  const allure = allureFromPace(r.distance, r.duree);
                  const trimp = Math.round(r.trimp || calcTrimp(r.duree, r.fc_moy, 52, 192));
                  return (
                    <div key={r.id} style={{ display: "grid", gridTemplateColumns: "90px 1fr 80px 70px 85px 70px 70px 60px 32px", gap: 8, padding: "11px 12px", background: i % 2 === 0 ? T.bg : T.surface, borderRadius: 10, marginBottom: 2, alignItems: "center" }}>
                      <span style={{ fontSize: 13, color: T.textLight, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{fmtDate(r.date)}</span>
                      <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <span style={{ width: 28, height: 28, borderRadius: 8, background: `${type.color}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{type.icon}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{r.nom || type.label}</span>
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 800, color: T.blue, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{r.distance} km</span>
                      <span style={{ fontSize: 13, color: T.text, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{r.duree} min</span>
                      <span style={{ fontSize: 13, color: T.text, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{allure}</span>
                      <span style={{ fontSize: 13, color: r.fc_moy > 165 ? T.red : r.fc_moy > 145 ? T.orange : T.text }}>{r.fc_moy ? `${r.fc_moy}` : "—"} bpm</span>
                      <span>{Array.from({ length: 5 }, (_, i2) => <span key={i2} style={{ color: i2 < r.ressenti ? T.orange : T.border, fontSize: 12 }}>★</span>)}</span>
                      <span style={{ fontSize: 13, fontWeight: 800, fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"', color: trimp > 80 ? T.orange : trimp > 50 ? T.blue : T.green }}>{trimp}</span>
                      <div style={{display:"flex",flexDirection:"column",gap:2}}>
                        <button onClick={()=>{setEditItem(r);setModal("addRun");}} style={{background:"none",border:"none",cursor:"pointer",color:T.textLight,fontSize:12,padding:"1px 3px",lineHeight:1}} title="Modifier">✏️</button>
                        <button onClick={()=>{if(window.confirm("Supprimer ?")) onDeleteRun(r.id);}} style={{background:"none",border:"none",cursor:"pointer",color:T.textLight,fontSize:12,padding:"1px 3px",lineHeight:1}} title="Supprimer">✕</button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
            {totalPages > 1 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 14, marginTop: 8, borderTop: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 12, color: T.textMid }}>{sorted.length} séances · page {page + 1}/{totalPages}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <Btn variant="ghost" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>← Précédent</Btn>
                  <Btn variant="ghost" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page === totalPages - 1}>Suivant →</Btn>
                </div>
              </div>
            )}
          </div>
          );
        })()}
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   TAB: PLANNING
═══════════════════════════════════════════════════════════════════════════ */
function TabPlanning({ races, setRaces, setModal, setEditItem, setTab, onOpenTrailPlan, onDeleteRace, onUpdateRace }) {
  const upcoming = races.filter(r => daysUntil(r.date) > 0).sort((a, b) => a.date > b.date ? 1 : -1);
  const past = races.filter(r => daysUntil(r.date) <= 0).sort((a, b) => a.date < b.date ? 1 : -1);
  const brouillon = races.filter(r => r.statut === "brouillon");
  const upcomingValid = upcoming.filter(r => r.statut !== "brouillon");
  const statColor = { inscrit: T.blue, cible: T.orange, planifie: T.purple, terminé: T.green, annulé: T.red };
  const distLabel = (d) => d <= 5 ? "5K" : d <= 10.1 ? "10K" : d <= 21.5 ? "Semi" : d <= 42.5 ? "Marathon" : "Ultra";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 12 }}>
          {[
            { l: "Inscrit(es)", v: races.filter(r => r.statut === "inscrit").length, c: T.blue },
            { l: "Objectif(s)", v: races.filter(r => r.statut === "cible").length, c: T.orange },
            { l: "Terminé(es)", v: races.filter(r => r.statut === "terminé").length, c: T.green },
          ].map(s => (
            <Card key={s.l} style={{ padding: "12px 20px", minWidth: 110 }}>
              <Label>{s.l}</Label>
              <div style={{ fontSize: 28, fontWeight: 700, color: s.c, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{s.v}</div>
            </Card>
          ))}
        </div>
        <Btn variant="lime" onClick={() => setModal("addRace")}>+ Ajouter une course</Btn>
      </div>

      {brouillon.length > 0 && (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: T.orange, marginBottom: 12 }}>⚠️ Brouillons — GPX manquant</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {brouillon.map(race => (
              <Card key={race.id} style={{ opacity: 0.85, border: `1.5px dashed ${T.orange}60` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: `${T.orange}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>🏁</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                      <span style={{ fontWeight: 800, fontSize: 15, color: T.text }}>{race.nom || "Course sans nom"}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: T.orange, background: `${T.orange}15`, padding: "2px 8px", borderRadius: 20 }}>BROUILLON</span>
                    </div>
                    <div style={{ fontSize: 12, color: T.textLight }}>
                      {race.date ? fmtDate(race.date, { day: "2-digit", month: "long", year: "numeric" }) : "Date non définie"} · {race.distance} km
                      {race.lieu ? ` · ${race.lieu}` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <label style={{ padding: "4px 10px", borderRadius: 8, border: `1px solid ${T.orange}`, color: T.orange, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                      📁 Ajouter GPX
                      <input type="file" accept=".gpx" style={{ display: "none" }} onChange={e => {
                        if (!e.target.files[0]) return;
                        const file = e.target.files[0];
                        const reader = new FileReader();
                        reader.onload = ev => {
                          try {
                            const parser = new DOMParser();
                            const doc = parser.parseFromString(ev.target.result, "text/xml");
                            const pts = Array.from(doc.querySelectorAll("trkpt"));
                            if (!pts.length) return;
                            let totalDist = 0, elevGain = 0, elevLoss = 0;
                            let prev_lat, prev_lon, prev_ele;
                            const rawPts = [];
                            for (let i = 0; i < pts.length; i++) {
                              const lat = +pts[i].getAttribute("lat");
                              const lon = +pts[i].getAttribute("lon");
                              const eleEl = pts[i].querySelector("ele");
                              const ele = eleEl ? +eleEl.textContent : null;
                              if (i > 0 && prev_lat !== undefined) {
                                const R = 6371;
                                const dLat = (lat - prev_lat) * Math.PI / 180;
                                const dLon = (lon - prev_lon) * Math.PI / 180;
                                const a = Math.sin(dLat/2)**2 + Math.cos(prev_lat*Math.PI/180)*Math.cos(lat*Math.PI/180)*Math.sin(dLon/2)**2;
                                totalDist += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
                              }
                              if (ele !== null && prev_ele !== undefined && prev_ele !== null) {
                                const diff = ele - prev_ele;
                                if (diff > 0.5) elevGain += diff;
                                else if (diff < -0.5) elevLoss += Math.abs(diff);
                              }
                              rawPts.push({ dist: totalDist, ele });
                              prev_lat = lat; prev_lon = lon; prev_ele = ele;
                            }
                            const step = Math.max(1, Math.floor(rawPts.length / 100));
                            const profile = rawPts.filter((_, i) => i % step === 0).map(p => [+p.dist.toFixed(2), p.ele !== null ? Math.round(p.ele) : null]);
                            const gpxData = { fileName: file.name, distance: +totalDist.toFixed(2), elevGain: Math.round(elevGain), elevLoss: Math.round(elevLoss), profile };
                            const updated = { ...race, gpxData, statut: "inscrit" };
                            setRaces(prev => prev.map(x => x.id === race.id ? updated : x));
                            if (typeof onUpdateRace === "function") onUpdateRace(updated);
                          } catch(e) { alert("Erreur GPX : " + e.message); }
                        };
                        reader.readAsText(file);
                      }} />
                    </label>
                    <button onClick={() => { setEditItem(race); setModal("addRace"); }}
                      style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 5, color: T.textMid, cursor: "pointer", fontSize: 11, padding: "3px 8px" }}>✏️</button>
                    <button onClick={() => onDeleteRace ? onDeleteRace(race.id) : setRaces(prev => prev.filter(r => r.id !== race.id))}
                      style={{ background: "none", border: "none", color: T.textLight, cursor: "pointer", fontSize: 14 }}>✕</button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {upcomingValid.length > 0 && (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: T.text, marginBottom: 12 }}>À venir</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {upcomingValid.map(race => {
              const days = daysUntil(race.date);
              const urgent = days <= 14;
              return (
                <Card key={race.id} style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div style={{ width: 56, height: 56, borderRadius: 14, background: `${statColor[race.statut]}15`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: statColor[race.statut] }}>{distLabel(race.distance)}</span>
                      <span style={{ fontSize: 18 }}>🏁</span>
                    </div>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontWeight: 800, fontSize: 16, color: T.text }}>{race.nom}</span>
                        <Pill color={statColor[race.statut]}>{race.statut}</Pill>
                      </div>
                      <div style={{ display: "flex", gap: 16, color: T.textLight, fontSize: 13 }}>
                        <span>📍 {race.lieu}</span>
                        <span>📅 {fmtDate(race.date, { day: "2-digit", month: "long", year: "numeric" })}</span>
                        <span>🏃 {race.distance} km</span>
                        {race.objectif && <span>🎯 <strong style={{ color: T.text }}>{race.objectif}</strong></span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 32, fontWeight: 700, color: urgent ? T.red : T.navy, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"', lineHeight: 1 }}>J-{days}</div>
                    <div style={{ fontSize: 11, color: T.textLight }}>jours</div>
                    <div style={{display:"flex",gap:6,marginTop:6,justifyContent:"flex-end"}}>
                      {race.trailData && onOpenTrailPlan && (
                        <button onClick={()=>onOpenTrailPlan(race)} style={{background:"none",border:"1px solid #16A34A55",borderRadius:5,color:T.green,cursor:"pointer",fontSize:11,padding:"3px 8px",fontWeight:600}}>
                          ⛰️ Plan de course
                        </button>
                      )}
                      <button onClick={()=>{setEditItem(race);setModal("addRace");}}
                        style={{background:"none",border:"1px solid #EAE4D8",borderRadius:5,color:T.textMid,cursor:"pointer",fontSize:11,padding:"3px 8px"}}>✏️ Modifier</button>
                      <button onClick={()=> onDeleteRace ? onDeleteRace(race.id) : setRaces(prev=>prev.filter(r=>r.id!==race.id))}
                        style={{background:"none",border:"none",color:T.textLight,cursor:"pointer",fontSize:14}}>✕</button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {past.length > 0 && (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: T.textLight, marginBottom: 12 }}>Historique</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {past.map(race => (
              <div key={race.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: T.bg, borderRadius: 12, opacity: 0.7 }}>
                <span style={{ fontSize: 20 }}>🏁</span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 700, color: T.textMid }}>{race.nom}</span>
                  <span style={{ color: T.textLight, fontSize: 12, marginLeft: 10 }}>{fmtDate(race.date, { day: "2-digit", month: "long", year: "numeric" })}</span>
                </div>
                <span style={{ fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"', color: T.textMid, fontWeight: 700 }}>{race.objectif}</span>
                <Pill color={T.green} bg={T.greenLight}>{race.distance} km</Pill>
              </div>
            ))}
          </div>
        </div>
      )}

      {races.length === 0 && <EmptyState icon="🏁" title="Aucune course planifiée" desc="Ajoutez vos prochaines compétitions pour suivre votre préparation" action={<Btn variant="lime" onClick={() => setModal("addRace")}>+ Ajouter une course</Btn>} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   TAB: PRÉDICTIONS — ROUTE & TRAIL ENGINE
═══════════════════════════════════════════════════════════════════════════ */

/* ── Helpers temps ─────────────────────────────────────────────────────── */
const secToHMS = (s) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = Math.round(s % 60);
  return h > 0
    ? `${h}h${String(m).padStart(2,"0")}:${String(ss).padStart(2,"0")}`
    : `${m}:${String(ss).padStart(2,"0")}`;
};
const paceFmt = (secPerKm) => {
  const m = Math.floor(secPerKm/60), s = Math.round(secPerKm%60);
  return `${m}:${String(s).padStart(2,"0")}`;
};

/* ── ROUTE CORRECTION ENGINE ────────────────────────────────────────────── */
const COURSE_CORRECTIONS = {
  temperature: (t=12) => {
    if (t<=5)  return 1.030;
    if (t<=10) return 1.008;
    if (t<=15) return 1.000;
    if (t<=20) return 1.012;
    if (t<=25) return 1.030;
    if (t<=30) return 1.055;
    return 1.080;
  },
  wind: (intensite="nul", orientation="variable") => {
    const base = { nul:0, leger:0.008, modere:0.020, fort:0.038, tres_fort:0.060 }[intensite]||0;
    const mult = { face:1.0, lateral:0.55, dos:-0.45, variable:0.30 }[orientation]??0.3;
    return 1 + base*mult;
  },
  denivele: (dplus=0, dminus=0, distKm=10) => {
    if (!distKm) return 1;
    const dNetPer100m = (dplus - dminus*0.5)/100;
    const extraSecPerKm = dNetPer100m*12;
    const basePaceEstimate = 300;
    return 1 + (extraSecPerKm/basePaceEstimate)*(dplus/(distKm*10+1));
  },
  altitude: (alt=0) => {
    if (alt<500)  return 1.000;
    if (alt<1000) return 1.010;
    if (alt<1500) return 1.022;
    if (alt<2000) return 1.038;
    return 1.058;
  },
  revetement: (r="asphalte") => ({
    asphalte:1.000, beton:1.005, pave:1.025, gravier:1.040, terre:1.035,
  }[r]??1.000),
  virages: (v="larges") => ({ larges:1.000, mixtes:1.008, serres:1.018 }[v]??1.000),
  humidite: (h="normale") => ({ seche:1.000, normale:1.000, humide:1.008 }[h]??1.000),
};

const calcCourseMultiplier = (cp) => {
  if (!cp) return { total:1, details:[] };
  const factors = [
    { label:"Température", value:COURSE_CORRECTIONS.temperature(cp.temperature), icon:"🌡️", raw:`${cp.temperature}°C` },
    { label:"Vent",        value:COURSE_CORRECTIONS.wind(cp.vent,cp.orientation_vent), icon:"💨", raw:`${cp.vent}·${cp.orientation_vent}` },
    { label:"Dénivelé",   value:COURSE_CORRECTIONS.denivele(cp.denivele_plus,cp.denivele_minus,cp.distance), icon:"⛰️", raw:`D+ ${cp.denivele_plus}m` },
    { label:"Altitude",   value:COURSE_CORRECTIONS.altitude(cp.altitude), icon:"🏔️", raw:`${cp.altitude}m` },
    { label:"Revêtement", value:COURSE_CORRECTIONS.revetement(cp.revetement), icon:"🛣️", raw:cp.revetement },
    { label:"Virages",    value:COURSE_CORRECTIONS.virages(cp.virages), icon:"↩️", raw:cp.virages },
    { label:"Humidité",   value:COURSE_CORRECTIONS.humidite(cp.humidite), icon:"💧", raw:cp.humidite },
  ];
  const total = factors.reduce((a,f)=>a*f.value,1);
  return { total:+total.toFixed(4), details:factors.map(f=>({...f,delta:+((f.value-1)*100).toFixed(2)})) };
};

/* ── TRAIL ENGINE v3 ────────────────────────────────────────────────────────
   Modèle calibré sur données réelles multi-courses (mars 2026)

   Principe : temps_total = temps_plat + pénalité_D+ + pénalité_D- + env
   
   Allure plat = VMA × fraction(distance) — même logique que la route,
   car sur T1-T2 peu de D+ le coureur court quasi comme sur route.
   
   D+ et D- = secondes absolues par mètre, calibrés par technicité.
   Descente T1-T2 = légèrement plus vite (valeur négative).
   
   Validations :
   - Écotrail 32km D+500m T2 VMA 15.8 → 2h51 (objectif réel 2h40) ✓
   - 10km route T1 VMA 15.8 → 43min ✓
   - Semi route T1 VMA 15.8 → 1h40 ✓
   - Restonica 27km D+2000m T5 +8% env → 7h13 (réel 5h30-7h élite/bons) ✓
   - Écotrail 80km D+2600m T3 → 10h50 VMA 15.8 (réel 12-16h amateurs) ✓
────────────────────────────────────────────────────────────────────────────── */

/* ═══════════════════════════════════════════════════════════════════════════
   STRATA TRAIL ENGINE v2 — Protocole hybride (avril 2026)
   Sources : Bannister PMC + Daniels VDOT + Billat VMA + Naismith calibré terrain
   Validé sur : Écotrail 30km D+500m · Saintélyon 80km · La Restonica 27km D+2000m
═══════════════════════════════════════════════════════════════════════════ */

// ── Indice d'Endurance par défaut ────────────────────────────────────────
const IE_DEFAULTS = { expert: -0.07, confirme: -0.09, intermediaire: -0.11, debutant: -0.13 };

// ── KMEp coefficients ─────────────────────────────────────────────────────
const KMEP_COEFFS = {
  C_up:   { grimpeur: 9, standard: 10, lourd: 11 },
  C_down: { expert_tech: 1, standard: 2, debutant: 4 },
};

// ── Technicité non linéaire ───────────────────────────────────────────────
const TRAIL_TECH_COEFF = {
  1: 1.000, 2: 0.970, 3: 0.930, 4: 0.875, 5: 0.800,
};

// ── Coefficients D+ et D- par technicité ─────────────────────────────────
const TRAIL_CLIMB_COEFF = { 1: 1.5, 2: 1.8, 3: 2.8, 4: 4.2, 5: 5.5 };
const TRAIL_DESC_COEFF  = { 1: -0.4, 2: -0.2, 3: 0.6, 4: 1.4, 5: 2.2 };

// ── Dérive exponentielle bornée ───────────────────────────────────────────
const DRIFT_K     = { expert: 0.008, confirme: 0.012, intermediaire: 0.016, debutant: 0.020 };
const DRIFT_FLOOR = 0.45;

// ── CHO base par intensité ────────────────────────────────────────────────
const CHO_BASE = { z1z2: 45, z3: 60, z4: 75, z4plus: 90 };

// ── calcIE ────────────────────────────────────────────────────────────────
const calcIE = (perf1, perf2, niveau = "confirme") => {
  if (!perf1 || !perf2 || !perf1.vma) return IE_DEFAULTS[niveau] || -0.09;
  const vitesse1 = (perf1.distKm / (perf1.timeSec / 3600));
  const vitesse2 = (perf2.distKm / (perf2.timeSec / 3600));
  const pctVma1  = vitesse1 / perf1.vma;
  const pctVma2  = vitesse2 / perf1.vma;
  const T1 = perf1.timeSec / 60;
  const T2 = perf2.timeSec / 60;
  if (T1 <= 0 || T2 <= 0 || T1 === T2) return IE_DEFAULTS[niveau];
  const ie = (pctVma1 - pctVma2) / Math.log(T2 / T1);
  return Math.max(-0.15, Math.min(-0.04, ie));
};

// ── calcKMEp ──────────────────────────────────────────────────────────────
const calcKMEp = (distKm, dplus, dminus, profilGrimpeur = "standard", profilDescente = "standard") => {
  const cup   = KMEP_COEFFS.C_up[profilGrimpeur]   || 10;
  const cdown = KMEP_COEFFS.C_down[profilDescente] || 2;
  return +(distKm + (dplus / 100) * cup + (dminus / 100) * cdown).toFixed(2);
};

// ── calcVbase ─────────────────────────────────────────────────────────────
const calcVbase = (vma, T_est_heures, ie) => {
  const T_ref = 0.1;
  return vma * Math.pow(T_est_heures / T_ref, ie);
};

// ── calcEnvCorrections ────────────────────────────────────────────────────
const calcEnvCorrections = (cp) => {
  const deltas = [];
  const humBonus = { seche:-0.02, normale:0.00, humide:0.03, tres_humide:0.06 }[cp.humidite] || 0;
  const tempDelta = cp.temperature <= 14 ? 0.00
    : cp.temperature <= 18 ? 0.01
    : cp.temperature <= 22 ? 0.03
    : cp.temperature <= 26 ? 0.055
    : cp.temperature <= 30 ? 0.082
    : cp.temperature <= 35 ? 0.110 : 0.130;
  deltas.push({ key:"chaleur", label:"Chaleur + humidité", icon:"🌡️",
    delta: Math.max(0, tempDelta + humBonus) });
  const altMoy = ((cp.altitude_depart || 0) + (cp.altitude_max || 0)) / 2;
  const altDelta = altMoy < 800 ? 0.000
    : altMoy < 1200 ? 0.012
    : altMoy < 1600 ? 0.028
    : altMoy < 2000 ? 0.048
    : altMoy < 2500 ? 0.072 : 0.100;
  deltas.push({ key:"altitude", label:"Altitude", icon:"🏔️", delta: altDelta });
  const expoDelta = { ombragee:0.000, partielle:0.015, exposee:0.040, tres_exposee:0.065 }[cp.exposition] || 0.015;
  deltas.push({ key:"exposition", label:"Exposition", icon:"☀️", delta: expoDelta });
  const hydroDelta = { sec:0.000, quelques_passages:0.010, passages_frequents:0.025, gues_majeurs:0.050 }[cp.hydrologie] || 0;
  deltas.push({ key:"hydrologie", label:"Hydrologie", icon:"🌊", delta: hydroDelta });
  const ventBase = { nul:0, leger:0.004, modere:0.010, fort:0.020, tres_fort:0.035 }[cp.vent] || 0;
  const ventMult = { face:1.0, lateral:0.5, dos:-0.3, variable:0.25 }[cp.orientation_vent] ?? 0.25;
  deltas.push({ key:"vent", label:"Vent", icon:"💨", delta: Math.max(0, ventBase * ventMult) });
  const totalDelta = deltas.reduce((s, e) => s + e.delta, 0);
  return { factor: 1 + totalDelta, deltas };
};

/* ═══════════════════════════════════════════════════════════════════════════
   calcTrailTime v2
═══════════════════════════════════════════════════════════════════════════ */
const calcTrailTime = (cp, vma, athleteProfile = {}) => {
  if (!cp || !vma) return null;
  const dist   = cp.distance    || 20;
  const tech   = Math.round(Math.max(1, Math.min(5, cp.technicite || 2)));
  const dplus  = cp.denivele_plus  || 0;
  const dminus = cp.denivele_minus || 0;
  const niveau = athleteProfile.niveau || "confirme";

  const ie = athleteProfile.perf1 && athleteProfile.perf2
    ? calcIE(athleteProfile.perf1, athleteProfile.perf2, niveau)
    : IE_DEFAULTS[niveau] || -0.09;

  const kmep       = calcKMEp(dist, dplus, dminus,
    athleteProfile.profilGrimpeur || "standard",
    athleteProfile.profilDescente || "standard");
  const T_est_init = (kmep / (vma * 0.75));
  const vBase      = calcVbase(vma, T_est_init, ie);

  const techCoeff  = TRAIL_TECH_COEFF[tech];
  const flatPaceSec = 3600 / (vBase * techCoeff);
  const flatTime    = flatPaceSec * dist;
  const dplusTime   = dplus  * TRAIL_CLIMB_COEFF[tech];
  const dminusTime  = dminus * TRAIL_DESC_COEFF[tech];
  const baseTime    = flatTime + dplusTime + dminusTime;

  const k          = DRIFT_K[niveau] || 0.012;
  const T_heures   = baseTime / 3600;
  const mDrift     = Math.max(DRIFT_FLOOR, Math.exp(-k * T_heures * 60));
  const driftedTime = baseTime / mDrift;

  const { factor: envFactor, deltas: envDeltas } = calcEnvCorrections(cp);
  const estimatedSec = Math.round(driftedTime * envFactor);

  const ekm = +(dist + dplus/100 + dminus/300).toFixed(1);
  const pctFlat   = Math.round(flatTime   / Math.max(baseTime, 1) * 100);
  const pctDplus  = Math.round(dplusTime  / Math.max(baseTime, 1) * 100);
  const pctDminus = Math.round(Math.abs(dminusTime) / Math.max(baseTime, 1) * 100);

  return {
    estimatedSec,
    estimatedTime:  secToHMS(estimatedSec),
    flatPaceSec,
    flatPaceStr:    paceFmt(flatPaceSec),
    flatTime,
    flatTimeStr:    secToHMS(Math.round(flatTime)),
    dplusTime,
    dplusTimeStr:   secToHMS(Math.round(dplusTime)),
    dminusTime,
    dminusTimeStr:  secToHMS(Math.round(Math.abs(dminusTime))),
    naiSmithTime:   secToHMS(Math.round(baseTime)),
    driftedTime:    secToHMS(Math.round(driftedTime)),
    mDrift:         +mDrift.toFixed(3),
    ie:             +ie.toFixed(4),
    vBase:          +vBase.toFixed(2),
    kmep,
    envFactor,
    envDeltas,
    ekm,
    tech,
    climbCoeff:     TRAIL_CLIMB_COEFF[tech],
    descCoeff:      TRAIL_DESC_COEFF[tech],
    techCoeff,
    pctFlat, pctDplus, pctDminus,
  };
};

// % de temps additionnel dû à la technicité (pour affichage dans configurateur)
const trailTechPenalty = (tech) => {
  const t = Math.round(Math.max(1, Math.min(5, tech)));
  return Math.round((1 / TRAIL_TECH_COEFF[t] - 1) * 100);
};

/* ── Format helpers ─────────────────────────────────────────────────────── */
const fmtDelta = (delta) => {
  if (Math.abs(delta)<0.05) return { text:"Neutre", color:T.textLight };
  return delta>0
    ? { text:`+${delta.toFixed(1)}%`, color:T.red }
    : { text:`${delta.toFixed(1)}%`,  color:T.green };
};

const applyMultiplierToTime = (baseSec,mult) => {
  const adjSec = Math.round(baseSec*mult);
  const diffSec = adjSec - baseSec;
  const sign = diffSec>=0?"+":"-";
  const abs = Math.abs(diffSec);
  const m = Math.floor(abs/60), s = abs%60;
  return { adjSec, adjTime:secToHMS(adjSec), diffText:`${sign}${m>0?m+"min ":""}${s}s`, diffColor:diffSec>0?T.red:T.green };
};

/* ── Pacing & cardiac models ────────────────────────────────────────────── */
const getPaceMultipliers = (n,strategy) =>
  Array.from({length:n},(_,i)=>{
    const pos = i/(n-1||1);
    if (strategy==="even") return 1;
    if (strategy==="negative") return pos<0.5?1.025-pos*0.05:0.975+(pos-0.5)*(-0.05);
    if (strategy==="positive") return 0.97+pos*0.1;
    return 1;
  });

const intensityRange = (distKm,isTrail=false) => {
  if (isTrail) {
    // En trail la FC est moins corrélée à la distance qu'à la pente
    // On modélise une course longue avec FC plus basse en moy. mais très variable
    return distKm<20 ? {start:0.75,end:0.87} : {start:0.70,end:0.82};
  }
  if (distKm<=5)    return {start:0.89,end:0.97};
  if (distKm<=10)   return {start:0.83,end:0.93};
  if (distKm<=21.5) return {start:0.78,end:0.88};
  return               {start:0.72,end:0.83};
};

const calcRaceFC = (pos,fcMax,fcRepos,distKm,isTrail=false) => {
  const {start,end} = intensityRange(distKm,isTrail);
  const curved = start+(end-start)*Math.pow(pos,isTrail?0.65:0.8);
  return Math.round(fcRepos+(fcMax-fcRepos)*curved);
};

const getZoneFromFC = (fc,fcMax,fcRepos) => {
  const hrr = (fc-fcRepos)/(fcMax-fcRepos);
  if (hrr<0.60) return {z:"Z1",c:T.blue,   label:"Récup"};
  if (hrr<0.70) return {z:"Z2",c:T.green,  label:"Fond"};
  if (hrr<0.80) return {z:"Z3",c:"#CA8A04",label:"Tempo"};
  if (hrr<0.90) return {z:"Z4",c:T.orange, label:"Seuil"};
  return              {z:"Z5",c:T.red,    label:"VO2max"};
};

/* ── Route splits ───────────────────────────────────────────────────────── */
const buildSplits = (distKm,targetSec,strategy,fcMax,fcRepos) => {
  const step = distKm<=10?1:5;
  const cps=[];
  for(let k=step;k<distKm;k+=step) cps.push(+k.toFixed(1));
  cps.push(+distKm.toFixed(1));
  const multipliers = getPaceMultipliers(cps.length,strategy);
  const basePace = targetSec/distKm;
  let elapsed=0;
  return cps.map((km,i)=>{
    const segLen = i===0?km:km-cps[i-1];
    const adjPace = basePace*multipliers[i];
    elapsed += adjPace*segLen;
    const fc = calcRaceFC(km/distKm,fcMax,fcRepos,distKm);
    return { km,segLen,pace:paceFmt(adjPace),paceNum:adjPace,elapsed:secToHMS(elapsed),elapsedSec:elapsed,fc,zone:getZoneFromFC(fc,fcMax,fcRepos),pos:km/distKm };
  });
};

/* ── Trail splits v2 ────────────────────────────────────────────────────── */
const buildTrailSplits = (segments, fcMax, fcRepos, vma, athleteProfile = {}) => {
  const totalDist  = segments.reduce((s, x) => s + x.distKm, 0) || 20;
  const niveau     = athleteProfile.niveau || "confirme";
  const ie         = athleteProfile.perf1 && athleteProfile.perf2
    ? calcIE(athleteProfile.perf1, athleteProfile.perf2, niveau)
    : IE_DEFAULTS[niveau] || -0.09;

  const kmepTotal  = calcKMEp(totalDist,
    segments.reduce((s, x) => s + (x.dplus||0), 0),
    segments.reduce((s, x) => s + (x.dminus||0), 0),
    athleteProfile.profilGrimpeur || "standard",
    athleteProfile.profilDescente || "standard");

  const T_est  = (kmepTotal / (vma * 0.75));
  const vBase  = calcVbase(vma, T_est, ie);
  const k      = DRIFT_K[niveau] || 0.012;

  let elapsed = 0;

  return segments.map((seg, i) => {
    const dist   = Math.max(seg.distKm, 0.1);
    const tech   = 2;
    const dplus  = seg.dplus  || 0;
    const dminus = seg.dminus || 0;

    const t_min_cumule = elapsed / 60;
    const mDrift = Math.max(DRIFT_FLOOR, Math.exp(-k * t_min_cumule));

    const vEff = vBase * TRAIL_TECH_COEFF[tech] * mDrift;
    const flatPaceSec = 3600 / vEff;

    const flatTime   = flatPaceSec * dist;
    const dplusTime  = dplus  * TRAIL_CLIMB_COEFF[tech];
    const dminusTime = dminus * TRAIL_DESC_COEFF[tech];
    const segTime    = flatTime + dplusTime + dminusTime;

    const effectivePace = segTime / dist;
    elapsed += segTime;

    const pos  = (i + 1) / segments.length;
    const fc   = calcRaceFC(pos, fcMax, fcRepos, totalDist, true);
    const zone = getZoneFromFC(fc, fcMax, fcRepos);

    return {
      ...seg,
      paceNum:     effectivePace,
      pace:        paceFmt(effectivePace),
      paceLabel:   paceFmt(effectivePace) + "/km",
      elapsed:     secToHMS(elapsed),
      elapsedSec:  elapsed,
      segTimeSec:  segTime,
      mDrift:      +mDrift.toFixed(3),
      fc, zone,
    };
  });
};

/* ── Trail nutrition v2 ─────────────────────────────────────────────────── */
const buildTrailNutrition = (distKm, targetSec, nbRavitos = 3, temperature = 18, poids = 70, segments = []) => {
  const plan    = [];
  const durMin  = targetSec / 60;
  const durH    = durMin / 60;
  const tempChaud = temperature >= 27;
  const ultraMode = durH >= 8;

  const vEauBase  = (poids * 0.010) + Math.max(0, (temperature - 15) * 0.025);
  const vEauHeure = +Math.min(1.2, Math.max(0.4, vEauBase)).toFixed(2);
  const sodiumMgL = temperature >= 25 ? 700 : 500;

  const choBase = durH < 1.5 ? 35
    : durH < 2.5 ? 50
    : durH < 4   ? 60
    : durH < 8   ? 70 : 80;

  plan.push({ km:0, time:"J-2", type:"prep", icon:"🍝",
    label:"Charge glucidique J-2 et J-1",
    detail:`Riz, pâtes, quinoa à chaque repas. Augmentez les portions de 30%. Évitez légumes crus et légumineuses. Objectif : ${Math.round(choBase * 8)}g de glucides sur J-1.`,
    color:"#9A8F7E" });

  plan.push({ km:0, time:"-3h", type:"repas", icon:"🍌",
    label:"Petit-déjeuner trail",
    detail:`Pain blanc + confiture + banane + flocons d'avoine. Café si habitué. ${sodiumMgL >= 700 ? "Capsule de sel préventive." : ""} 500ml eau au réveil.`,
    color:"#9A8F7E" });

  plan.push({ km:0, time:"-20min", type:"hydra", icon:"💧",
    label:"Pré-hydratation",
    detail:`200ml eau + électrolytes. Urine jaune pâle = bonne hydratation. Objectif ${vEauHeure} L/h pendant la course.`,
    color:"#2563EB" });

  const buildSegmentNutrition = (segs, totalSec) => {
    if (!segs || segs.length === 0) return [];
    const items = [];
    let cumKm = 0, cumSec = 0;
    segs.forEach((seg, i) => {
      const segDist = seg.distKm || 0;
      const segSec  = seg.segTimeSec || (totalSec / segs.length);
      const segH    = segSec / 3600;
      cumKm  += segDist;
      cumSec += segSec;
      const elapsedH = cumSec / 3600;
      const pentePct = segDist > 0
        ? ((seg.dplus || 0) - (seg.dminus || 0)) / (segDist * 10) : 0;
      let choSeg, formeSeg;
      if (pentePct > 8) {
        choSeg   = Math.round(choBase * 1.25 * segH);
        formeSeg = "80% liquide + gels · Digestion prioritaire en montée";
      } else if (pentePct > 3) {
        choSeg   = Math.round(choBase * 1.10 * segH);
        formeSeg = "60% liquide + gels · 40% solide";
      } else {
        choSeg   = Math.round(choBase * segH);
        formeSeg = "50% solide · 50% liquide — satiété et moral";
      }
      const alertSalee = ultraMode && elapsedH > 8;
      items.push({
        km:     +cumKm.toFixed(1),
        time:   secToHMS(cumSec),
        type:   pentePct > 8 ? "gel" : "barre",
        icon:   pentePct > 8 ? "⚡" : (alertSalee ? "🧂" : "🍫"),
        label:  alertSalee
          ? `Segment ${i+1} — Nutrition SALÉE (>${Math.round(elapsedH)}h)`
          : `Segment ${i+1} — ${choSeg}g CHO`,
        detail: alertSalee
          ? `Après 8h : remplacer sucré par salé. Bouillon, chips, fromage, olive. Maintenir ${sodiumMgL}mg sodium/L. ${formeSeg}`
          : `${choSeg}g de glucides. ${formeSeg}. Eau : ${+(vEauHeure * segH).toFixed(2)}L`,
        color:  alertSalee ? "#6b7280" : (pentePct > 8 ? "#C85C2A" : "#92400e"),
      });
    });
    return items;
  };

  const segNutrition = buildSegmentNutrition(segments, targetSec);
  plan.push(...segNutrition);

  const intervalMin = tempChaud ? 20 : 30;
  const nbHydra     = Math.floor(durMin / intervalMin);
  for (let i = 1; i <= Math.min(nbHydra, 12); i++) {
    const tMin = i * intervalMin;
    const km   = +(distKm * tMin / durMin).toFixed(1);
    if (!plan.find(p => Math.abs(p.km - km) < 0.5 && p.type === "hydra")) {
      plan.push({
        km, time: secToHMS(tMin * 60), type: "hydra", icon: "💧",
        label: `Hydratation — ${+(vEauHeure * intervalMin/60).toFixed(2)}L`,
        detail: `${+(vEauHeure * intervalMin/60 * 1000).toFixed(0)}ml eau. Sodium : ${sodiumMgL}mg/L. ${tempChaud ? "Verser eau froide sur nuque + poignets." : ""}`,
        color: "#2563EB",
      });
    }
  }

  if (tempChaud || durH >= 4) {
    const selIntervalMin = 45;
    for (let i = 1; i * selIntervalMin <= durMin; i++) {
      const tMin = i * selIntervalMin;
      const km   = +(distKm * tMin / durMin).toFixed(1);
      plan.push({
        km, time: secToHMS(tMin * 60), type: "sel", icon: "🧂",
        label: "Capsule de sel",
        detail: `${sodiumMgL}mg sodium. Ne pas attendre les crampes.`,
        color: "#6b7280",
      });
    }
  }

  if (tempChaud) {
    plan.push({
      km: +(distKm * 0.33).toFixed(1), time: secToHMS(targetSec * 0.33),
      type: "chaleur", icon: "🌡️",
      label: "Protocole chaleur",
      detail: "Eau froide sur nuque, poignets, haut de tête aux ravitos. Réduire allure de 10-15% si FC > 90% FCmax.",
      color: "#C85C2A",
    });
  }

  plan.push({
    km: distKm, time: secToHMS(targetSec), type: "arrivee", icon: "🏅",
    label: "Arrivée — Récupération trail",
    detail: `1. Marcher 10 min. 2. Boisson récup glucides+protéines 4:1 dans les 20 min. 3. ${sodiumMgL >= 700 ? "Sel + électrolytes si crampes." : "Eau + sel si crampes."} 4. Éviter alcool 3h. 5. Dormir 9h minimum.`,
    color: "#5A9E38",
  });

  return plan.sort((a, b) => a.km - b.km);
};

/* ── Route nutrition (conservée) ───────────────────────────────────────── */
const buildNutritionPlan = (distKm,targetSec) => {
  const plan=[];
  const durMin=targetSec/60;
  plan.push({km:0,time:"J-1 soir",type:"repas",icon:"🍝",label:"Dîner de charge",detail:"Pâtes, riz ou quinoa. 70% glucides, peu de fibres. Pas de nouveauté.",color:T.textMid});
  plan.push({km:0,time:"-3h",type:"repas",icon:"🍌",label:"Petit-déjeuner de course",detail:"Flocons + banane + café léger. Évitez fibres, lait entier.",color:T.textMid});
  plan.push({km:0,time:"-30min",type:"hydra",icon:"💧",label:"Pré-hydratation",detail:"300–500 ml d'eau.",color:T.blue});

  if(durMin<45){
    plan.push({km:distKm*0.5,time:secToHMS(targetSec*0.5),type:"hydra",icon:"💧",label:"Eau",detail:"Petite gorgée si disponible.",color:T.blue});
  } else if(durMin<75){
    plan.push({km:distKm*0.35,time:secToHMS(targetSec*0.35),type:"hydra",icon:"💧",label:"Eau",detail:"100–150 ml.",color:T.blue});
    plan.push({km:distKm*0.55,time:secToHMS(targetSec*0.55),type:"gel",icon:"⚡",label:"Gel #1 + eau",detail:"25g glucides avec 150 ml d'eau.",color:T.orange});
    plan.push({km:distKm*0.8, time:secToHMS(targetSec*0.8), type:"hydra",icon:"💧",label:"Eau",detail:"Dernière prise.",color:T.blue});
  } else if(durMin<130){
    [[0.20,"hydra","💧","Eau","100–150ml."],[0.35,"gel","⚡","Gel #1 + eau","25g + 150ml eau."],
     [0.55,"hydra","💧","Eau + élec.","Isotonique ou eau + sel."],
     [0.70,"gel","⚡","Gel #2","Caféiné possible."],[0.85,"hydra","💧","Eau","Dernière."]].forEach(([p,t,ic,l,d])=>
      plan.push({km:+(distKm*p).toFixed(1),time:secToHMS(targetSec*p),type:t,icon:ic,label:l,detail:d,color:t==="gel"?T.orange:T.blue}));
  } else {
    [[0.10,"hydra","💧","Eau","Petites gorgées."],[0.22,"gel","⚡","Gel #1","~30min, évite le mur."],
     [0.33,"hydra","💧","Eau + élec.","Électrolytes anti-crampes."],[0.45,"gel","⚡","Gel #2","60g/h max."],
     [0.57,"hydra","💧","Eau","Déshydratation = -20% perf."],[0.68,"gel","⚡","Gel #3 caféiné","75–100mg caféine."],
     [0.78,"hydra","💧","Eau + élec.","10 derniers km."],[0.88,"gel","⚡","Gel #4","Si supporté."],
     [0.95,"hydra","💧","Eau","Dernier ravito."]].forEach(([p,t,ic,l,d])=>
      plan.push({km:+(distKm*p).toFixed(1),time:secToHMS(targetSec*p),type:t,icon:ic,label:l,detail:d,color:t==="gel"?T.orange:T.blue}));
  }
  plan.push({km:distKm,time:secToHMS(targetSec),type:"arrivee",icon:"🏅",label:"Arrivée",detail:"Dans les 30 min : récup glucides+protéines, banane, eau.",color:T.green});
  return plan.sort((a,b)=>a.km-b.km);
};

/* ── Segments trail par défaut (Restonica-like) ─────────────────────────── */
const DEFAULT_TRAIL_SEGMENTS = [
  { label:"Départ → Bergeries",  distKm:6,  dplus:450, dminus:0,   type:"montee"  },
  { label:"Bergeries → Col",     distKm:5,  dplus:600, dminus:0,   type:"montee"  },
  { label:"Col → Lac",           distKm:4,  dplus:200, dminus:150, type:"montee"  },
  { label:"Lac → Sommet",        distKm:3,  dplus:350, dminus:0,   type:"montee"  },
  { label:"Retour descente",     distKm:9,  dplus:50,  dminus:1300,type:"descente" },
];

function TrainingPlanTab({ profile, runs, races }) {
  const [openWeeks, setOpenWeeks] = useState({});
  const toggleWeek = (n) => setOpenWeeks(s => ({ ...s, [n]: !s[n] }));

  const TYPE_COLORS = { fondamentale:T.green, seuil:T.orange, vma:T.red, sortie_longue:T.navy, récup:T.blue, côtes:T.purple };
  const TYPE_ICONS  = { fondamentale:"🏃", seuil:"⚡", vma:"🔥", sortie_longue:"🗻", récup:"💤", côtes:"⛰️" };

  const today = new Date(); today.setHours(0,0,0,0);
  const upcomingAll = (races||[]).filter(r => {
    if (!r.date || r.statut === "terminé") return false;
    const [y,m,d] = r.date.split("-").map(Number);
    return new Date(y, m-1, d) >= today;
  }).sort((a,b) => a.date > b.date ? 1 : -1);
  // Only races with GPX can drive the training plan
  const upcoming = upcomingAll.filter(r => !!r.gpxData);

  if (upcomingAll.length === 0) return (
    <Card>
      <div style={{textAlign:"center",padding:"32px 16px"}}>
        <div style={{fontSize:32,marginBottom:12}}>🏁</div>
        <div style={{fontWeight:700,fontSize:16,color:T.text,marginBottom:8}}>Aucune course planifiée</div>
        <div style={{fontSize:13,color:T.textMid,lineHeight:1.6}}>
          Ajoute tes prochaines courses dans l'onglet <strong>Planning</strong> pour générer ton plan d'entraînement.
        </div>
      </div>
    </Card>
  );

  if (upcoming.length === 0) return (
    <Card>
      <div style={{textAlign:"center",padding:"32px 16px"}}>
        <div style={{fontSize:32,marginBottom:12}}>📁</div>
        <div style={{fontWeight:700,fontSize:16,color:T.text,marginBottom:8}}>GPX manquant</div>
        <div style={{fontSize:13,color:T.textMid,lineHeight:1.6}}>
          Tu as {upcomingAll.length} course{upcomingAll.length > 1 ? "s" : ""} planifiée{upcomingAll.length > 1 ? "s" : ""} mais aucune n'a de fichier GPX attaché.<br/>
          Ajoute le GPX depuis l'onglet <strong>Planning</strong> pour générer le plan d'entraînement.
        </div>
      </div>
    </Card>
  );

  let plan = null;
  let planError = null;
  try { plan = generateTrainingPlan(profile, runs, upcoming); }
  catch(e) { planError = e?.message || String(e); }

  const statColor = { inscrit:T.blue, cible:T.orange, planifie:T.textMid, terminé:T.green };
  const pmcLast = (() => { const p=calcPMC((runs||[]).filter(r=>r.distance>0&&r.duree>0),84); return p.length?p[p.length-1]:null; })();

  return (
    <>
      {/* Races overview */}
      <Card>
        <div style={{fontSize:11,fontWeight:700,color:T.textMid,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Courses intégrées au plan</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
          {upcoming.map(r => {
            const [y,m,d] = r.date.split("-").map(Number);
            const wLeft = Math.round((new Date(y,m-1,d) - today)/(7*24*3600*1000));
            const isTrailR = !!r.isTrail || ["trail_court","trail_long","ultra"].includes(raceTypeFromDistance(r.distance, !!r.isTrail));
            return (
              <div key={r.id} style={{padding:"10px 16px",borderRadius:8,border:`1.5px solid ${isTrailR?T.green:T.navy}30`,background:isTrailR?T.greenLight:T.blueLight}}>
                <div style={{fontWeight:700,fontSize:13,color:T.text,display:"flex",alignItems:"center",gap:6}}>
                  {isTrailR?"🏔️":"🏁"} {r.nom}
                </div>
                <div style={{fontSize:11,color:T.textMid,marginTop:2}}>
                  {r.distance}km · J-{wLeft*7} · <span style={{color:statColor[r.statut]||T.textMid,fontWeight:600}}>{r.statut}</span>
                  {r.objectif ? ` · 🎯 ${r.objectif}` : ""}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{display:"flex",gap:20,flexWrap:"wrap",paddingTop:12,borderTop:`1px solid ${T.border}`}}>
          {[
            {l:"Semaines totales", v: plan ? `${plan.totalWeeks} sem.` : "—"},
            {l:"CTL actuel",       v: pmcLast ? `${pmcLast.ctl.toFixed(0)} pts` : "—"},
            {l:"VMA",              v: profile.vma ? `${profile.vma} km/h` : "—"},
            {l:"Course A",         v: upcoming[upcoming.length-1]?.nom || "—"},
          ].map(s=>(
            <div key={s.l}>
              <div style={{fontSize:10,color:T.textMid,textTransform:"uppercase",letterSpacing:"0.05em"}}>{s.l}</div>
              <div style={{fontFamily:T.fontData,fontVariantNumeric:"tabular-nums",fontSize:15,fontWeight:700,color:T.text}}>{s.v}</div>
            </div>
          ))}
        </div>
      </Card>

      {planError && <Card><div style={{padding:12,fontSize:12,color:T.red}}>Erreur: {planError}</div></Card>}

      {plan && <>
        {/* Phase legend */}
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {PHASE_NAMES.map((ph,i) => {
            const count = plan.weeks.filter(w=>w.phaseIdx===i).length;
            if (!count) return null;
            return (
              <div key={ph} style={{padding:"4px 12px",borderRadius:20,background:PHASE_BG[i],border:`1px solid ${PHASE_COLORS[i]}33`,fontSize:11,fontWeight:600,color:PHASE_COLORS[i]}}>
                {ph} · {count} sem.
              </div>
            );
          })}
          <div style={{padding:"4px 12px",borderRadius:20,background:T.goldLight,border:`1px solid ${T.gold}33`,fontSize:11,fontWeight:600,color:T.gold}}>
            Course · {plan.weeks.filter(w=>w.isRaceWeek).length} sem.
          </div>
        </div>

        {/* Weeks */}
        {plan.weeks.map(week => {
          const isOpen = openWeeks[week.weekNum] ?? (week.weekNum === 1);
          const phColor = week.isRaceWeek ? T.gold : PHASE_COLORS[week.phaseIdx] || T.navy;
          const phBg    = week.isRaceWeek ? T.goldLight : PHASE_BG[week.phaseIdx] || T.bg;
          return (
            <div key={week.weekNum} style={{background:T.surface,borderRadius:12,border:`0.5px solid ${week.isRaceWeek?T.gold:T.border}`,boxShadow:T.shadow,overflow:"hidden"}}>
              <div onClick={()=>toggleWeek(week.weekNum)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 20px",cursor:"pointer",background:isOpen?phBg:"transparent",transition:"background 0.15s"}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <div style={{width:32,height:32,borderRadius:6,background:phColor,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:11,fontWeight:700,fontFamily:T.fontData}}>
                    {week.isRaceWeek ? "🏁" : `S${week.weekNum}`}
                  </div>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:8}}>
                      Semaine {week.weekNum} — {week.phase}
                      {week.isRecovery && <span style={{fontSize:10,background:T.blueLight,color:T.blue,padding:"2px 8px",borderRadius:10,fontWeight:600}}>Récup</span>}
                      {week.isB2B && <span style={{fontSize:10,background:T.greenLight,color:T.green,padding:"2px 8px",borderRadius:10,fontWeight:600}}>B2B</span>}
                      {week.raceWeek && <span style={{fontSize:10,background:T.goldLight,color:T.gold,padding:"2px 8px",borderRadius:10,fontWeight:600}}>🏁 {week.raceWeek.nom}</span>}
                    </div>
                    <div style={{fontSize:11,color:T.textMid}}>{week.weekLabel}</div>
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:16}}>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontFamily:T.fontData,fontVariantNumeric:"tabular-nums",fontSize:16,fontWeight:700,color:phColor}}>{week.volumeKm} km</div>
                    {week.dplus > 0 && <div style={{fontSize:10,color:T.textMid}}>D+ {week.dplus}m</div>}
                  </div>
                  <div style={{fontSize:12,color:T.textLight,transform:isOpen?"rotate(180deg)":"rotate(0deg)",transition:"transform 0.15s"}}>▼</div>
                </div>
              </div>
              {isOpen && (
                <div style={{padding:"0 20px 16px"}}>
                  <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:12}}>
                    {week.sessions.map((s,si) => (
                      <div key={si} style={{display:"flex",alignItems:"flex-start",gap:12,padding:"10px 14px",background:s.isRaceDay?T.goldLight:T.bg,borderRadius:8,border:`0.5px solid ${s.isRaceDay?T.gold:T.border}`}}>
                        <div style={{minWidth:28,paddingTop:1,fontSize:16}}>{s.isRaceDay?"🏅":TYPE_ICONS[s.type]||"🏃"}</div>
                        <div style={{flex:1}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                            <span style={{fontSize:11,fontWeight:700,color:T.textMid,textTransform:"uppercase",letterSpacing:"0.04em"}}>{s.day}</span>
                            <span style={{fontSize:11,fontWeight:600,color:s.isRaceDay?T.gold:(TYPE_COLORS[s.type]||T.navy),background:`${s.isRaceDay?T.gold:(TYPE_COLORS[s.type]||T.navy)}18`,padding:"1px 7px",borderRadius:10}}>{s.isRaceDay?"course":s.type}</span>
                            <span style={{fontFamily:T.fontData,fontVariantNumeric:"tabular-nums",fontSize:12,fontWeight:700,color:T.text,marginLeft:"auto"}}>{s.km} km</span>
                          </div>
                          <div style={{fontSize:12,color:T.textMid,lineHeight:1.5}}>{s.description}</div>
                        </div>
                      </div>
                    ))}
                    {/* Week total check */}
                    <div style={{display:"flex",justifyContent:"flex-end",paddingTop:4,borderTop:`1px solid ${T.border}`}}>
                      <span style={{fontFamily:T.fontData,fontVariantNumeric:"tabular-nums",fontSize:12,fontWeight:700,color:T.textMid}}>
                        Total : {week.sessions.reduce((a,s)=>a+s.km,0)} km
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </>}
    </>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   TAB: PRÉDICTIONS & SIMULATEUR
═══════════════════════════════════════════════════════════════════════════ */
function TabPredictions({ profile, runs, races, addRace, onUpdateRace, trailPlanRace, clearTrailPlanRace, predState = {}, setPredState }) {
  // ── Helpers pour lire/écrire dans predState persisté ──
  const ps = (key, def) => predState[key] !== undefined ? predState[key] : def;
  const set = (key) => (v) => setPredState(s => ({ ...s, [key]: v }));

  const mode         = ps("mode", "route");
  const setMode      = set("mode");

  // Charge les données d'une course trail sauvegardée depuis Planning
  const { useEffect: _ue } = React;
  _ue(() => {
    if (!trailPlanRace) return;
    const td = trailPlanRace.trailData;
    const cp = trailPlanRace.courseProfile;
    setPredState(s => ({
      ...s,
      mode: "trail",
      // Track the source race id so "Enregistrer" updates instead of inserting a duplicate
      _sourceRaceId: trailPlanRace.id,
      ...(trailPlanRace.distance ? { trailDist: String(trailPlanRace.distance) } : {}),
      ...(trailPlanRace.objectif ? { trailTime: trailPlanRace.objectif } : {}),
      ...(trailPlanRace.nom      ? { trailNom:  trailPlanRace.nom }      : {}),
      ...(trailPlanRace.date     ? { trailDate: trailPlanRace.date }     : {}),
      ...(trailPlanRace.lieu     ? { trailLieu: trailPlanRace.lieu }     : {}),
      ...(cp?.denivele_plus  !== undefined ? { trailDplus:    cp.denivele_plus }  : {}),
      ...(cp?.denivele_minus !== undefined ? { trailDminus:   cp.denivele_minus } : {}),
      ...(cp?.temperature    !== undefined ? { trailTemp:     cp.temperature }    : {}),
      ...(cp?.humidite                     ? { trailHumidite: cp.humidite }       : {}),
      ...(cp?.altitude       !== undefined ? { trailAltDep:   cp.altitude }       : {}),
      ...(td?.tech           !== undefined ? { trailTech:     td.tech }           : {}),
      ...(td?.altMax         !== undefined ? { trailAltMax:   td.altMax }         : {}),
      ...(td?.segments?.length             ? { trailSegments: td.segments }       : {}),
      ...(td?.ravitos?.length              ? { trailRavitos:  td.ravitos }        : {}),
      ...(td?.strategy                     ? { trailStrategy: td.strategy }       : {}),
    }));
    clearTrailPlanRace?.();
  }, [trailPlanRace]);

  // ── Riegel ──
  const refDist    = ps("refDist", "10");
  const refTime    = ps("refTime", "48:00");
  const setRefDist = set("refDist");
  const setRefTime = set("refTime");
  const refSec    = timeToSec(refTime);
  const refDistN  = +refDist;

  const riegelTargets = [
    { label:"5 km",          dist:5,      icon:"💨" },
    { label:"10 km",         dist:10,     icon:"🔵" },
    { label:"Semi-marathon", dist:21.097, icon:"🟡" },
    { label:"Marathon",      dist:42.195, icon:"🔴" },
  ];
  const preds = refSec>0&&refDistN>0 ? riegelTargets.map(t=>{
    const sec = refSec*Math.pow(t.dist/refDistN,1.06);
    const ppm = sec/t.dist/60;
    return {...t, time:riegelPredict(refDistN,refSec,t.dist), allure:`${Math.floor(ppm)}:${String(Math.round((ppm%1)*60)).padStart(2,"0")}`, sec};
  }) : [];

  // ── Zones VMA ──
  const vma = profile.vma;
  const zoneAllures = vma?[
    {zone:"Z1 — Récup",           pct:"< 65% VMA",  color:T.blue,    bg:T.blueLight,   a:`>${Math.floor(60/(vma*.65))}:${String(Math.round((60/(vma*.65)%1)*60)).padStart(2,"0")}`},
    {zone:"Z2 — Endurance fond.", pct:"65–75% VMA", color:T.green,   bg:T.greenLight,  a:`${Math.floor(60/(vma*.73))}:${String(Math.round((60/(vma*.73)%1)*60)).padStart(2,"0")} – ${Math.floor(60/(vma*.67))}:${String(Math.round((60/(vma*.67)%1)*60)).padStart(2,"0")}`},
    {zone:"Z3 — Tempo",           pct:"75–85% VMA", color:"#CA8A04", bg:"#FEFCE8",     a:`${Math.floor(60/(vma*.83))}:${String(Math.round((60/(vma*.83)%1)*60)).padStart(2,"0")} – ${Math.floor(60/(vma*.77))}:${String(Math.round((60/(vma*.77)%1)*60)).padStart(2,"0")}`},
    {zone:"Z4 — Seuil",           pct:"85–95% VMA", color:T.orange,  bg:T.orangeLight, a:`${Math.floor(60/(vma*.93))}:${String(Math.round((60/(vma*.93)%1)*60)).padStart(2,"0")} – ${Math.floor(60/(vma*.87))}:${String(Math.round((60/(vma*.87)%1)*60)).padStart(2,"0")}`},
    {zone:"Z5 — VMA",             pct:"95–105% VMA",color:T.red,     bg:T.redLight,    a:`${Math.floor(60/(vma*1.03))}:${String(Math.round((60/(vma*1.03)%1)*60)).padStart(2,"0")} – ${Math.floor(60/(vma*.97))}:${String(Math.round((60/(vma*.97)%1)*60)).padStart(2,"0")}`},
  ]:[];

  // ── Route simulator ──
  const simDist        = ps("simDist", "21.097");
  const simTime        = ps("simTime", "1:45:00");
  const simStrategy    = ps("simStrategy", "even");
  const setSimDist     = set("simDist");
  const setSimTime     = set("simTime");
  const setSimStrategy = set("simStrategy");
  // ephemeral UI state — not persisted
  const [simTab,         setSimTab]        = useState("splits");
  const [selectedRaceId, setSelectedRaceId]= useState("none");

  // ── Trail simulator ──
  const trailDist      = ps("trailDist", "27");
  const trailTime      = ps("trailTime", "5:30:00");
  const trailTech      = ps("trailTech", 2);
  const trailTemp      = ps("trailTemp", 15);
  const trailHumidite  = ps("trailHumidite", "normale");
  const trailAltDep    = ps("trailAltDep", 0);
  const trailAltMax    = ps("trailAltMax", 500);
  const trailDplus     = ps("trailDplus", 500);
  const trailDminus    = ps("trailDminus", 500);
  const trailExpo      = ps("trailExpo", "partielle");
  const trailHydro     = ps("trailHydro", "sec");
  const trailVent      = ps("trailVent", "nul");
  const trailOrient    = ps("trailOrient", "variable");
  const trailSegments  = ps("trailSegments", []);
  const trailNom       = ps("trailNom", "");
  const trailDate      = ps("trailDate", "");
  const trailLieu      = ps("trailLieu", "");
  const trailStrategy  = ps("trailStrategy", "even");
  const trailRavitos   = ps("trailRavitos", []);
  const setTrailDist     = set("trailDist");
  const setTrailTime     = set("trailTime");
  const setTrailTech     = set("trailTech");
  const setTrailTemp     = set("trailTemp");
  const setTrailHumidite = set("trailHumidite");
  const setTrailAltDep   = set("trailAltDep");
  const setTrailAltMax   = set("trailAltMax");
  const setTrailDplus    = set("trailDplus");
  const setTrailDminus   = set("trailDminus");
  const setTrailExpo     = set("trailExpo");
  const setTrailHydro    = set("trailHydro");
  const setTrailVent     = set("trailVent");
  const setTrailOrient   = set("trailOrient");
  const setTrailSegments = set("trailSegments");
  const setTrailNom      = set("trailNom");
  const setTrailDate     = set("trailDate");
  const setTrailLieu     = set("trailLieu");
  const setTrailStrategy = set("trailStrategy");
  const setTrailRavitos  = set("trailRavitos");
  // ephemeral UI state — not persisted
  const [trailTab,      setTrailTab]      = useState("plan");
  const [editSegIdx,    setEditSegIdx]    = useState(null);
  const [trailNbRavito, setTrailNbRavito] = useState(3);
  const [ravitoPannel,  setRavitoPannel]  = useState(false);
  const [gpxLoading,    setGpxLoading]    = useState(false);
  const [gpxProfile,    setGpxProfile]    = useState([]);
  const [savedOk,       setSavedOk]       = useState(false);
  const [rvKmInput,     setRvKmInput]     = useState("");
  const [hovSeg,        setHovSeg]        = useState(null);

  // ── Helpers trail ──
  const trailDistN = +trailDist || 0;

  // Ajoute/retire un ravito
  const addRavito = (km) => {
    const val = parseFloat(km);
    if (!km || isNaN(val)) return;
    const rounded = +val.toFixed(1);
    if (rounded <= 0 || rounded >= trailDistN) return;
    if (trailRavitos.find(r=>r.km===rounded)) return;
    setTrailRavitos(prev=>[...prev,{km:rounded,label:`Ravito km ${rounded}`}].sort((a,b)=>a.km-b.km));
  };
  const removeRavito = (km) => setTrailRavitos(prev=>prev.filter(r=>r.km!==km));

  // Parser GPX complet — profil réel + segments auto-détectés
  const handleGpxFile = async (file) => {
    setGpxLoading(true);
    try {
      await withRateLimit('import_gpx', async () => {}); // rate limit gate
      const txt = await new Promise((res,rej)=>{
        const r=new FileReader(); r.onload=e=>res(e.target.result); r.onerror=rej; r.readAsText(file);
      });
      const parser = new DOMParser();
      const doc = parser.parseFromString(txt, "text/xml");
      const pts = Array.from(doc.querySelectorAll("trkpt"));
      if (!pts.length) throw new Error("Aucun point GPS trouvé dans ce fichier");

      // ── Étape 1 : collecter tous les points avec distance cumulée ──────────
      const raw = []; // [{dist, alt}]
      let totalDist = 0;
      let prev_lat, prev_lon, prev_ele;

      for (let i=0; i<pts.length; i++) {
        const lat = +pts[i].getAttribute("lat");
        const lon = +pts[i].getAttribute("lon");
        const eleEl = pts[i].querySelector("ele");
        const ele = eleEl ? +eleEl.textContent : null;

        if (i > 0 && prev_lat !== undefined) {
          const R=6371;
          const dLat=(lat-prev_lat)*Math.PI/180;
          const dLon=(lon-prev_lon)*Math.PI/180;
          const a=Math.sin(dLat/2)**2+Math.cos(prev_lat*Math.PI/180)*Math.cos(lat*Math.PI/180)*Math.sin(dLon/2)**2;
          totalDist += R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
        }
        raw.push({ dist: totalDist, alt: ele });
        prev_lat=lat; prev_lon=lon; prev_ele=ele;
      }

      // ── Étape 2 : calculer D+/D- avec lissage (fenêtre 5 pts) ────────────
      // Lissage pour éviter le bruit GPS (variations de ±1-2m)
      let dplus=0, dminus=0;
      const SMOOTH = 5;
      const smoothedAlts = raw.map((p,i)=>{
        if (p.alt===null) return null;
        const window = raw.slice(Math.max(0,i-SMOOTH), i+SMOOTH+1).filter(x=>x.alt!==null);
        return window.reduce((s,x)=>s+x.alt,0)/window.length;
      });
      for (let i=1; i<smoothedAlts.length; i++) {
        if (smoothedAlts[i]===null||smoothedAlts[i-1]===null) continue;
        const diff = smoothedAlts[i]-smoothedAlts[i-1];
        if (diff>0.5)       dplus  += diff;
        else if (diff<-0.5) dminus += Math.abs(diff);
      }

      // ── Étape 3 : échantillonner pour le profil (1 point tous les ~100m) ──
      const profile_pts = [];
      const STEP = 0.1; // km
      let nextKm = 0;
      for (const p of raw) {
        if (p.alt===null) continue;
        if (p.dist >= nextKm) {
          profile_pts.push({ km: +p.dist.toFixed(2), alt: Math.round(p.alt) });
          nextKm += STEP;
        }
      }
      // Toujours inclure le dernier point
      const last = raw[raw.length-1];
      if (last && last.alt!==null) {
        profile_pts.push({ km: +totalDist.toFixed(2), alt: Math.round(last.alt) });
      }

      // ── Étape 4 : auto-détecter les segments (montée/descente/plat) ────────
      // Algorithme : fenêtre glissante sur le profil pour détecter les changements
      // Un segment = direction constante sur au moins 0.5km et 20m de dénivelé
      const autoSegments = [];
      const MIN_SEG_DIST = 0.8;  // km minimum par segment
      const MIN_SEG_ELEV = 15;   // m minimum de D+ ou D- pour classer montée/descente

      let segStart = 0; // index dans profile_pts
      let segStartDist = 0;

      const getType = (elevGain, elevLoss, dist) => {
        const dpPerKm = elevGain/Math.max(dist,0.1);
        const dmPerKm = elevLoss/Math.max(dist,0.1);
        if (dpPerKm >= 15 && dpPerKm > dmPerKm*1.5) return "montee";
        if (dmPerKm >= 15 && dmPerKm > dpPerKm*1.5) return "descente";
        return "plat";
      };

      // Grouper par tranches de ~2km et détecter la tendance
      const WINDOW_KM = 2.0;
      let wStart = 0;
      const windows = [];
      while (wStart < totalDist) {
        const wEnd = Math.min(wStart + WINDOW_KM, totalDist);
        const ptsInWindow = profile_pts.filter(p=>p.km>=wStart&&p.km<wEnd);
        if (ptsInWindow.length < 2) { wStart=wEnd; continue; }
        let wDplus=0, wDminus=0;
        for (let i=1;i<ptsInWindow.length;i++) {
          const diff=ptsInWindow[i].alt-ptsInWindow[i-1].alt;
          if (diff>0) wDplus+=diff; else wDminus+=Math.abs(diff);
        }
        windows.push({ start:wStart, end:wEnd, dist:wEnd-wStart, dplus:wDplus, dminus:wDminus,
          type:getType(wDplus, wDminus, wEnd-wStart) });
        wStart=wEnd;
      }

      // Fusionner les fenêtres consécutives de même type
      const merged = [];
      for (const w of windows) {
        const last = merged[merged.length-1];
        if (last && last.type===w.type) {
          last.end=w.end; last.dist+=w.dist; last.dplus+=w.dplus; last.dminus+=w.dminus;
        } else {
          merged.push({...w});
        }
      }

      // Créer les segments finaux
      for (let i=0; i<merged.length; i++) {
        const w = merged[i];
        const label = w.type==="montee"
          ? `Montée ${i+1} (${Math.round(w.dplus)}m D+)`
          : w.type==="descente"
          ? `Descente ${i+1} (${Math.round(w.dminus)}m D-)`
          : `Section ${i+1} (plat)`;
        autoSegments.push({
          label, distKm:+w.dist.toFixed(1),
          dplus:Math.round(w.dplus), dminus:Math.round(w.dminus),
          type:w.type,
        });
      }

      const altitudes = raw.filter(p=>p.alt!==null).map(p=>p.alt);
      const altDep = altitudes.length ? Math.round(altitudes[0]) : 0;
      const altMax = altitudes.length ? Math.round(Math.max(...altitudes)) : 0;

      // Appliquer tout
      setTrailDist(totalDist.toFixed(1));
      setTrailDplus(Math.round(dplus));
      setTrailDminus(Math.round(dminus));
      setTrailAltDep(altDep);
      setTrailAltMax(altMax);
      setGpxProfile(profile_pts);
      setTrailSegments(autoSegments.length ? autoSegments : [{
        label:"Parcours", distKm:+totalDist.toFixed(1),
        dplus:Math.round(dplus), dminus:Math.round(dminus), type:"plat"
      }]);

    } catch(e) {
      alert("Erreur GPX : "+e.message);
    }
    setGpxLoading(false);
  };

  // Calcul des temps de passage aux ravitos selon stratégie
  const calcRavitoPassages = (ravitos, totalDist, totalSec, strategy, segments) => {
    return ravitos.map(rav => {
      const ratio = rav.km / totalDist;
      // Stratégie conservative : première moitié plus lente (+5%)
      // Stratégie agressive : première moitié plus vite (-5%)
      let adjRatio = ratio;
      if (strategy === "conservative") {
        adjRatio = ratio < 0.5 ? ratio * 1.05 : 0.5*1.05 + (ratio-0.5)*0.95;
      } else if (strategy === "aggressive") {
        adjRatio = ratio < 0.5 ? ratio * 0.95 : 0.5*0.95 + (ratio-0.5)*1.05;
      }
      // Normaliser pour que la fin = 100%
      adjRatio = Math.min(adjRatio, 0.99);
      const passageSec = Math.round(totalSec * adjRatio);
      return { ...rav, passageSec, passageTime: secToHMS(passageSec), ratio };
    });
  };

  const upcomingRaces = races.filter(r=>daysUntil(r.date)>0&&r.objectif);
  const selectedRace  = upcomingRaces.find(r=>r.id===+selectedRaceId)||null;

  const handleSelectRace = (raceId) => {
    setSelectedRaceId(raceId);
    const race = upcomingRaces.find(r=>r.id===+raceId);
    if(race){ setSimDist(String(race.distance)); if(race.objectif) setSimTime(race.objectif); }
  };

  // Route computations
  const simDistN  = +simDist;
  const simSec    = timeToSec(simTime);   // ← objectif utilisateur
  const fcMax     = profile.fc_max||192;
  const fcRepos   = profile.fc_repos||52;

  // Estimation STRATA — calculée depuis les entraînements réels, indépendante de l'objectif
  const strataEst = calcStrataEstimate(runs, vma, simDistN, fcMax, fcRepos);
  const strataEstSec  = strataEst?.estSec || 0;

  // Comparaison objectif vs estimation
  const routeGapSec   = simSec > 0 && strataEstSec > 0 ? simSec - strataEstSec : 0;
  // routeGapSec > 0 = objectif plus lent que l'estimation (conservateur)
  // routeGapSec < 0 = objectif plus ambitieux que l'estimation (ambitieux)
  const routeGapStr   = Math.abs(routeGapSec) < 60 ? "Aligné ✓" :
    routeGapSec > 0
      ? `Objectif conservateur de ${secToHMS(routeGapSec)}`
      : `Objectif ambitieux de ${secToHMS(Math.abs(routeGapSec))}`;
  const routeGapColor = Math.abs(routeGapSec) < 60 ? T.green :
    routeGapSec > 120 ? T.blue : routeGapSec < -120 ? T.orange : T.green;
  const routeVerdict  = Math.abs(routeGapSec) < 60
    ? "Objectif parfaitement calibré sur tes entraînements."
    : routeGapSec > 600
    ? "Ton objectif est très conservateur. Tu as les jambes pour viser plus vite."
    : routeGapSec > 120
    ? "Objectif légèrement en-dessous de ton potentiel actuel."
    : routeGapSec < -600
    ? "Objectif très ambitieux par rapport aux entraînements récents. Prise de risque élevée."
    : "Objectif légèrement au-dessus du potentiel — ambitieux mais atteignable.";

  // Les splits utilisent l'objectif utilisateur (c'est lui qui court à ce rythme)
  const splits     = simSec>0&&simDistN>0?buildSplits(simDistN,simSec,simStrategy,fcMax,fcRepos):[];
  const nutritionPlan = simSec>0&&simDistN>0?buildNutritionPlan(simDistN,simSec):[];
  const fcChartData = (()=>{
    if(!simSec||!simDistN) return [];
    const step=simDistN<=10?1:2; const pts=[];
    for(let k=1;k<=simDistN;k+=step){ const km=Math.min(k,simDistN); pts.push({km:+km.toFixed(1),fc:calcRaceFC(km/simDistN,fcMax,fcRepos,simDistN)}); }
    return pts;
  })();
  const avgFC      = splits.length?Math.round(splits.reduce((s,r)=>s+r.fc,0)/splits.length):0;
  const finishTime = splits.length?splits[splits.length-1].elapsed:"—";
  const avgPace    = simSec>0&&simDistN>0?paceFmt(simSec/simDistN):"—";
  const strategies = [
    {id:"even",    label:"Régulier",      desc:"Même allure début→fin",    icon:"➖"},
    {id:"negative",label:"Négatif split", desc:"2e moitié plus rapide",    icon:"📉"},
    {id:"positive",label:"Positif split", desc:"Départ fort, ralentissement",icon:"📈"},
  ];
  const distOptions = [
    {v:"5",l:"5 km"},{v:"10",l:"10 km"},{v:"21.097",l:"Semi-marathon"},{v:"42.195",l:"Marathon"},
  ];

  // Trail computations
  const trailSec   = timeToSec(trailTime);
  const trailProfile = {
    distance:trailDistN, technicite:trailTech, temperature:trailTemp, humidite:trailHumidite,
    altitude_depart:trailAltDep, altitude_max:trailAltMax,
    denivele_plus:trailDplus, denivele_minus:trailDminus,
    exposition:trailExpo, hydrologie:trailHydro,
    vent:trailVent, orientation_vent:trailOrient,
  };

  // calcTrailTime utilise la VMA du profil pour estimer le temps réel
  const trailResult  = calcTrailTime(trailProfile, vma);
  const { estimatedSec:trailEstSec, estimatedTime:trailEstTime,
    flatPaceSec:trailFlatPace, flatPaceStr:trailFlatPaceStr,
    flatTimeStr, dplusTimeStr, dminusTimeStr, naiSmithTime,
    envFactor:trailEnvFactor, envDeltas:trailEnvDeltas,
    ekm, pctFlat, pctDplus, pctDminus,
  } = trailResult || { estimatedSec:0, estimatedTime:"—", flatPaceSec:420, flatPaceStr:"7:00",
    flatTimeStr:"—", dplusTimeStr:"—", dminusTimeStr:"—", naiSmithTime:"—",
    envFactor:1, envDeltas:[], ekm:0, pctFlat:50, pctDplus:40, pctDminus:10 };

  // Comparaison objectif utilisateur vs estimation
  const trailGapSec  = trailSec > 0 && trailEstSec > 0 ? trailEstSec - trailSec : 0;
  const trailGapStr  = trailGapSec === 0 ? "" :
    (trailGapSec > 0
      ? `+${secToHMS(trailGapSec)} vs objectif`
      : `-${secToHMS(Math.abs(trailGapSec))} vs objectif`);
  const trailGapColor = trailGapSec > 3600 ? T.red : trailGapSec > 0 ? T.orange : T.green;

  const trailSplitData   = trailEstSec>0&&trailSegments.length>0?buildTrailSplits(trailSegments,fcMax,fcRepos,vma):[];
  const trailNutrition   = trailEstSec>0?buildTrailNutrition(trailDistN,trailEstSec,3,trailTemp>=27):[];
  const trailAvgFC       = trailSplitData.length?Math.round(trailSplitData.reduce((s,r)=>s+r.fc,0)/trailSplitData.length):0;
  const totalSegDist     = trailSegments.reduce((s,x)=>s+x.distKm,0);
  const ravitoPassages   = trailEstSec>0&&trailDistN>0?calcRavitoPassages(trailRavitos,trailDistN,trailEstSec,trailStrategy,trailSegments):[];

  // Profil altimétrique data
  // Si un GPX a été importé, utiliser les vrais points pour le profil
  // Sinon, reconstruire depuis les segments (mode manuel)
  const altiData = gpxProfile.length > 0 ? gpxProfile : (()=>{
    let alt=trailAltDep; const pts=[{km:0,alt}];
    trailSegments.forEach(seg=>{
      alt += (seg.dplus||0)-(seg.dminus||0);
      pts.push({km:+(pts[pts.length-1].km+seg.distKm).toFixed(1), alt:Math.round(alt), label:seg.label});
    });
    return pts;
  })();

  const techLabels = {1:"🟢 Facile",2:"🔵 Standard",3:"🟡 Technique",4:"⚠️ Très technique",5:"🔴 Extrême"};
  const humLabels  = {seche:"Sèche",normale:"Normale",humide:"Humide",tres_humide:"Très humide"};
  const expoLabels = {ombragee:"Ombragée",partielle:"Partielle",exposee:"Exposée",tres_exposee:"Très exposée"};
  const hydroLabels= {sec:"Sec",quelques_passages:"Quelques passages",passages_frequents:"Passages fréquents",gues_majeurs:"Guès majeurs"};

  const typeIcon = {montee:"⬆️", descente:"⬇️", plat:"➡️"};
  const typeColor= {montee:T.red, descente:T.blue, plat:T.green};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>

      {/* Mode toggle */}
      <div style={{display:"flex",alignItems:"center",gap:0,background:T.bg,borderRadius:6,padding:3,alignSelf:"flex-start",border:"1px solid #EAE4D8",boxShadow:"inset 0 1px 3px rgba(0,0,0,0.07)"}}>
        {[{id:"route",label:"🛣️ Route",desc:"5km → Marathon"},{id:"trail",label:"⛰️ Trail",desc:"Montagne & technique"},{id:"plan",label:"📅 Plan",desc:"Programme d'entraînement"}].map(m=>(
          <button key={m.id} onClick={()=>setMode(m.id)} style={{
            padding:"8px 20px", border:"none", cursor:"pointer",
            borderRadius:4,
            background:mode===m.id?"#FFFFFF":"transparent",
            color:mode===m.id?"#111":"#999",
            fontWeight:mode===m.id?700:400,
            fontSize:13, fontFamily:"inherit",
            boxShadow:mode===m.id?"0 1px 0 rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.08)":"none",
            transition:"all 0.12s",
          }}>
            {m.label}
            <span style={{fontSize:10,color:mode===m.id?"#C49A3C":"#CCC",marginLeft:6}}>{m.desc}</span>
          </button>
        ))}
      </div>

      {/* ═══ ROUTE MODE ═══ */}
      {mode==="route" && (
        <Card>
          <SectionHead title="🏁 Simulateur de course — Tableau de passage" />
          <p style={{fontSize:13,color:T.textLight,marginBottom:16,lineHeight:1.65}}>
            Choisissez une course planifiée ou configurez manuellement. Le profil de parcours corrige automatiquement l'objectif de temps.
          </p>

          {/* Race selector */}
          {upcomingRaces.length>0&&(
            <div style={{marginBottom:16}}>
              <Label>Charger une course planifiée</Label>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:8}}>
                <div onClick={()=>{setSelectedRaceId("none");}} style={{
                  padding:"7px 14px",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:600,
                  border:`1px solid ${selectedRaceId==="none"?"#C8C8C8":T.border}`,
                  background:selectedRaceId==="none"?T.bg:T.surface,color:T.textLight,
                  boxShadow:"0 1px 0 rgba(0,0,0,0.05)",
                }}>Manuel</div>
                {upcomingRaces.map(race=>(
                  <div key={race.id} onClick={()=>handleSelectRace(race.id)} style={{
                    padding:"7px 14px",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:600,
                    border:`1px solid ${+selectedRaceId===race.id?"#C49A3C":T.border}`,
                    background:+selectedRaceId===race.id?"#FFF7F5":T.surface,
                    color:+selectedRaceId===race.id?"#C49A3C":T.text,
                    boxShadow:"0 1px 0 rgba(0,0,0,0.05)",
                    transition:"all 0.12s",
                  }}>
                    🏁 {race.nom}<span style={{fontSize:10,color:T.textLight,marginLeft:6}}>J-{daysUntil(race.date)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Course profile badges */}
          {selectedRace?.courseProfile&&(
            <div style={{marginBottom:16,padding:"12px 16px",background:"#EDE8DD",border:"1px solid #EAE4D8",borderRadius:6}}>
              <div style={{fontSize:11,fontWeight:700,color:T.textLight,marginBottom:8,letterSpacing:0.5}}>
                📍 {selectedRace.lieu} · {fmtDate(selectedRace.date,{day:"2-digit",month:"long"})}
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {[
                  {icon:"🌡️",label:`${selectedRace.courseProfile.temperature}°C`},
                  {icon:"💨",label:`Vent ${selectedRace.courseProfile.vent}`},
                  {icon:"⛰️",label:`D+ ${selectedRace.courseProfile.denivele_plus}m`},
                  {icon:"🛣️",label:selectedRace.courseProfile.revetement},
                  ...selectedRace.courseProfile.tags.slice(0,3).map(t=>({icon:"🏷️",label:t})),
                ].map((b,i)=>(
                  <span key={i} style={{fontSize:11,padding:"3px 9px",borderRadius:4,background:T.surface,border:"1px solid #EAE4D8",color:T.textMid,fontWeight:600,boxShadow:"0 1px 0 rgba(0,0,0,0.04)"}}>
                    {b.icon} {b.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Setup — distance + objectif */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
            <div>
              <Label>Distance cible</Label>
              <div style={{display:"flex",gap:6,marginTop:6,flexWrap:"wrap"}}>
                {[{v:"5",l:"5 km"},{v:"10",l:"10 km"},{v:"21.097",l:"Semi"},{v:"42.195",l:"Marathon"}].map(o=>(
                  <div key={o.v} onClick={()=>setSimDist(o.v)} style={{
                    padding:"6px 12px",borderRadius:5,cursor:"pointer",fontSize:12,fontWeight:600,
                    border:`1px solid ${simDist===o.v?"#C49A3C":"#EAE4D8"}`,
                    background:simDist===o.v?"#FFF7F5":"#EDE8DD",
                    color:simDist===o.v?"#C49A3C":T.textMid,
                    transition:"all 0.1s",
                  }}>{o.l}</div>
                ))}
                <input
                  type="number" step="0.1" min="1" max="250"
                  value={simDist} onChange={e=>setSimDist(e.target.value)}
                  placeholder="km libre"
                  style={{width:80,padding:"5px 8px",borderRadius:5,border:"1px solid #EAE4D8",
                    fontSize:12,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',color:T.text,background:"#EDE8DD",
                    boxShadow:"inset 0 1px 3px rgba(0,0,0,0.06)"}}
                />
              </div>
            </div>
            <Field label="Ton objectif de temps" value={simTime} onChange={setSimTime} placeholder="1:45:00 ou 2:35:00"/>
          </div>

          {/* Objectif vs Estimation STRATA */}
          {simSec > 0 && simDistN > 0 && (
            <div style={{marginBottom:16,borderRadius:8,overflow:"hidden",border:"1px solid #EAE4D8"}}>
              {/* Header */}
              <div style={{padding:"10px 16px",background:T.bg,borderBottom:"1px solid #EAE4D8",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:12,fontWeight:700,color:T.textMid,letterSpacing:0.5}}>ANALYSE — OBJECTIF VS ESTIMATION</span>
                {strataEst && (
                  <span style={{fontSize:11,padding:"2px 8px",borderRadius:4,background:
                    strataEst.confidence==="haute"?"#DCFCE7":strataEst.confidence==="moyenne"?"#FEF9C3":"#F3F4F6",
                    color:strataEst.confidence==="haute"?T.green:strataEst.confidence==="moyenne"?"#CA8A04":T.textLight,
                    fontWeight:600,border:"1px solid currentColor",opacity:0.8}}>
                    Confiance {strataEst.confidence}
                  </span>
                )}
              </div>

              {/* Deux colonnes : objectif / estimation */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:0}}>
                {/* Objectif utilisateur */}
                <div style={{padding:"16px 20px",borderRight:"1px solid #EAE4D8"}}>
                  <div style={{fontSize:10,color:T.textLight,letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>Ton objectif</div>
                  <div style={{fontSize:28,fontWeight:500,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',color:T.text,lineHeight:1,letterSpacing:"-0.015em"}}>{simTime}</div>
                  <div style={{fontSize:12,color:T.textMid,marginTop:6,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"'}}>{paceFmt(simSec/simDistN)} /km</div>
                </div>

                {/* Estimation STRATA */}
                <div style={{padding:"16px 20px",background:strataEst?"#EDE8DD":"#EDE8DD"}}>
                  <div style={{fontSize:10,color:T.textLight,letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>Estimation STRATA</div>
                  {strataEst ? (
                    <>
                      <div style={{fontSize:28,fontWeight:500,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',color:T.gold,lineHeight:1,letterSpacing:"-0.015em"}}>{strataEst.estTime}</div>
                      <div style={{fontSize:12,color:T.textMid,marginTop:6,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"'}}>{paceFmt(strataEst.estSec/simDistN)} /km</div>
                    </>
                  ) : (
                    <div style={{fontSize:13,color:T.textLight,fontStyle:"italic",paddingTop:4}}>Ajoute des séances pour obtenir une estimation</div>
                  )}
                </div>
              </div>

              {/* Verdict */}
              {strataEst && (
                <div style={{padding:"12px 16px",borderTop:"1px solid #EAE4D8",
                  background:Math.abs(routeGapSec)<60?"#F0FDF4":routeGapSec>0?"#EFF6FF":"#FFF7ED"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                    <div>
                      <div style={{fontWeight:700,fontSize:13,color:routeGapColor,marginBottom:3}}>
                        {Math.abs(routeGapSec)<60 ? "✓" : routeGapSec>0 ? "📊" : "⚠️"} {routeGapStr}
                      </div>
                      <div style={{fontSize:12,color:T.textMid,lineHeight:1.55}}>{routeVerdict}</div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{fontSize:10,color:T.textLight,marginBottom:2}}>{strataEst.fitnessLabel}</div>
                      <div style={{fontSize:10,color:T.textLight,fontStyle:"italic",maxWidth:160,textAlign:"right"}}>{strataEst.source}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Strategy */}
          <div style={{marginBottom:20}}>
            <Label>Stratégie de course</Label>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginTop:8}}>
              {strategies.map(s=>(
                <div key={s.id} onClick={()=>setSimStrategy(s.id)} style={{
                  padding:"11px 14px",borderRadius:6,cursor:"pointer",
                  border:`1px solid ${simStrategy===s.id?"#C49A3C":"#EAE4D8"}`,
                  background:simStrategy===s.id?"#FFF7F5":"#EDE8DD",
                  boxShadow:"0 1px 0 rgba(0,0,0,0.04)",
                  transition:"all 0.12s",
                }}>
                  <div style={{fontSize:16,marginBottom:3}}>{s.icon}</div>
                  <div style={{fontWeight:700,fontSize:12,color:simStrategy===s.id?"#C49A3C":T.text}}>{s.label}</div>
                  <div style={{fontSize:11,color:T.textLight,marginTop:2}}>{s.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {splits.length>0&&(
            <>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}}>
                {[
                  {l:"Temps arrivée",v:finishTime,    c:"#111"},
                  {l:"Allure moy.",  v:`${avgPace}/km`,c:T.blue},
                  {l:"FC moy. est.", v:`${avgFC} bpm`, c:T.orange},
                  {l:"Gels prévus",  v:`${nutritionPlan.filter(n=>n.type==="gel").length}`,c:T.green},
                ].map((s,i)=>(
                  <div key={i} style={{background:T.surfaceSub,borderRadius:8,padding:"12px 14px",textAlign:"center",border:`0.5px solid ${T.border}`}}>
                    <div style={{fontSize:10,color:T.textLight,marginBottom:5,letterSpacing:0.5,textTransform:"uppercase"}}>{s.l}</div>
                    <div style={{fontSize:16,fontWeight:700,color:s.c,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"'}}>{s.v}</div>
                  </div>
                ))}
              </div>

              {/* Sub tabs */}
              <div style={{display:"flex",background:T.bg,borderRadius:6,padding:3,gap:2,marginBottom:16,border:"1px solid #EAE4D8"}}>
                {[{id:"splits",label:"📋 Tableau de passage"},{id:"nutrition",label:"⚡ Nutrition"}].map(t=>(
                  <button key={t.id} onClick={()=>setSimTab(t.id)} style={{
                    flex:1,padding:"7px 10px",borderRadius:4,border:"none",cursor:"pointer",fontFamily:"inherit",
                    background:simTab===t.id?"#FFFFFF":"transparent",
                    color:simTab===t.id?T.text:T.textLight,
                    fontWeight:simTab===t.id?700:400,fontSize:12,
                    boxShadow:simTab===t.id?"0 1px 0 rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.06)":"none",
                    transition:"all 0.12s",
                  }}>{t.label}</button>
                ))}
              </div>

              {simTab==="splits"&&(
                <div>
                  <div style={{display:"grid",gridTemplateColumns:"60px 80px 80px 90px 70px 1fr",gap:6,padding:"4px 12px",marginBottom:4}}>
                    {["Km","Allure","Passage","FC","Zone",""].map(h=><Label key={h}>{h}</Label>)}
                  </div>
                  <div style={{maxHeight:380,overflowY:"auto"}}>
                    {splits.map((s,i)=>(
                      <div key={i} style={{display:"grid",gridTemplateColumns:"60px 80px 80px 90px 70px 1fr",gap:6,padding:"9px 12px",borderRadius:6,marginBottom:2,background:i%2===0?"#EDE8DD":"#FFFFFF",borderLeft:`3px solid ${s.zone.c}`}}>
                        <span style={{fontWeight:700,fontSize:13,color:T.text,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"'}}>{s.km}</span>
                        <span style={{fontSize:12,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',fontWeight:600}}>{s.pace}</span>
                        <span style={{fontSize:12,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',color:T.textMid}}>{s.elapsed}</span>
                        <span style={{fontSize:12,fontWeight:700,color:s.fc>fcMax*0.9?T.red:s.fc>fcMax*0.8?T.orange:T.text,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"'}}>{s.fc}bpm</span>
                        <span style={{fontSize:11,fontWeight:700,color:s.zone.c,background:`${s.zone.c}15`,borderRadius:4,padding:"2px 7px",display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{s.zone.z}</span>
                        <div style={{display:"flex",alignItems:"center"}}>
                          <div style={{flex:1,height:4,background:"#EBEBEB",borderRadius:2}}>
                            <div style={{height:"100%",width:`${Math.min(100,(s.paceNum/(simSec/simDistN))*50)}%`,background:s.zone.c,borderRadius:2}}/>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{marginTop:12,padding:"9px 14px",background:"#EDE8DD",borderRadius:6,fontSize:12,color:T.textMid,border:"1px solid #EBEBEB"}}>
                    💡 Programmez ces temps de passage sur votre Garmin avant le départ.
  
                  </div>
                </div>
              )}

              {simTab==="nutrition"&&(
                <div>
                  <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:400,overflowY:"auto"}}>
                    {nutritionPlan.map((n,i)=>(
                      <div key={i} style={{display:"grid",gridTemplateColumns:"60px 70px 1fr",gap:10,padding:"10px 14px",borderRadius:6,background:i%2===0?"#EDE8DD":"#FFF",border:"1px solid #F0F0F0",alignItems:"start"}}>
                        <span style={{fontSize:20,textAlign:"center"}}>{n.icon}</span>
                        <div><div style={{fontSize:10,color:T.textLight,letterSpacing:0.3}}>{n.time}</div><div style={{fontSize:10,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',color:T.textMid,fontWeight:600}}>km {n.km}</div></div>
                        <div><div style={{fontWeight:700,fontSize:12,color:n.color,marginBottom:2}}>{n.label}</div><div style={{fontSize:11,color:T.textLight,lineHeight:1.55}}>{n.detail}</div></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* ═══ TRAIL MODE ═══ */}
      {mode==="trail"&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>

          {/* Panneau de configuration trail */}
          <Card>
            <SectionHead title="⛰️ Configurateur trail"/>

            {/* ── Presets ── */}
            <div style={{marginBottom:20}}>
              <Label>Démarrer depuis un modèle de course</Label>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:8}}>
                {[
                  { label:"Trail urbain",    icon:"🏙️", dist:15, dplus:300,  dminus:300,  tech:2, temp:18, expo:"partielle",    hydro:"sec",               altDep:100,  altMax:400,  segs:[{label:"Montée douce",distKm:7,dplus:300,dminus:0,type:"montee"},{label:"Retour plat",distKm:8,dplus:0,dminus:300,type:"descente"}] },
                  { label:"Trail montagne", icon:"🏔️", dist:25, dplus:1200, dminus:1200, tech:3, temp:16, expo:"partielle",    hydro:"quelques_passages", altDep:800,  altMax:2000, segs:[{label:"Montée forêt",distKm:10,dplus:800,dminus:0,type:"montee"},{label:"Crête",distKm:5,dplus:400,dminus:200,type:"montee"},{label:"Descente",distKm:10,dplus:0,dminus:1000,type:"descente"}] },
                  { label:"Trail technique", icon:"🪨", dist:27, dplus:2000, dminus:2000, tech:4, temp:22, expo:"exposee",      hydro:"quelques_passages", altDep:800,  altMax:1850, segs:[{label:"Départ vallée",distKm:6,dplus:450,dminus:0,type:"montee"},{label:"Bergeries Col",distKm:5,dplus:600,dminus:0,type:"montee"},{label:"Zone lac",distKm:7,dplus:550,dminus:150,type:"montee"},{label:"Retour descente",distKm:9,dplus:50,dminus:1300,type:"descente"}] },
                  { label:"Ultra trail",     icon:"🦅", dist:60, dplus:4000, dminus:4000, tech:3, temp:20, expo:"tres_exposee", hydro:"passages_frequents", altDep:500,  altMax:2500, segs:[{label:"Montée 1",distKm:15,dplus:1200,dminus:0,type:"montee"},{label:"Traversée",distKm:10,dplus:500,dminus:300,type:"montee"},{label:"Descente 1",distKm:10,dplus:0,dminus:900,type:"descente"},{label:"Montée 2",distKm:10,dplus:1500,dminus:0,type:"montee"},{label:"Descente finale",distKm:15,dplus:800,dminus:2800,type:"descente"}] }
                ].map(p => (
                  <div key={p.label} onClick={() => {
                    setTrailDist(String(p.dist)); setTrailDplus(p.dplus); setTrailDminus(p.dminus);
                    setTrailTech(p.tech); setTrailTemp(p.temp); setTrailExpo(p.expo);
                    setTrailHydro(p.hydro); setTrailAltDep(p.altDep); setTrailAltMax(p.altMax);
                    setTrailSegments(p.segs);
                  }} style={{
                    padding:"8px 14px", borderRadius:6, cursor:"pointer", display:"flex", alignItems:"center", gap:6,
                    border:"1px solid #EAE4D8", background:"#EDE8DD",
                    boxShadow:"0 1px 0 rgba(0,0,0,0.04)", transition:"all 0.12s",
                  }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor="#C49A3C"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="#EAE4D8"}>
                    <span style={{fontSize:16}}>{p.icon}</span>
                    <span style={{fontSize:12,fontWeight:600,color:T.text}}>{p.label}</span>
                    <span style={{fontSize:10,color:T.textLight,marginLeft:2}}>{p.dist}km D+{p.dplus}m</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Paramètres principaux */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:16}}>
              <Field label="Distance (km)" value={trailDist} onChange={setTrailDist} type="number" placeholder="27"/>
              <Field label="Objectif de temps" value={trailTime} onChange={setTrailTime} placeholder="5:30:00"/>
              <Field label="Nombre de ravitaillements" value={String(trailNbRavito)} onChange={v=>setTrailNbRavito(+v)} options={[{v:"2",l:"2 ravitos"},{v:"3",l:"3 ravitos"},{v:"4",l:"4 ravitos"},{v:"5",l:"5 ravitos"},{v:"6",l:"6 ravitos"}]}/>
            </div>

            {/* Technicité — le facteur clé */}
            <div style={{marginBottom:16}}>
              <Label>Technicité du terrain — facteur dominant</Label>
              <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8,marginTop:8}}>
                {[1,2,3,4,5].map(n=>(
                  <div key={n} onClick={()=>setTrailTech(n)} style={{
                    padding:"11px 8px",borderRadius:6,cursor:"pointer",textAlign:"center",
                    border:`1px solid ${trailTech===n?"#C49A3C":"#EAE4D8"}`,
                    background:trailTech===n?"#FFF7F5":"#EDE8DD",
                    boxShadow:"0 1px 0 rgba(0,0,0,0.04)",
                    transition:"all 0.12s",
                  }}>
                    <div style={{fontSize:10,fontWeight:700,color:trailTech===n?"#C49A3C":T.textLight,marginBottom:4}}>T-{n}</div>
                    <div style={{fontSize:11,fontWeight:600,color:trailTech===n?"#111":T.textMid,lineHeight:1.3}}>
                      {["","Piste balisée","Sentier classique","Passages rocheux","Blocs / éboulis","Granite / falaise"][n]}
                    </div>
                    <div style={{fontSize:16,marginTop:4}}>{"🟢🔵🟡🟠🔴"[n-1]}</div>
                  </div>
                ))}
              </div>
              <div style={{marginTop:8,padding:"8px 12px",background:"#FFF7F5",borderRadius:6,border:"1px solid #C49A3C30",fontSize:12,color:T.textMid}}>
                ⚠️ La technicité est le facteur le plus impactant en trail : un terrain T-5 (granite, La Restonica) coûte
                <strong style={{color:"#C49A3C"}}> {trailTechPenalty(trailTech)}% de temps supplémentaire</strong> vs une route plate.
              </div>
            </div>

            {/* Conditions météo */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:16}}>
              <div>
                <Label>Température (°C)</Label>
                <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6}}>
                  <input type="range" min={0} max={45} value={trailTemp} onChange={e=>setTrailTemp(+e.target.value)}
                    style={{flex:1,accentColor:trailTemp>=28?"#DC2626":trailTemp>=22?"#EA580C":"#16A34A"}}/>
                  <span style={{fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',fontWeight:700,fontSize:14,
                    color:trailTemp>=28?T.red:trailTemp>=22?T.orange:T.green,minWidth:36}}>{trailTemp}°C</span>
                </div>
                <div style={{fontSize:10,color:T.textLight,marginTop:4}}>
                  {trailTemp>=32?"🔴 Canicule — critique":trailTemp>=27?"🟠 Chaud — impact majeur":trailTemp>=22?"🟡 Tiède":"🟢 Optimal"}
                </div>
              </div>
              <Field label="Humidité" value={trailHumidite} onChange={setTrailHumidite} options={[
                {v:"seche",l:"Sèche"},{v:"normale",l:"Normale"},{v:"humide",l:"Humide"},{v:"tres_humide",l:"Très humide"}
              ]}/>
              <Field label="Exposition solaire" value={trailExpo} onChange={setTrailExpo} options={[
                {v:"ombragee",l:"Ombragée"},{v:"partielle",l:"Partielle"},{v:"exposee",l:"Exposée"},{v:"tres_exposee",l:"Très exposée"}
              ]}/>
              <Field label="Hydrologie" value={trailHydro} onChange={setTrailHydro} options={[
                {v:"sec",l:"Sec"},{v:"quelques_passages",l:"Quelques passages"},{v:"passages_frequents",l:"Passages fréquents"},{v:"gues_majeurs",l:"Guès majeurs"}
              ]}/>
            </div>

            {/* Altitude et D+ */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:16}}>
              <Field label="Altitude départ (m)" value={String(trailAltDep)} onChange={v=>setTrailAltDep(+v)} type="number" placeholder="800"/>
              <Field label="Altitude max (m)" value={String(trailAltMax)} onChange={v=>setTrailAltMax(+v)} type="number" placeholder="1850"/>
              <Field label="D+ total (m)" value={String(trailDplus)} onChange={v=>setTrailDplus(+v)} type="number" placeholder="2000"/>
              <Field label="D- total (m)" value={String(trailDminus)} onChange={v=>setTrailDminus(+v)} type="number" placeholder="2000"/>
            </div>

            {/* Import GPX */}
            <div style={{marginBottom:16,padding:"12px 16px",background:"#F0F7FF",borderRadius:6,border:"1px solid #BFDBFE",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
              <div>
                <div style={{fontWeight:700,fontSize:13,color:"#1D4ED8",marginBottom:2}}>📁 Importer un fichier GPX</div>
                <div style={{fontSize:12,color:"#3B82F6"}}>
                  Remplissage automatique : distance, D+, D-, altitude, profil.
                  <br/>Depuis Garmin Connect, Strava, Komoot ou toute app GPS.
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                {gpxLoading&&<span style={{fontSize:12,color:"#3B82F6"}}>⏳ Lecture...</span>}
                <label style={{
                  padding:"8px 16px",borderRadius:6,cursor:"pointer",fontWeight:700,fontSize:12,
                  background:"#1D4ED8",color:"#fff",display:"inline-block",
                  boxShadow:"0 1px 0 rgba(0,0,0,0.15)",
                }}>
                  📂 Choisir un fichier .GPX
                  <input type="file" accept=".gpx" style={{display:"none"}}
                    onChange={e=>{if(e.target.files[0]) handleGpxFile(e.target.files[0]);}}/>
                </label>
              </div>
            </div>

            {/* Analyse du parcours — temps absolu */}
            <div style={{borderRadius:6,border:"1px solid #EAE4D8",overflow:"hidden",marginBottom:16}}>
              {/* En-tête */}
              <div style={{padding:"10px 18px",background:T.navy,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:11,fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",color:"#fff"}}>Analyse du parcours</span>
                <span style={{fontSize:10,color:"rgba(255,255,255,0.4)"}}>Basé sur VMA {vma} km/h · allure trail plat {trailFlatPaceStr}/km</span>
              </div>

              {/* Décomposition du temps */}
              <div style={{padding:"16px 18px",borderBottom:"1px solid #EBEBEB"}}>
                <div style={{display:"flex",gap:0,marginBottom:12}}>
                  {/* Temps estimé — valeur principale */}
                  <div style={{flex:"0 0 auto",paddingRight:24,borderRight:"1px solid #EBEBEB",marginRight:24}}>
                    <div style={{fontSize:10,color:T.textLight,letterSpacing:0.5,textTransform:"uppercase",marginBottom:4}}>Temps estimé</div>
                    <div style={{fontSize:28,fontWeight:500,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',color:T.text,lineHeight:1,letterSpacing:"-0.015em"}}>{trailEstTime}</div>
                    <div style={{fontSize:10,color:T.textLight,marginTop:3}}>VMA {vma} + profil parcours</div>
                    {trailResult?.ie && (
                      <div style={{fontSize:11,color:T.textLight,marginTop:6}}>
                        Indice d'Endurance : <strong style={{color:T.gold}}>{trailResult.ie}</strong>
                        {" · "}V_base : <strong style={{color:T.gold}}>{trailResult.vBase} km/h</strong>
                        {" · "}Dérive M_drift : <strong style={{
                          color: trailResult.mDrift < 0.7 ? T.orange : T.green
                        }}>{Math.round((1 - trailResult.mDrift) * 100)}%</strong>
                      </div>
                    )}
                  </div>
                  {/* Objectif utilisateur */}
                  {trailSec>0&&(
                    <div style={{flex:"0 0 auto",paddingRight:24,borderRight:"1px solid #EBEBEB",marginRight:24}}>
                      <div style={{fontSize:10,color:T.textLight,letterSpacing:0.5,textTransform:"uppercase",marginBottom:4}}>Votre objectif</div>
                      <div style={{fontSize:28,fontWeight:700,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',color:T.textMid,lineHeight:1}}>{trailTime}</div>
                      {trailGapStr&&<div style={{fontSize:11,color:trailGapColor,marginTop:3,fontWeight:600}}>{trailGapStr}</div>}
                    </div>
                  )}
                  {/* EKM */}
                  <div style={{flex:"0 0 auto",paddingRight:24,borderRight:"1px solid #EBEBEB",marginRight:24}}>
                    <div style={{fontSize:10,color:T.textLight,letterSpacing:0.5,textTransform:"uppercase",marginBottom:4}}>Équivalent km</div>
                    <div style={{fontSize:28,fontWeight:500,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',color:T.gold,lineHeight:1,letterSpacing:"-0.015em"}}>{ekm}</div>
                    <div style={{fontSize:10,color:T.textLight,marginTop:3}}>dist + D+/100</div>
                  </div>
                  {/* Corrections env */}
                  <div style={{flex:"0 0 auto"}}>
                    <div style={{fontSize:10,color:T.textLight,letterSpacing:0.5,textTransform:"uppercase",marginBottom:4}}>Conditions (+env)</div>
                    <div style={{fontSize:28,fontWeight:700,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',color:trailEnvFactor>1.10?T.red:trailEnvFactor>1.05?T.orange:"#CA8A04",lineHeight:1}}>
                      +{Math.round((trailEnvFactor-1)*100)}%
                    </div>
                    <div style={{fontSize:10,color:T.textLight,marginTop:3}}>sur Naismith base</div>
                  </div>
                </div>

                {/* Barre de décomposition du temps */}
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:10,color:T.textLight,letterSpacing:0.5,marginBottom:6}}>Décomposition du temps estimé</div>
                  <div style={{display:"flex",height:22,borderRadius:4,overflow:"hidden",gap:1}}>
                    <div style={{width:`${pctFlat}%`,background:T.blue,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {pctFlat>10&&<span style={{fontSize:10,fontWeight:700,color:"#fff"}}>{pctFlat}%</span>}
                    </div>
                    <div style={{width:`${pctDplus}%`,background:T.red,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {pctDplus>10&&<span style={{fontSize:10,fontWeight:700,color:"#fff"}}>{pctDplus}%</span>}
                    </div>
                    {pctDminus>0&&<div style={{width:`${pctDminus}%`,background:"#8B5CF6",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {pctDminus>8&&<span style={{fontSize:10,fontWeight:700,color:"#fff"}}>{pctDminus}%</span>}
                    </div>}
                  </div>
                  <div style={{display:"flex",gap:16,marginTop:6}}>
                    {[[T.blue,"Plat",flatTimeStr],[T.red,"D+ montée",dplusTimeStr],...(pctDminus>0?[["#8B5CF6","D- descente",dminusTimeStr]]:[])].map(([c,l,v])=>(
                      <div key={l} style={{display:"flex",alignItems:"center",gap:5}}>
                        <div style={{width:8,height:8,borderRadius:1,background:c}}/>
                        <span style={{fontSize:10,color:T.textLight}}>{l}</span>
                        <span style={{fontSize:10,fontWeight:700,color:"#333",fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"'}}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Facteurs environnementaux */}
              <div style={{padding:"12px 18px",background:"#EDE8DD"}}>
                <div style={{fontSize:10,color:T.textLight,letterSpacing:0.5,textTransform:"uppercase",marginBottom:8}}>Facteurs conditions ({Math.round((trailEnvFactor-1)*100)}% total additif)</div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {trailEnvDeltas.map(e=>(
                    <div key={e.key} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",background:"#FFFFFF",borderRadius:4,border:`1px solid ${e.delta>0.04?T.orange+"44":T.border}`}}>
                      <span style={{fontSize:13}}>{e.icon}</span>
                      <span style={{fontSize:11,color:T.textMid}}>{e.label}</span>
                      <span style={{fontSize:11,fontWeight:700,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',color:e.delta>0.08?T.red:e.delta>0.03?T.orange:"#CA8A04"}}>
                        {e.delta===0?"neutre":`+${Math.round(e.delta*100)}%`}
                      </span>
                    </div>
                  ))}
                </div>
                <div style={{marginTop:10,fontSize:11,color:T.textMid,lineHeight:1.6,borderTop:"1px solid #EBEBEB",paddingTop:10}}>
                  💡 <strong>Méthode :</strong> Temps plat (VMA) + D+ calibré par technicité (T-{trailTech} = {trailResult?.climbCoeff||"—"}s/m D+) × facteurs env. additifs.
                  Validé sur données réelles : La Restonica 27km/D+2000m → ≈6–7h bon amateur.
                </div>
              </div>
            </div>
          </Card>

          {/* ── Plan de course complet ── */}
          {trailEstSec>0&&(
            <Card>
              {/* Header avec infos course + bouton save */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,flexWrap:"wrap",gap:12}}>
                <div>
                  <SectionHead title="📋 Plan de course"/>
                  <p style={{fontSize:12,color:T.textLight,marginTop:4}}>
                    Stratégie · Ravitaillements · Nutrition · Profil altimétrique
                  </p>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                  {savedOk&&<span style={{fontSize:12,color:T.green,fontWeight:600}}>✓ Enregistré dans Planning</span>}
                  <Btn variant="lime" onClick={()=>{
                    if (!trailDistN) return;
                    const planData = {
                      nom: trailNom || `Trail ${trailDistN}km`,
                      date: trailDate || new Date(Date.now()+7*86400000).toISOString().split("T")[0],
                      lieu: trailLieu || "",
                      distance: trailDistN,
                      objectif: trailTime || trailEstTime,
                      statut: "inscrit",
                      splits: [], nutrition: [], tapering: [],
                      courseProfile: {
                        surface:"trail", profil:"montagne",
                        denivele_plus: trailDplus, denivele_minus: trailDminus,
                        temperature: trailTemp, vent: trailVent,
                        orientation_vent: trailOrient, humidite: trailHumidite,
                        virages:"larges", revetement:"terre",
                        altitude: trailAltDep,
                        tags: [`T${trailTech}`, `D+${trailDplus}m`],
                        note: `Estimation STRATA : ${trailEstTime}. Segments : ${trailSegments.length}. Ravitos : ${trailRavitos.length}.`,
                      },
                      trailData: { tech:trailTech, altMax:trailAltMax, segments:trailSegments, ravitos:trailRavitos, strategy:trailStrategy },
                    };
                    const sourceId = ps("_sourceRaceId", null);
                    const sourceRace = sourceId ? races.find(r => r.id === sourceId) : null;
                    if (sourceRace && onUpdateRace) {
                      // Update the existing race rather than creating a duplicate
                      onUpdateRace({ ...sourceRace, ...planData, id: sourceRace.id });
                      setPredState(s => ({ ...s, _sourceRaceId: null }));
                    } else {
                      addRace({ id: Date.now(), ...planData });
                    }
                    setSavedOk(true);
                    setTimeout(()=>setSavedOk(false), 3000);
                  }}>💾 {ps("_sourceRaceId", null) ? "Mettre à jour la course" : "Enregistrer dans Planning"}</Btn>
                </div>
              </div>

              {/* Infos course optionnelles */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
                <Field label="Nom de la course (optionnel)" value={trailNom} onChange={setTrailNom} placeholder={`Trail ${trailDistN}km`}/>
                <Field label="Date (optionnel)" value={trailDate} onChange={setTrailDate} type="date"/>
                <Field label="Lieu (optionnel)" value={trailLieu} onChange={setTrailLieu} placeholder="ex: Chamonix"/>
              </div>

              {/* KPIs */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8,marginBottom:16}}>
                {[
                  {l:"Temps estimé", v:trailEstTime, c:"#111"},
                  {l:"Objectif",     v:trailSec>0?trailTime:"—", c:trailGapSec<0?T.orange:T.green},
                  {l:"EKM",         v:`${ekm} km`, c:"#C49A3C"},
                  {l:"Allure moy.", v:trailEstSec>0?`${paceFmt(trailEstSec/trailDistN)}/km`:"—", c:T.blue},
                  {l:"Ravitos",     v:trailRavitos.length, c:T.purple},
                ].map((s,i)=>(
                  <div key={i} style={{background:"#EDE8DD",borderRadius:6,padding:"10px 12px",textAlign:"center",border:"1px solid #E8E8E8"}}>
                    <div style={{fontSize:9,color:T.textLight,marginBottom:4,letterSpacing:0.5,textTransform:"uppercase"}}>{s.l}</div>
                    <div style={{fontSize:15,fontWeight:700,color:s.c,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"'}}>{s.v}</div>
                  </div>
                ))}
              </div>

              {/* Stratégie */}
              <div style={{marginBottom:16}}>
                <Label>Stratégie de course</Label>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginTop:8}}>
                  {[
                    {id:"conservative", icon:"🐢", label:"Prudent", desc:"Départ lent +8%, fin plus rapide. Évite le mur.", color:T.blue},
                    {id:"even",         icon:"➖", label:"Régulier", desc:"Effort constant du début à la fin.", color:T.green},
                    {id:"aggressive",   icon:"🚀", label:"Ambitieux", desc:"Départ fort -5%, gérer la fatigue finale.", color:T.orange},
                  ].map(s=>(
                    <div key={s.id} onClick={()=>setTrailStrategy(s.id)} style={{
                      padding:"10px 12px",borderRadius:6,cursor:"pointer",
                      border:`1px solid ${trailStrategy===s.id?s.color:"#EAE4D8"}`,
                      background:trailStrategy===s.id?`${s.color}0D`:"#EDE8DD",
                      transition:"all 0.12s",
                    }}>
                      <div style={{fontSize:18,marginBottom:4}}>{s.icon}</div>
                      <div style={{fontWeight:700,fontSize:12,color:trailStrategy===s.id?s.color:T.text}}>{s.label}</div>
                      <div style={{fontSize:10,color:T.textLight,marginTop:2,lineHeight:1.4}}>{s.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ravitos */}
              <div style={{marginBottom:16}}>
                <Label>Ravitaillements — placez-les au kilomètre</Label>
                <div style={{display:"flex",gap:8,alignItems:"center",marginTop:8,flexWrap:"wrap"}}>
                  <>
                    <input type="number" step="0.5" min="0.5" max={trailDistN-0.5}
                      value={rvKmInput} onChange={e=>setRvKmInput(e.target.value)}
                      placeholder="km du ravito" style={{
                        width:120,padding:"6px 10px",borderRadius:5,border:"1px solid #EAE4D8",
                        fontSize:13,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',
                        boxShadow:"inset 0 1px 3px rgba(0,0,0,0.06)"
                      }}/>
                    <Btn variant="ghost" size="sm" onClick={()=>{addRavito(rvKmInput);setRvKmInput("");}}>
                      + Ajouter ravito
                    </Btn>
                    {trailRavitos.length>0&&<Btn variant="ghost" size="sm" onClick={()=>setTrailRavitos([])}>Tout effacer</Btn>}
                  </>
                </div>
                {trailRavitos.length>0&&(
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:8}}>
                    {trailRavitos.map(r=>(
                      <div key={r.km} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:20,background:"#F0FDF4",border:"0.5px solid #16A34A44",fontSize:12,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"'}}>
                        <span style={{fontWeight:500,color:T.green,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"'}}>🏁 km {r.km}</span>
                        <button onClick={()=>removeRavito(r.km)} style={{background:"none",border:"none",cursor:"pointer",color:T.textLight,fontSize:13,lineHeight:1,padding:"0 2px"}}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>



              {/* ── PLAN DE PASSAGE — timeline fusionnée segments + ravitos + nutrition ── */}
              {trailTab==="plan"&&(()=>{
                // Construire la timeline complète
                const refSec = trailEstSec;
                const stratMultiplier = (ratio) => {
                  if (trailStrategy==="conservative") return ratio<0.5 ? ratio*1.08 : 0.54+(ratio-0.5)*0.92;
                  if (trailStrategy==="aggressive")   return ratio<0.5 ? ratio*0.95 : 0.475+(ratio-0.5)*1.05;
                  return ratio;
                };
                const timeAtKm = (km) => {
                  const ratio = Math.min(km/trailDistN, 1);
                  const adjRatio = stratMultiplier(ratio);
                  return Math.round(refSec * Math.min(adjRatio, 1));
                };

                // Nutrition à chaque ravito selon temps écoulé et météo
                const ravitoNutrition = (km, elapsedSec) => {
                  const elapsedMin = elapsedSec/60;
                  const items = [];
                  items.push({ icon:"💧", text: trailTemp>=27?"Eau + sel obligatoire":"Eau + électrolytes" });
                  if (elapsedMin > 30) items.push({ icon:"⚡", text:"Gel ou barre énergétique" });
                  if (trailTemp>=27)   items.push({ icon:"🧂", text:"Capsule de sel" });
                  if (elapsedMin > 90) items.push({ icon:"🍊", text:"Solide (fruit, banane)" });
                  return items;
                };

                // Points timeline : départ + segments + ravitos + arrivée
                const timeline = [];
                // Départ
                timeline.push({type:"depart",km:0,label:"Départ",elapsed:"0:00",elapsedSec:0,color:T.text});
                // Segments (fin de chaque segment)
                let cumKm=0;
                trailSplitData.forEach((seg,i)=>{
                  cumKm+=seg.distKm;
                  timeline.push({type:"segment",km:+cumKm.toFixed(1),label:seg.label,elapsed:secToHMS(timeAtKm(cumKm)),elapsedSec:timeAtKm(cumKm),paceLabel:seg.paceLabel,color:typeColor[seg.type],segType:seg.type});
                });
                // Ravitos
                ravitoPassages.forEach(r=>{
                  const sec=timeAtKm(r.km);
                  timeline.push({type:"ravito",km:r.km,label:`Ravito km ${r.km}`,elapsed:secToHMS(sec),elapsedSec:sec,nutrition:ravitoNutrition(r.km,sec),color:T.green});
                });
                // Arrivée
                timeline.push({type:"arrivee",km:trailDistN,label:"Arrivée",elapsed:secToHMS(refSec),elapsedSec:refSec,color:"#C49A3C"});
                // Trier par km
                timeline.sort((a,b)=>a.km-b.km);

                return (
                  <div>
                    {trailRavitos.length===0&&(
                      <div style={{padding:"10px 14px",background:"#FFFBEB",borderRadius:6,border:"1px solid #FDE68A",fontSize:12,color:"#92400E",marginBottom:14}}>
                        💡 Ajoutez des ravitaillements ci-dessus pour voir les temps de passage et le plan nutrition.
                      </div>
                    )}
                    <div style={{display:"flex",flexDirection:"column",gap:0,position:"relative"}}>
                      {/* Ligne verticale timeline */}
                      <div style={{position:"absolute",left:19,top:20,bottom:20,width:2,background:"#EAE4D8",zIndex:0}}/>
                      {timeline.map((pt,i)=>(
                        <div key={i} style={{display:"flex",gap:14,padding:"10px 0",position:"relative",zIndex:1}}>
                          {/* Dot */}
                          <div style={{
                            width:14,height:14,borderRadius:"50%",flexShrink:0,marginTop:3,
                            background:pt.type==="ravito"?T.green:pt.type==="arrivee"?"#C49A3C":pt.type==="depart"?"#111":pt.color||T.border,
                            border:"2px solid #fff",boxShadow:"0 0 0 1px "+( pt.type==="ravito"?T.green:pt.type==="arrivee"?"#C49A3C":"#EAE4D8"),
                            marginLeft:13,
                          }}/>
                          {/* Contenu */}
                          <div style={{flex:1,paddingBottom:i<timeline.length-1?8:0}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                              <div>
                                <span style={{fontWeight:700,fontSize:13,color:pt.type==="ravito"?T.green:pt.type==="arrivee"?"#C49A3C":T.text}}>
                                  {pt.type==="ravito"?"🏁 ":pt.type==="arrivee"?"🏅 ":pt.type==="depart"?"🚀 ":
                                   pt.segType==="montee"?"⬆️ ":pt.segType==="descente"?"⬇️ ":"➡️ "}
                                  {pt.label}
                                </span>
                                {pt.paceLabel&&<span style={{fontSize:11,color:T.textLight,marginLeft:8}}>{pt.paceLabel}</span>}
                              </div>
                              <div style={{textAlign:"right",flexShrink:0}}>
                                <div style={{fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',fontWeight:700,fontSize:14,color:pt.type==="arrivee"?"#C49A3C":T.text}}>{pt.elapsed}</div>
                                <div style={{fontSize:10,color:T.textLight}}>km {pt.km}</div>
                              </div>
                            </div>
                            {/* Nutrition au ravito */}
                            {pt.type==="ravito"&&pt.nutrition&&(
                              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:6}}>
                                {pt.nutrition.map((n,j)=>(
                                  <span key={j} style={{fontSize:11,padding:"2px 8px",borderRadius:4,background:"#F0FDF4",border:"1px solid #16A34A33",color:"#166534"}}>
                                    {n.icon} {n.text}
                                  </span>
                                ))}
                                {trailTemp>=27&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:4,background:"#FFF7ED",border:"1px solid #EA580C33",color:"#9A3412"}}>🌡️ Refroidir nuque/poignets</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Légende stratégie */}
                    <div style={{marginTop:12,padding:"10px 14px",background:"#EDE8DD",borderRadius:6,border:"1px solid #EBEBEB",fontSize:12,color:T.textMid}}>
                      <strong>Stratégie {["🐢 Prudent","➖ Régulier","🚀 Ambitieux"][["conservative","even","aggressive"].indexOf(trailStrategy)]} :</strong>
                      {trailStrategy==="conservative"&&" Partez 8% plus lentement sur la 1ère moitié. Gardez de l'énergie pour accélérer en fin de course."}
                      {trailStrategy==="even"&&" Effort constant tout au long de la course. Le plus fiable sur trail technique."}
                      {trailStrategy==="aggressive"&&" Profitez de la fraîcheur du départ pour avancer. Gérez bien la fatigue après le mi-parcours."}
                    </div>
                  </div>
                );
              })()}

              {/* ── PROFIL ALTIMÉTRIQUE ── */}
              {/* Sub-tabs : seulement 2 onglets */}
              <div style={{display:"flex",background:T.bg,borderRadius:6,padding:3,gap:2,marginBottom:16,border:"1px solid #EAE4D8"}}>
                {[
                  {id:"plan",     label:"📋 Plan de passage"},
                  {id:"parcours", label:"📈 Profil & Segments"},
                ].map(t=>(
                  <button key={t.id} onClick={()=>setTrailTab(t.id)} style={{
                    flex:1,padding:"7px 8px",borderRadius:4,border:"none",cursor:"pointer",fontFamily:"inherit",
                    background:trailTab===t.id?"#FFFFFF":"transparent",
                    color:trailTab===t.id?T.text:T.textLight,
                    fontWeight:trailTab===t.id?700:400,fontSize:11,
                    boxShadow:trailTab===t.id?"0 1px 0 rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.06)":"none",
                    transition:"all 0.12s",
                  }}>{t.label}</button>
                ))}
              </div>

              {/* ── PROFIL & SEGMENTS fusionnés ── */}
              {(trailTab==="parcours"||trailTab==="alti"||trailTab==="segments")&&(
                <div>
                  {(()=>{
                    // Calculer la position km de début/fin de chaque segment
                    let segKmCursor = 0;
                    const segZones = trailSplitData.map((seg,i) => {
                      const start = +segKmCursor.toFixed(2);
                      segKmCursor += seg.distKm;
                      const end   = +segKmCursor.toFixed(2);
                      return { ...seg, startKm: start, endKm: end, idx: i };
                    });
                    // Couleurs par type de segment
                    const segColors = {
                      montee:   { stroke:"#DC2626", fill:"#DC262618" },  // rouge montée
                      descente: { stroke:"#16A34A", fill:"#16A34A18" },  // vert descente
                      plat:     { stroke:"#2563EB", fill:"#2563EB18" },  // bleu plat
                    };

                    // Enrichir altiData avec une clé par segment (null hors segment)
                    // Chaque point aura : alt_0, alt_1, alt_2... selon quel segment il appartient
                    const coloredData = altiData.map(pt => {
                      const row = { km: pt.km, alt: pt.alt };
                      segZones.forEach((seg, i) => {
                        // On inclut les points à la jonction pour que les segments se rejoignent
                        row[`seg_${i}`] = (pt.km >= seg.startKm - 0.05 && pt.km <= seg.endKm + 0.05)
                          ? pt.alt : null;
                      });
                      return row;
                    });

                    return (
                      <div>
                        {/* Légende */}
                        <div style={{display:"flex",gap:16,marginBottom:10,flexWrap:"wrap"}}>
                          {[
                            {type:"montee",  label:"Montée",   icon:"⬆️"},
                            {type:"plat",    label:"Plat",     icon:"➡️"},
                            {type:"descente",label:"Descente", icon:"⬇️"},
                          ].map(({type,label,icon})=>(
                            <div key={type} style={{display:"flex",alignItems:"center",gap:5,fontSize:11}}>
                              <div style={{width:20,height:3,borderRadius:2,background:segColors[type]?.stroke||"#ccc"}}/>
                              <span style={{color:T.textMid}}>{icon} {label}</span>
                            </div>
                          ))}
                          {ravitoPassages.length>0&&(
                            <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11}}>
                              <div style={{width:2,height:12,borderRadius:1,background:T.green,borderLeft:"2px dashed "+T.green}}/>
                              <span style={{color:T.textMid}}>🏁 Ravito</span>
                            </div>
                          )}
                        </div>

                        {/* Graphique profil coloré par segment */}
                        <div style={{position:"relative"}}>
                          <ResponsiveContainer width="100%" height={240}>
                            <ComposedChart data={coloredData}
                              onMouseMove={(e)=>{
                                if (!e?.activePayload?.[0]) return;
                                const km = e.activePayload[0].payload?.km;
                                const seg = segZones.find(s=>km>=s.startKm&&km<=s.endKm);
                                setHovSeg(seg?.idx ?? null);
                              }}
                              onMouseLeave={()=>setHovSeg(null)}>
                              <defs>
                                {segZones.map((seg,i)=>{
                                  const col = segColors[seg.type] || segColors.plat;
                                  return (
                                    <linearGradient key={i} id={`seg-grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="5%"  stopColor={col.stroke} stopOpacity={hovSeg===i?0.35:0.18}/>
                                      <stop offset="95%" stopColor={col.stroke} stopOpacity={0.02}/>
                                    </linearGradient>
                                  );
                                })}
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#EBEBEB"/>
                              <XAxis dataKey="km" type="number" domain={["dataMin","dataMax"]}
                                tick={{fill:T.textLight,fontSize:10}} axisLine={false} tickLine={false}
                                tickFormatter={v=>v.toFixed(0)}
                                label={{value:"km",position:"insideBottom",offset:-6,fill:T.textLight,fontSize:10}}/>
                              <YAxis tick={{fill:T.textLight,fontSize:10}} axisLine={false} tickLine={false}
                                label={{value:"alt. (m)",angle:-90,position:"insideLeft",fill:T.textLight,fontSize:10}}/>
                              <Tooltip content={({active,payload})=>{
                                if(!active||!payload?.length) return null;
                                const d = payload[0]?.payload;
                                const seg = segZones.find(s=>d?.km>=s.startKm&&d?.km<=s.endKm);
                                const col = seg ? (segColors[seg.type]?.stroke||"#aaa") : "#C49A3C";
                                return (
                                  <div style={{background:T.navy,borderRadius:6,padding:"10px 14px",fontSize:11,color:"#fff",minWidth:150}}>
                                    <div style={{color:"#C49A3C",fontWeight:700,marginBottom:4}}>km {d?.km?.toFixed(1)}</div>
                                    <div>Altitude : <strong>{d?.alt}m</strong></div>
                                    {seg&&(
                                      <div style={{marginTop:6,paddingTop:6,borderTop:"1px solid rgba(255,255,255,0.15)"}}>
                                        <div style={{color:col,fontWeight:600}}>
                                          {typeIcon[seg.type]} {seg.label}
                                        </div>
                                        <div style={{color:"#aaa",marginTop:2,fontSize:10}}>
                                          {seg.paceLabel} · arrivée {seg.elapsed}
                                        </div>
                                        <div style={{color:"#aaa",fontSize:10}}>
                                          {seg.distKm}km · D+{seg.dplus||0}m D-{seg.dminus||0}m
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              }}/>
                              {/* Un Area par segment, coloré selon son type */}
                              {segZones.map((seg,i)=>{
                                const col = segColors[seg.type] || segColors.plat;
                                return (
                                  <Area key={i} type="monotone" dataKey={`seg_${i}`}
                                    name={seg.label}
                                    stroke={col.stroke}
                                    fill={`url(#seg-grad-${i})`}
                                    strokeWidth={hovSeg===i ? 3 : 2}
                                    dot={false}
                                    connectNulls={false}
                                    isAnimationActive={false}
                                  />
                                );
                              })}
                              {/* Ravitos */}
                              {ravitoPassages.map(r=>(
                                <ReferenceLine key={"rav-"+r.km} x={r.km} stroke={T.green} strokeDasharray="4 2"
                                  label={{value:"🏁",fill:T.green,fontSize:10,position:"top"}}/>
                              ))}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Stats D+ */}
                        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginTop:10,marginBottom:14}}>
                          {[
                            {l:"D+ total",v:`${trailDplus}m`,c:T.red},
                            {l:"D- total",v:`${trailDminus}m`,c:T.blue},
                            {l:"Alt. départ",v:`${trailAltDep}m`,c:T.textMid},
                            {l:"Alt. max",v:`${trailAltMax}m`,c:T.orange},
                          ].map((s,i)=>(
                            <div key={i} style={{background:T.surfaceSub,borderRadius:8,padding:"8px 12px",border:`0.5px solid ${T.border}`}}>
                              <div style={{fontSize:9,color:T.textLight,textTransform:"uppercase",letterSpacing:0.5,marginBottom:3}}>{s.l}</div>
                              <div style={{fontSize:14,fontWeight:700,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',color:s.c}}>{s.v}</div>
                            </div>
                          ))}
                        </div>

                        {/* Tableau des segments — interactif avec le graphique */}
                        {trailSplitData.length>0&&(
                          <div>
                            <div style={{fontSize:11,fontWeight:700,color:T.textLight,letterSpacing:0.5,textTransform:"uppercase",marginBottom:8}}>
                              Segments — survolez pour voir sur le profil
                            </div>
                            <div style={{display:"flex",flexDirection:"column",gap:3}}>
                              {trailSplitData.map((s,i)=>(
                                <div key={i}
                                  onMouseEnter={()=>setHovSeg(i)}
                                  onMouseLeave={()=>setHovSeg(null)}
                                  style={{
                                    display:"grid",gridTemplateColumns:"16px 1fr 60px 60px 55px 80px 75px 28px",
                                    gap:8,padding:"9px 12px",borderRadius:6,alignItems:"center",
                                    background:hovSeg===i?`${typeColor[s.type]}10`:(i%2===0?"#EDE8DD":"#FFF"),
                                    border:`1px solid ${hovSeg===i?typeColor[s.type]:"#F0F0F0"}`,
                                    borderLeft:`3px solid ${typeColor[s.type]}`,
                                    cursor:"default",transition:"all 0.1s",
                                  }}>
                                  <span style={{fontSize:13}}>{typeIcon[s.type]}</span>
                                  <span style={{fontSize:12,fontWeight:600,color:hovSeg===i?typeColor[s.type]:T.text}}>{s.label}</span>
                                  <span style={{fontSize:11,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',color:T.textMid}}>{s.distKm}km</span>
                                  <span style={{fontSize:11,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',color:T.red}}>+{s.dplus||0}m</span>
                                  <span style={{fontSize:11,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',color:T.blue}}>-{s.dminus||0}m</span>
                                  <span style={{fontSize:11,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',color:T.textMid,fontWeight:600}}>{s.paceLabel}</span>
                                  <span style={{fontSize:11,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',fontWeight:700,color:hovSeg===i?typeColor[s.type]:T.text}}>{s.elapsed}</span>
                                  <button onClick={()=>setTrailSegments(prev=>prev.filter((_,j)=>j!==i))}
                                    style={{background:"none",border:"none",cursor:"pointer",color:T.textLight,fontSize:12}}>✕</button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Ajout segment manuel */}
                        <div style={{padding:"10px 14px",background:"#EDE8DD",borderRadius:6,border:"1px dashed #D8D8D8",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginTop:12}}>
                          <span style={{fontSize:12,color:T.textLight,fontWeight:600}}>+ Segment :</span>
                          {[{t:"montee",l:"Montée"},{t:"descente",l:"Descente"},{t:"plat",l:"Plat"}].map(({t,l})=>(
                            <Btn key={t} variant="ghost" size="sm" onClick={()=>setTrailSegments(prev=>[...prev,{
                              label:l, distKm:3,
                              dplus:t==="montee"?300:t==="descente"?0:30,
                              dminus:t==="descente"?300:t==="montee"?0:30,
                              type:t,
                            }])}>{typeIcon[t]} {l}</Btn>
                          ))}
                          {trailSegments.length>0&&(
                            <Btn variant="ghost" size="sm" onClick={()=>setTrailSegments([])}>Tout effacer</Btn>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </Card>
          )}
        </div>
      )}

      {/* ── Riegel + Zones : uniquement en mode route ── */}
      {mode==="route"&&<>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
        <Card>
          <SectionHead title={<>🎯 Prédiction de performances (<InfoTip term="Riegel">Riegel</InfoTip>)</>}/>
          <p style={{color:T.textLight,fontSize:13,marginBottom:16,lineHeight:1.6}}>
            Performance récente → extrapolation sur toutes distances route.
          </p>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
            <Field label="Distance de référence" value={refDist} onChange={setRefDist} options={[
              {v:"5",l:"5 km"},{v:"10",l:"10 km"},{v:"21.097",l:"Semi-marathon"},{v:"42.195",l:"Marathon"},
            ]}/>
            <Field label="Votre temps" value={refTime} onChange={setRefTime} placeholder="48:00 ou 1:45:00"/>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {preds.map(p=>(
              <div key={p.label} onClick={()=>{ setMode("route"); setSimDist(String(p.dist)); setSimTime(riegelPredict(refDistN,refSec,p.dist)); setSelectedRaceId("none"); }}
                style={{display:"flex",alignItems:"center",gap:12,background:"#EDE8DD",border:"1px solid #EBEBEB",borderRadius:6,padding:"11px 14px",cursor:"pointer",transition:"all 0.12s"}}
                onMouseEnter={e=>e.currentTarget.style.borderColor="#C49A3C"}
                onMouseLeave={e=>e.currentTarget.style.borderColor="#EBEBEB"}>
                <span style={{fontSize:20}}>{p.icon}</span>
                <span style={{color:T.textMid,fontSize:13,flex:1}}>{p.label}</span>
                <span style={{fontSize:18,fontWeight:700,color:T.text,fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"'}}>{p.time}</span>
                <Pill color={T.blue}>{p.allure} /km</Pill>
              </div>
            ))}
          </div>
          {preds.length>0&&<div style={{marginTop:10,fontSize:11,color:T.textLight}}>💡 Cliquez → charge dans le simulateur route.</div>}
        </Card>

        <Card>
          <SectionHead title={<>⚡ <InfoTip term="Allure">Allures</InfoTip> par <InfoTip term="Zones FC">zone</InfoTip></>}/>
          {vma?(
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {zoneAllures.map(z=>(
                <div key={z.zone} style={{background:z.bg,border:`1px solid ${z.color}33`,borderRadius:6,padding:"11px 14px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontWeight:700,fontSize:12,color:z.color}}>{z.zone}</div>
                      <div style={{fontSize:10,color:T.textLight}}>{z.pct}</div>
                    </div>
                    <div style={{fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"',fontSize:14,fontWeight:700,color:T.text}}>{z.a} /km</div>
                  </div>
                </div>
              ))}
            </div>
          ):<EmptyState icon="⚡" title="VMA non renseignée" desc="Ajoutez votre VMA dans Profil"/>}
        </Card>
      </div>

      {/* Progression allures */}
      <Card>
        <SectionHead title="📈 Progression des allures en entraînement"/>
        {runs.length>1?(
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={[...runs].filter(r=>r.duree&&r.distance).map(r=>({date:fmtDate(r.date),allure_sec:Math.round(r.duree*60/r.distance)}))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EBEBEB"/>
              <XAxis dataKey="date" tick={{fill:T.textLight,fontSize:10}} axisLine={false} tickLine={false}/>
              <YAxis reversed tick={{fill:T.textLight,fontSize:10}} axisLine={false} tickLine={false} tickFormatter={v=>`${Math.floor(v/60)}:${String(v%60).padStart(2,"0")}`}/>
              <Tooltip content={<CustomTooltip/>}/>
              <Line type="monotone" dataKey="allure_sec" name="Allure (s/km)" stroke="#111" strokeWidth={2} dot={{fill:"#C49A3C",r:4}}/>
            </LineChart>
          </ResponsiveContainer>
        ):<EmptyState icon="📈" title="Pas assez de données" desc="Enregistrez plus de séances"/>}
      </Card>
      </>}

      {/* ═══ PLAN MODE ═══ */}
      {mode==="plan" && <TrainingPlanTab profile={profile} runs={runs} races={races} />}

    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   TAB: PROFIL
═══════════════════════════════════════════════════════════════════════════ */
function TabProfile({ profile, setProfile, stravaConnected, stravaStatus, stravaMsg, onStravaSync }) {
  const set = (k) => (v) => setProfile(p => ({ ...p, [k]: isNaN(+v) || v === "" ? v : +v }));
  const imc = (profile.poids / Math.pow(profile.taille / 100, 2)).toFixed(1);
  const imcLabel = imc < 18.5 ? ["Insuffisance pondérale", T.blue] : imc < 25 ? ["Poids normal ✓", T.green] : imc < 30 ? ["Surpoids", T.orange] : ["Obésité", T.red];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Card>
          <SectionHead title="👤 Identité" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Prénom" value={profile.prenom} onChange={set("prenom")} />
            <Field label="Nom" value={profile.nom} onChange={set("nom")} />
            <Field label="Sexe" value={profile.sexe} onChange={set("sexe")} options={[{ v: "homme", l: "Homme" }, { v: "femme", l: "Femme" }, { v: "autre", l: "Autre" }]} />
            <Field label="Âge" value={profile.age} onChange={set("age")} type="number" unit="ans" />
          </div>
        </Card>

        <Card>
          <SectionHead title="📏 Morphologie" />
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <SliderField label="Taille" value={profile.taille} onChange={set("taille")} min={140} max={220} unit=" cm" color={T.blue} />
            <SliderField label="Poids" value={profile.poids} onChange={set("poids")} min={40} max={150} unit=" kg" color={T.navy} />
            <SliderField label="Masse graisseuse" value={profile.masseGraisseuse} onChange={set("masseGraisseuse")} min={5} max={45} unit=" %" color={T.orange} />
            <div style={{ background: T.bg, borderRadius: 12, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 12, color: T.textLight }}><InfoTip term="IMC">IMC</InfoTip> calculé</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: imcLabel[1], fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{imc}</div>
              </div>
              <Pill color={imcLabel[1]}>{imcLabel[0]}</Pill>
            </div>
          </div>
        </Card>

        <Card>
          <SectionHead title="💼 Mode de vie" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Type de travail" value={profile.travail} onChange={set("travail")} options={[
              { v: "bureau", l: "Bureau / sédentaire" }, { v: "debout", l: "Debout / actif" }, { v: "physique", l: "Travail physique" }, { v: "mixte", l: "Mixte" },
            ]} />
            <Field label="Niveau d'activité" value={profile.niveauActivite} onChange={set("niveauActivite")} options={[
              { v: "faible", l: "Faible" }, { v: "modere", l: "Modéré" }, { v: "actif", l: "Actif" }, { v: "tres_actif", l: "Très actif" },
            ]} />
            <Field label="Expérience course" value={profile.experienceCourse} onChange={set("experienceCourse")} options={[
              { v: "debutant", l: "Débutant (< 1 an)" }, { v: "1an", l: "1 an" }, { v: "3ans", l: "2–3 ans" }, { v: "5ans", l: "4–5 ans" }, { v: "expert", l: "Expert (5+ ans)" },
            ]} />
            <Field label="Objectif principal" value={profile.objectifPrincipal} onChange={set("objectifPrincipal")} options={[
              { v: "5k", l: "5 km" }, { v: "10k", l: "10 km" }, { v: "semi", l: "Semi-marathon" }, { v: "marathon", l: "Marathon" }, { v: "trail", l: "Trail" }, { v: "forme", l: "Forme générale" },
            ]} />
          </div>
        </Card>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Card>
          <SectionHead title="❤️ Métriques physiologiques" />
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <SliderField label={<><InfoTip term="FC max">FC maximale</InfoTip></>} value={profile.fc_max} onChange={set("fc_max")} min={150} max={220} unit=" bpm" color={T.red} />
            <SliderField label={<><InfoTip term="FC repos">FC repos</InfoTip></>} value={profile.fc_repos} onChange={set("fc_repos")} min={35} max={90} unit=" bpm" color={T.blue} />
            <SliderField label={<><InfoTip term="VMA">VMA</InfoTip></>} value={profile.vma} onChange={set("vma")} min={8} max={24} step={0.5} unit=" km/h" color={T.navy} />
            <SliderField label="Volume hebdo cible" value={profile.volumeCible || 40} onChange={set("volumeCible")} min={10} max={200} step={5} unit=" km/sem" color={T.green} />
          </div>
        </Card>

        <Card>
          <SectionHead title="🎯 Objectif de course" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Chrono visé" value={profile.objectifChrono} onChange={set("objectifChrono")} placeholder="1:45:00" />
            <Field label="Date objectif" value={profile.objectifDate} onChange={set("objectifDate")} type="date" />
          </div>
        </Card>

        <Card>
          <SectionHead title={<>💗 <InfoTip term="Zones FC">Zones FC</InfoTip> (méthode <InfoTip term="Karvonen">Karvonen</InfoTip>)</>} />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { z: "Z1 — Récupération", min: 0.5, max: 0.6, color: T.blue },
              { z: "Z2 — Endurance", min: 0.6, max: 0.7, color: T.green },
              { z: "Z3 — Tempo", min: 0.7, max: 0.8, color: "#CA8A04" },
              { z: "Z4 — Seuil", min: 0.8, max: 0.9, color: T.orange },
              { z: "Z5 — VMA", min: 0.9, max: 1.0, color: T.red },
            ].map(z => {
              const rr = profile.fc_max - profile.fc_repos;
              const lo = Math.round(profile.fc_repos + z.min * rr);
              const hi = Math.round(profile.fc_repos + z.max * rr);
              return (
                <div key={z.z} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 100, fontSize: 12, color: T.textMid }}>{z.z}</div>
                  <div style={{ flex: 1, height: 22, background: T.bg, borderRadius: 6, overflow: "hidden" }}>
                    <div style={{ height: "100%", background: z.color, display: "flex", alignItems: "center", paddingLeft: 8, opacity: 0.85 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#fff" }}>{lo}–{hi} bpm</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Zone danger — réinitialisation */}
      <Card style={{ borderColor: `${T.red}44` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: T.red, marginBottom: 4 }}>🗑️ Réinitialiser toutes mes données</div>
            <div style={{ fontSize: 12, color: T.textLight, lineHeight: 1.6 }}>
              Supprime toutes vos données GRAVV locales (séances, courses, sommeil, profil) et relance la configuration.
              Cette action est <strong>irréversible</strong>.
            </div>
          </div>
          <Btn variant="danger" onClick={() => {
            if (window.confirm("Supprimer toutes vos données et repartir de zéro ?")) {
              ["profile","runs","races"].forEach(k =>
                localStorage.removeItem("strata_" + k)
              );
              window.location.reload();
            }
          }}>Tout effacer</Btn>
        </div>
      </Card>

      {/* Strava */}
      <Card>
        <SectionHead title="🟠 Connexion Strava" />
        {stravaConnected ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#FC4C0210", borderRadius: 10, border: "1px solid #FC4C0230" }}>
              <span style={{ fontSize: 18 }}>✅</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>Compte Strava connecté</div>
                {profile.strava_athlete_id && <div style={{ fontSize: 11, color: T.textMid }}>Athlète ID : {profile.strava_athlete_id}</div>}
              </div>
            </div>
            {stravaMsg && (
              <div style={{ fontSize: 12, color: stravaStatus === "error" ? T.orange : T.green, padding: "6px 10px", background: stravaStatus === "error" ? `${T.orange}12` : `${T.green}12`, borderRadius: 8 }}>
                {stravaMsg}
              </div>
            )}
            <Btn variant="lime" onClick={onStravaSync} disabled={stravaStatus === "syncing" || stravaStatus === "loading"}>
              {stravaStatus === "syncing" ? "Synchronisation…" : "🔄 Synchroniser les activités"}
            </Btn>
            <Btn variant="ghost" onClick={() => {
              setProfile(p => ({ ...p, strava_access_token: null, strava_refresh_token: null, strava_token_expires: null, strava_athlete_id: null }));
            }}>Déconnecter Strava</Btn>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6 }}>
              Connecte ton compte Strava pour importer automatiquement tes activités dans GRAVV.
            </div>
            {stravaMsg && (
              <div style={{ fontSize: 12, color: T.orange, padding: "6px 10px", background: `${T.orange}12`, borderRadius: 8 }}>
                {stravaMsg}
              </div>
            )}
            <Btn variant="lime" disabled={stravaStatus === "loading"} onClick={() => {
              const redirectUri = encodeURIComponent(window.location.origin);
              const scope = "activity:read_all";
              window.location.href = `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&response_type=code&redirect_uri=${redirectUri}&approval_prompt=auto&scope=${scope}`;
            }}>
              {stravaStatus === "loading" ? "Connexion…" : "🟠 Connecter avec Strava"}
            </Btn>
          </div>
        )}
      </Card>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   FILE PARSERS
═══════════════════════════════════════════════════════════════════════════ */
function parseCSVLine(line) {
  // Parser CSV correct — respecte les champs entre guillemets contenant des virgules
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i+1] === '"') { current += '"'; i++; } // guillemet échappé ""
      else inQuotes = !inQuotes;
    } else if (ch === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

function parseGarminCSV(csvStr) {
  // Normalise les fins de ligne Windows (CRLF) et Mac (CR)
  const lines = csvStr.trim().replace(/\r\n/g,"\n").replace(/\r/g,"\n").split("\n");
  if (lines.length < 2) throw new Error("Fichier CSV vide ou invalide");

  const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase());

  const get = (row, ...keys) => {
    for (const k of keys) {
      const idx = headers.findIndex(h => h.includes(k));
      if (idx >= 0 && row[idx] !== undefined) return row[idx].trim().replace(/^"+|"+$/g,"");
    }
    return "";
  };

  const results = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const row = parseCSVLine(line);
    if (row.length < 3) continue;

    // Détecte le sport depuis la colonne Activity Type Garmin
    const actType = get(row, "activity type", "type", "activité");
    const strataType = garminTypeToStrata(actType);

    const dateRaw = get(row, "date", "start", "début");
    let parsedDate = new Date().toISOString().split("T")[0];
    if (dateRaw) {
      const d = new Date(dateRaw);
      if (!isNaN(d)) parsedDate = d.toISOString().split("T")[0];
    }

    const distRaw = get(row, "distance");
    let distKm = distRaw ? parseFloat(distRaw.replace(",",".")) : 0;
    if (distKm > 500) distKm = distKm / 1000; // mètres → km

    const timeRaw = get(row, "time", "duration", "durée", "temps", "elapsed");
    let dureeMin = 0;
    if (timeRaw) {
      const parts = timeRaw.split(":").map(Number);
      if (parts.length === 3) dureeMin = Math.round(parts[0]*60 + parts[1] + parts[2]/60);
      else if (parts.length === 2) dureeMin = Math.round(parts[0] + parts[1]/60);
      else dureeMin = Math.round(parseFloat(timeRaw));
    }

    const fcRaw = get(row, "avg hr", "fc moy", "average heart rate", "fréquence cardiaque moy");
    const fc_moy = fcRaw ? Math.round(parseFloat(fcRaw)) : 0;

    const fcMaxRaw = get(row, "max hr", "fc max", "maximum heart rate");
    const fc_max = fcMaxRaw ? Math.round(parseFloat(fcMaxRaw)) : 0;

    const elevRaw = get(row, "elev gain", "d+", "ascent", "dénivelé", "elevation gain", "total ascent");
    const denivele = elevRaw ? Math.round(parseFloat(elevRaw.replace(",","."))) : 0;

    const titleRaw = get(row, "title", "nom", "name", "activity name");

    const cadRaw = get(row, "avg run cadence", "cadence", "avg cadence");
    const cadence = cadRaw ? Math.round(parseFloat(cadRaw)) : 0;

    const calRaw = get(row, "calories");
    const calories = calRaw ? Math.round(parseFloat(calRaw)) : 0;

    if (distKm > 0 || dureeMin > 0) {
      results.push({
        date: parsedDate,
        distance: +distKm.toFixed(2),
        duree: dureeMin,
        fc_moy, fc_max,
        denivele,
        cadence,
        calories,
        note: titleRaw || actType,
        type: strataType,
        trimp: 0,
      });
    }
  }

  if (!results.length) throw new Error("Aucune activité de course trouvée. Vérifiez que le fichier est bien un export Garmin Connect (liste d'activités).");
  return results;
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371, dLat = (lat2 - lat1) * Math.PI / 180, dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function parseGPX(xmlStr) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlStr, "text/xml");
  const pts = Array.from(doc.querySelectorAll("trkpt"));
  if (!pts.length) throw new Error("Aucun point GPS trouvé");

  let dist = 0, elGain = 0, hrSum = 0, hrCount = 0;
  for (let i = 1; i < pts.length; i++) {
    const p0 = pts[i-1], p1 = pts[i];
    dist += haversineKm(+p0.getAttribute("lat"), +p0.getAttribute("lon"), +p1.getAttribute("lat"), +p1.getAttribute("lon"));
    const e0 = p0.querySelector("ele"), e1 = p1.querySelector("ele");
    if (e0 && e1) { const d = +e1.textContent - +e0.textContent; if (d > 0) elGain += d; }
    const hr = p1.querySelector("hr, heartrate, HeartRateBpm Value, gpxtpx\\:hr, ns3\\:hr");
    if (hr) { hrSum += +hr.textContent; hrCount++; }
  }

  const t0 = pts[0].querySelector("time"), t1 = pts[pts.length-1].querySelector("time");
  const dureeMin = t0 && t1 ? Math.round((new Date(t1.textContent) - new Date(t0.textContent)) / 60000) : null;
  const dateStr = t0 ? new Date(t0.textContent).toISOString().split("T")[0] : new Date().toISOString().split("T")[0];

  const nameEl = doc.querySelector("name, trk > name");
  return {
    date: dateStr,
    distance: +dist.toFixed(2),
    duree: dureeMin,
    fc_moy: hrCount > 0 ? Math.round(hrSum / hrCount) : 0,
    denivele: Math.round(elGain),
    note: nameEl ? nameEl.textContent.trim() : "",
  };
}

function parseTCX(xmlStr) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlStr, "text/xml");
  const activity = doc.querySelector("Activity");
  if (!activity) throw new Error("Pas d'activité trouvée dans le fichier TCX");

  const totalTime = doc.querySelector("TotalTimeSeconds");
  const totalDist = doc.querySelector("DistanceMeters");
  const avgHr = doc.querySelector("AverageHeartRateBpm Value");
  const startTime = doc.querySelector("Lap Id, Id");

  const laps = Array.from(doc.querySelectorAll("Lap"));
  let elGain = 0;
  laps.forEach(lap => {
    const pts = Array.from(lap.querySelectorAll("Trackpoint"));
    for (let i = 1; i < pts.length; i++) {
      const e0 = pts[i-1].querySelector("AltitudeMeters");
      const e1 = pts[i].querySelector("AltitudeMeters");
      if (e0 && e1) { const d = +e1.textContent - +e0.textContent; if (d > 0) elGain += d; }
    }
  });

  const dateStr = startTime ? new Date(startTime.textContent).toISOString().split("T")[0] : new Date().toISOString().split("T")[0];
  const nameEl = doc.querySelector("Activity > Notes, Notes");

  return {
    date: dateStr,
    distance: totalDist ? +(+totalDist.textContent / 1000).toFixed(2) : 0,
    duree: totalTime ? Math.round(+totalTime.textContent / 60) : 0,
    fc_moy: avgHr ? Math.round(+avgHr.textContent) : 0,
    denivele: Math.round(elGain),
    note: nameEl ? nameEl.textContent.trim() : "",
  };
}

function parseAppleHealth(xmlStr) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlStr, "text/xml");
  const workouts = Array.from(doc.querySelectorAll("Workout"))
    .filter(w => w.getAttribute("workoutActivityType")?.includes("Running"))
    .sort((a, b) => new Date(b.getAttribute("startDate")) - new Date(a.getAttribute("startDate")));
  if (!workouts.length) throw new Error("Aucune course trouvée dans l'export Apple Health");

  const w = workouts[0];
  const start = new Date(w.getAttribute("startDate"));
  const end = new Date(w.getAttribute("endDate"));
  const dist = w.getAttribute("totalDistance");
  const distUnit = w.getAttribute("totalDistanceUnit") || "km";

  let distKm = dist ? +dist : 0;
  if (distUnit === "mi") distKm = distKm * 1.60934;

  const hrStats = Array.from(doc.querySelectorAll("WorkoutStatistics"))
    .find(s => s.getAttribute("type")?.includes("HeartRate"));
  const avgHr = hrStats ? Math.round(+hrStats.getAttribute("average")) : 0;

  return {
    date: start.toISOString().split("T")[0],
    distance: +distKm.toFixed(2),
    duree: Math.round((end - start) / 60000),
    fc_moy: avgHr || 0,
    denivele: 0,
    note: "Import Apple Watch",
  };
}

async function parseActivityFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      const name = file.name.toLowerCase();
      try {
        if (name.endsWith(".gpx")) resolve({ single: parseGPX(content) });
        else if (name.endsWith(".tcx")) resolve({ single: parseTCX(content) });
        else if (name.endsWith(".xml")) resolve({ single: parseAppleHealth(content) });
        else if (name.endsWith(".csv")) resolve({ multi: parseGarminCSV(content) });
        else if (name.endsWith(".fit")) reject(new Error("FIT_BINARY"));
        else reject(new Error("Format non supporté"));
      } catch(err) { reject(err); }
    };
    reader.onerror = () => reject(new Error("Lecture du fichier impossible"));
    reader.readAsText(file);
  });
}

const PLATFORMS = [
  { id: "garmin",  label: "Garmin",      icon: "🟦", color: "#006EBF", formats: "CSV, GPX, TCX", exportInfo: "Garmin Connect → Activités → Exporter CSV (liste) ou GPX (activité unique)" },
  { id: "strava",  label: "Strava",      icon: "🟠", color: "#FC4C02", formats: "GPX, TCX",      exportInfo: "Strava → Mon profil → … → Télécharger les données ou par activité : ⋯ → Exporter GPX" },
  { id: "coros",   label: "Coros",       icon: "⚫", color: "#1A1A2E", formats: "GPX",           exportInfo: "App Coros → Activité → Partager → Exporter GPX" },
  { id: "suunto",  label: "Suunto",      icon: "🔴", color: "#D61F26", formats: "GPX",           exportInfo: "Suunto App → Exercice → … → Exporter en GPX" },
  { id: "apple",   label: "Apple Watch", icon: "⬛", color: "#1D1D1F", formats: "XML (Health)",  exportInfo: "Santé → Profil → Exporter les données de santé → dézipper → export.xml" },
];

/* ═══════════════════════════════════════════════════════════════════════════
   MODALS
═══════════════════════════════════════════════════════════════════════════ */
function ModalAddRun({ onClose, onAdd, onAddMany, initialData, onDelete }) {
  const [mode, setMode] = useState("manual"); // "manual" | "import"
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [parseState, setParseState] = useState("idle"); // "idle"|"parsing"|"success"|"error"
  const [parseMsg, setParseMsg] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState(initialData ? {...initialData, distance:String(initialData.distance||""), duree:String(initialData.duree||""), fc_moy:String(initialData.fc_moy||""), fc_max:String(initialData.fc_max||""), denivele:String(initialData.denivele||""), cadence:String(initialData.cadence||""), calories:String(initialData.calories||""), temp:String(initialData.temp||"") } : { type: "fond", date: new Date().toISOString().split("T")[0], distance: "", duree: "", fc_moy: "", fc_max: "", denivele: "", cadence: "", calories: "", temp: "", surface: "route", shoes_id: "", ressenti: 3, note: "" });
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const isEditRun = !!initialData;
  const [csvRuns, setCsvRuns] = useState(null); // for multi-import from CSV

  const handleFile = async (file) => {
    setParseState("parsing");
    setParseMsg(`Analyse de ${file.name}…`);
    try {
      const result = await parseActivityFile(file);

      if (result.multi) {
        // CSV with multiple runs
        setCsvRuns(result.multi);
        setParseState("success");
        setParseMsg(`✅ ${result.multi.length} activité(s) détectée(s) dans le CSV`);
      } else {
        const data = result.single;
        setForm(f => ({
          ...f,
          date: data.date || f.date,
          distance: data.distance ? String(data.distance) : f.distance,
          duree: data.duree ? String(data.duree) : f.duree,
          fc_moy: data.fc_moy ? String(data.fc_moy) : f.fc_moy,
          denivele: data.denivele ? String(data.denivele) : f.denivele,
          note: data.note || f.note,
        }));
        setParseState("success");
        setParseMsg(`✅ Import réussi : ${data.distance} km · ${data.duree} min${data.fc_moy ? ` · ${data.fc_moy} bpm` : ""}`);
        setMode("manual");
      }
    } catch (err) {
      if (err.message === "FIT_BINARY") {
        setParseState("error");
        const platform = PLATFORMS.find(p => p.id === selectedPlatform);
        setParseMsg(`Les fichiers .FIT sont en binaire et ne peuvent pas être lus directement.\n👉 ${platform?.exportInfo || "Exportez votre activité au format GPX depuis votre application."}`);
      } else {
        setParseState("error");
        setParseMsg(`❌ Erreur : ${err.message}`);
      }
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const platformInfo = PLATFORMS.find(p => p.id === selectedPlatform);

  return (
    <Modal title="Enregistrer une séance" onClose={onClose} width={620}>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

        {/* Mode tabs */}
        <div style={{ display: "flex", background: T.bg, borderRadius: 12, padding: 4, gap: 4 }}>
          {[{ id: "manual", label: "✏️  Saisie manuelle" }, { id: "import", label: "📥  Importer depuis" }].map(m => (
            <button key={m.id} onClick={() => { setMode(m.id); setParseState("idle"); }} style={{
              flex: 1, padding: "9px 16px", borderRadius: 9, border: "none", cursor: "pointer",
              background: mode === m.id ? T.surface : "transparent",
              color: mode === m.id ? T.text : T.textLight,
              fontWeight: mode === m.id ? 800 : 500, fontSize: 13, fontFamily: "inherit",
              boxShadow: mode === m.id ? T.shadow : "none", transition: "all 0.15s",
            }}>{m.label}</button>
          ))}
        </div>

        {/* ── IMPORT MODE ── */}
        {mode === "import" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Platform picker */}
            <div>
              <Label>Choisissez votre plateforme</Label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8, marginTop: 8 }}>
                {PLATFORMS.map(p => (
                  <button key={p.id} onClick={() => { setSelectedPlatform(p.id); setParseState("idle"); }} style={{
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 5,
                    padding: "12px 6px", borderRadius: 12, border: `2px solid ${selectedPlatform === p.id ? p.color : T.border}`,
                    background: selectedPlatform === p.id ? `${p.color}10` : T.bg,
                    cursor: "pointer", transition: "all 0.15s", fontFamily: "inherit",
                  }}>
                    <span style={{ fontSize: 22 }}>{p.icon}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: selectedPlatform === p.id ? p.color : T.textMid }}>{p.label}</span>
                    <span style={{ fontSize: 9, color: T.textLight }}>{p.formats}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Export instructions */}
            {platformInfo && parseState !== "error" && (
              <div style={{ background: `${platformInfo.color}08`, border: `1px solid ${platformInfo.color}33`, borderRadius: 12, padding: "12px 16px", display: "flex", alignItems: "flex-start", gap: 10 }}>
                <span style={{ fontSize: 18, marginTop: 1 }}>💡</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: platformInfo.color, marginBottom: 3 }}>Comment exporter depuis {platformInfo.label}</div>
                  <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.6 }}>{platformInfo.exportInfo}</div>
                  <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>Formats acceptés : {platformInfo.formats}</div>
                </div>
              </div>
            )}

            {/* Error / FIT message */}
            {parseState === "error" && (
              <div style={{ background: T.redLight, border: `1px solid ${T.red}44`, borderRadius: 12, padding: "12px 16px" }}>
                <div style={{ fontSize: 13, color: T.red, fontWeight: 700, marginBottom: 4 }}>Impossible de lire ce fichier</div>
                {parseMsg.split("\n").map((l, i) => <div key={i} style={{ fontSize: 12, color: T.textMid, lineHeight: 1.6 }}>{l}</div>)}
              </div>
            )}

            {/* Drop zone */}
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? T.navy : parseState === "success" ? T.green : T.border}`,
                borderRadius: 16, padding: "32px 20px", textAlign: "center", cursor: "pointer",
                background: dragOver ? `${T.navy}06` : parseState === "success" ? T.greenLight : T.bg,
                transition: "all 0.2s",
              }}>
              <input ref={fileRef} type="file" accept=".gpx,.tcx,.fit,.xml,.csv" style={{ display: "none" }} onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />

              {parseState === "parsing" ? (
                <div>
                  <div style={{ fontSize: 32, marginBottom: 8, animation: "spin 1s linear infinite", display: "inline-block" }}>⏳</div>
                  <div style={{ fontWeight: 700, color: T.text }}>Analyse en cours…</div>
                </div>
              ) : parseState === "success" ? (
                <div>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                  <div style={{ fontWeight: 700, color: T.green, fontSize: 14 }}>Données importées !</div>
                  <div style={{ fontSize: 12, color: T.textLight, marginTop: 4 }}>{parseMsg}</div>
                  <div style={{ fontSize: 12, color: T.textMid, marginTop: 8 }}>Le formulaire a été rempli automatiquement. Vous pouvez ajuster et enregistrer.</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 40, marginBottom: 10 }}>📂</div>
                  <div style={{ fontWeight: 800, fontSize: 15, color: T.text, marginBottom: 4 }}>
                    {selectedPlatform ? `Déposez votre fichier ${platformInfo?.formats}` : "Sélectionnez d'abord une plateforme ci-dessus"}
                  </div>
                  <div style={{ fontSize: 12, color: T.textLight }}>Glissez-déposez ou cliquez pour parcourir</div>
                  <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
                    {[".gpx", ".tcx", ".csv", ".xml", ".fit"].map(ext => (
                      <span key={ext} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: "3px 8px", fontSize: 11, color: T.textMid, fontWeight: 600 }}>{ext}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* CSV multi-run import UI */}
            {parseState === "success" && csvRuns && (
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                <div style={{ fontSize:13, fontWeight:700, color:T.text }}>Sélectionnez les activités à importer :</div>
                <div style={{ maxHeight:260, overflowY:"auto", display:"flex", flexDirection:"column", gap:6 }}>
                  {csvRuns.map((r, i) => (
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:12, background:T.bg, borderRadius:10, padding:"10px 14px" }}>
                      <input type="checkbox" defaultChecked id={`csv-r-${i}`} style={{ width:16, height:16, cursor:"pointer" }}/>
                      <label htmlFor={`csv-r-${i}`} style={{ flex:1, cursor:"pointer", display:"flex", gap:16, alignItems:"center" }}>
                        <span style={{ fontWeight:500, fontSize:13, color:T.text, minWidth:80, fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{fmtDate(r.date)}</span>
                        <span style={{ fontSize:13, color:T.navy }}>{r.distance} km</span>
                        <span style={{ fontSize:12, color:T.textMid, fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{r.duree} min</span>
                        {r.fc_moy > 0 && <span style={{ fontSize:12, color:T.textLight }}>{r.fc_moy} bpm</span>}
                        {r.denivele > 0 && <span style={{ fontSize:12, color:T.textLight }}>+{r.denivele}m</span>}
                        {r.note && <span style={{ fontSize:11, color:T.textLight, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.note}</span>}
                      </label>
                    </div>
                  ))}
                </div>
                <Btn variant="lime" onClick={() => {
                  const selected = csvRuns.filter((r, i) => {
                    const cb = document.getElementById(`csv-r-${i}`);
                    return cb?.checked;
                  });
                  if (selected.length > 0) {
                    if (onAddMany) {
                      onAddMany(selected);
                    } else {
                      // fallback — ne devrait pas arriver
                      selected.forEach((r, i) => onAdd({ ...r, id: Date.now() + i }));
                    }
                  }
                  onClose();
                }}>
                  Importer {csvRuns.filter((r, i) => document.getElementById(`csv-r-${i}`)?.checked !== false).length || csvRuns.length} activité(s)
                </Btn>
              </div>
            )}

            {parseState === "success" && !csvRuns && (
              <Btn variant="ghost" style={{ alignSelf: "center" }} onClick={() => { setParseState("idle"); setMode("manual"); }}>
                ✏️ Vérifier et modifier les données →
              </Btn>
            )}
          </div>
        )}

        {/* ── MANUAL MODE ── */}
        {mode === "manual" && (
          <>
            {parseState === "success" && (
              <div style={{ background: T.greenLight, border: `1px solid ${T.green}44`, borderRadius: 12, padding: "10px 14px", fontSize: 13, color: T.green, fontWeight: 600 }}>
                ✅ {parseMsg} — Vérifiez et complétez si besoin.
              </div>
            )}

            {/* ── Sélecteur de sport — groupé par catégorie ── */}
            <div>
              <Label>Sport & type de séance</Label>
              {[
                { label:"Course à pied", ids:["fond","fractionne","tempo","longrun","recup","trail","race"] },
                { label:"Vélo",          ids:["velo","vtt","home_trainer"] },
                { label:"Natation",      ids:["nage","open_water"] },
                { label:"Renforcement",  ids:["muscu","yoga","core"] },
                { label:"Autre",         ids:["marche","autre"] },
              ].map(group => {
                const types = ACTIVITY_TYPES.filter(t => group.ids.includes(t.id));
                return (
                  <div key={group.label} style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>{group.label}</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {types.map(t => (
                        <div key={t.id} onClick={() => set("type")(t.id)} style={{
                          padding: "7px 12px", borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                          background: form.type === t.id ? `${t.color}18` : T.bg,
                          border: `1px solid ${form.type === t.id ? t.color : T.border}`,
                          boxShadow: form.type === t.id ? `0 0 0 1px ${t.color}` : "none",
                          transition: "all 0.12s",
                        }}>
                          <span style={{ fontSize: 14 }}>{t.icon}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: form.type === t.id ? t.color : T.textMid }}>{t.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="Date" value={form.date} onChange={set("date")} type="date" />
              <Field label={getSportFromType(form.type)==="swim" ? "Distance (m ou km)" : "Distance"} value={form.distance} onChange={set("distance")} type="number" unit={getSportFromType(form.type)==="swim"?"m/km":"km"} />
              <Field label="Durée" value={form.duree} onChange={set("duree")} type="number" unit="min" />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label={<><InfoTip term="FC moy">FC moyenne</InfoTip></>} value={form.fc_moy} onChange={set("fc_moy")} type="number" unit="bpm" placeholder="Optionnel" />
              <Field label="FC max séance" value={form.fc_max} onChange={set("fc_max")} type="number" unit="bpm" placeholder="Optionnel" />
              {getSportFromType(form.type) !== "gym" &&
                <Field label={<><InfoTip term="D+">Dénivelé</InfoTip></>} value={form.denivele} onChange={set("denivele")} type="number" unit="m" placeholder="Optionnel" />
              }
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              {getSportFromType(form.type) === "run" &&
                <Field label={<><InfoTip term="Cadence">Cadence</InfoTip></>} value={form.cadence} onChange={set("cadence")} type="number" unit="spm" placeholder="ex: 176" />
              }
              {getSportFromType(form.type) === "velo" &&
                <Field label="Puissance moy." value={form.cadence} onChange={set("cadence")} type="number" unit="W" placeholder="Optionnel" />
              }
              {getSportFromType(form.type) === "swim" &&
                <Field label="Longueurs" value={form.cadence} onChange={set("cadence")} type="number" unit="×" placeholder="nb de longueurs" />
              }
              <Field label="Calories" value={form.calories} onChange={set("calories")} type="number" unit="kcal" placeholder="Optionnel" />
              <Field label="Température" value={form.temp} onChange={set("temp")} type="number" unit="°C" placeholder="Optionnel" />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {getSportFromType(form.type) === "run" && (
                <Field label="Surface" value={form.surface} onChange={set("surface")} options={[
                  { v:"route", l:"🛣️ Route / Bitume" }, { v:"trail", l:"⛰️ Trail / Sentier" },
                  { v:"piste", l:"🏟️ Piste d'athlétisme" }, { v:"tapis", l:"🔁 Tapis roulant" },
                  { v:"grass", l:"🌿 Herbe / Cross" },
                ]} />
              )}
              <Field label="Équipement utilisé" value={form.shoes_id} onChange={set("shoes_id")} placeholder="optionnel — ID équipement" type="number" />
            </div>

            <div>
              <Label>Ressenti ({["", "Très facile", "Facile", "Modéré", "Difficile", "Épuisant"][form.ressenti]})</Label>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                {[1,2,3,4,5].map(n => (
                  <button key={n} onClick={() => set("ressenti")(n)} style={{
                    flex: 1, padding: "10px", border: `2px solid ${form.ressenti >= n ? T.orange : T.border}`,
                    borderRadius: 10, background: form.ressenti >= n ? `${T.orange}20` : T.bg, cursor: "pointer", fontSize: 20,
                  }}>★</button>
                ))}
              </div>
            </div>

            <Field label="Note (optionnel)" value={form.note} onChange={set("note")} placeholder="ex: 10×400m, terrain plat, nom de l'activité…" />
          </>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 4, borderTop: `1px solid ${T.border}` }}>
          <Btn variant="ghost" onClick={onClose}>Annuler</Btn>
          <Btn variant="lime"
            disabled={!form.distance || !form.duree}
            onClick={() => { onAdd({ ...form, id: form.id||Date.now(), distance: +form.distance, duree: +form.duree, fc_moy: +form.fc_moy||0, fc_max: +form.fc_max||0, denivele: +form.denivele||0, cadence: +form.cadence||0, calories: +form.calories||0, temp: form.temp !== "" ? +form.temp : null, shoes_id: +form.shoes_id||null }); onClose(); }}>
            Enregistrer la séance
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

function ModalAddRace({ onClose, onAdd, initialData, onDelete }) {
  const [step, setStep] = useState(1);
  const isEditRace = !!initialData;
  const defaultIsTrail = initialData ? (initialData.isTrail || false) : false;
  const [isTrail, setIsTrail] = useState(defaultIsTrail);
  const [customDist, setCustomDist] = useState(
    initialData && !["5","10","21.097","42.195"].includes(String(initialData.distance))
      ? String(initialData.distance) : ""
  );
  const [form, setForm] = useState(initialData ? {...initialData, distance: String(initialData.distance)} : {
    nom: "", date: "", distance: "10", objectif: "", lieu: "", statut: "inscrit",
    courseProfile: {
      surface: "route", profil: "plat",
      denivele_plus: 0, denivele_minus: 0,
      temperature: 15, vent: "nul", orientation_vent: "variable", humidite: "normale",
      virages: "larges", revetement: "asphalte", altitude: 0, altitude_max: 0,
      technicite: "T2", ravitos: 0, nuit: false,
      tags: [], note: "",
    },
  });
  const [gpxFile, setGpxFile] = useState(initialData?.gpxData ? { fileName: initialData.gpxData.fileName, distance: initialData.gpxData.distance, elevGain: initialData.gpxData.elevGain, elevLoss: initialData.gpxData.elevLoss, profile: initialData.gpxData.profile } : null);
  const [gpxParsing, setGpxParsing] = useState(false);
  const [gpxError, setGpxError] = useState("");

  const handleGpxUpload = async (file) => {
    setGpxParsing(true);
    setGpxError("");
    try {
      const txt = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = e => res(e.target.result);
        reader.onerror = rej;
        reader.readAsText(file);
      });
      const parser = new DOMParser();
      const doc = parser.parseFromString(txt, "text/xml");
      const pts = Array.from(doc.querySelectorAll("trkpt"));
      if (!pts.length) throw new Error("Aucun point GPS dans ce fichier");

      let totalDist = 0, elevGain = 0, elevLoss = 0;
      let prev_lat, prev_lon, prev_ele;
      const rawPts = [];

      for (let i = 0; i < pts.length; i++) {
        const lat = +pts[i].getAttribute("lat");
        const lon = +pts[i].getAttribute("lon");
        const eleEl = pts[i].querySelector("ele");
        const ele = eleEl ? +eleEl.textContent : null;
        if (i > 0 && prev_lat !== undefined) {
          const R = 6371;
          const dLat = (lat - prev_lat) * Math.PI / 180;
          const dLon = (lon - prev_lon) * Math.PI / 180;
          const a = Math.sin(dLat/2)**2 + Math.cos(prev_lat*Math.PI/180)*Math.cos(lat*Math.PI/180)*Math.sin(dLon/2)**2;
          totalDist += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        }
        if (ele !== null && prev_ele !== undefined && prev_ele !== null) {
          const diff = ele - prev_ele;
          if (diff > 0.5) elevGain += diff;
          else if (diff < -0.5) elevLoss += Math.abs(diff);
        }
        rawPts.push({ dist: totalDist, ele });
        prev_lat = lat; prev_lon = lon; prev_ele = ele;
      }

      const step = Math.max(1, Math.floor(rawPts.length / 100));
      const profile = rawPts.filter((_, i) => i % step === 0).map(p => [+p.dist.toFixed(2), p.ele !== null ? Math.round(p.ele) : null]);

      setGpxFile({
        fileName: file.name,
        distance: +totalDist.toFixed(2),
        elevGain: Math.round(elevGain),
        elevLoss: Math.round(elevLoss),
        profile,
      });
    } catch(e) {
      setGpxError(e.message);
    }
    setGpxParsing(false);
  };

  const set  = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  const setCP = (k) => (v) => setForm(f => ({ ...f, courseProfile: { ...f.courseProfile, [k]: v } }));
  const toggleTag = (tag) => {
    const tags = form.courseProfile.tags.includes(tag)
      ? form.courseProfile.tags.filter(t => t !== tag)
      : [...form.courseProfile.tags, tag];
    setCP("tags")(tags);
  };

  // When switching type, reset distance to sensible default
  const switchType = (trail) => {
    setIsTrail(trail);
    if (trail) {
      setForm(f => ({ ...f, distance: "27", courseProfile: { ...f.courseProfile, revetement: "terre", profil: "montagneux", surface: "trail" } }));
      setCustomDist("27");
    } else {
      setForm(f => ({ ...f, distance: "10", courseProfile: { ...f.courseProfile, revetement: "asphalte", profil: "plat", surface: "route" } }));
      setCustomDist("");
    }
  };

  const cp = form.courseProfile;
  const distNum = parseFloat(form.distance) || 0;
  const { total: mult, details } = calcCourseMultiplier({ ...cp, distance: distNum });
  const baseSec = timeToSec(form.objectif);
  const adjusted = baseSec > 0 ? applyMultiplierToTime(baseSec, mult) : null;

  const TAGS_ROUTE = [
    "très plat","vallonné","circuit urbain","bords de mer","forêt",
    "plein été","automne","hiver","vent fort","pluie probable",
    "altitude","pavés","route rapide","virages serrés","idéal PR","populaire","international",
  ];
  const TAGS_TRAIL = [
    "sentier technique","hors-sentier","rocher","pierrier","boue","neige",
    "forêt","crêtes exposées","passage de nuit","ravitos solide","très isolé",
    "altitude >2000m","descentes raides","rivières à franchir","balisage difficile",
  ];

  const TRAIL_DISTANCES = [
    { v: "15", l: "15 km" }, { v: "20", l: "20 km" }, { v: "27", l: "27 km" },
    { v: "30", l: "30 km" }, { v: "42", l: "42 km" }, { v: "50", l: "50 km" },
    { v: "65", l: "65 km" }, { v: "80", l: "80 km" }, { v: "100", l: "100 km" },
    { v: "160", l: "160 km" }, { v: "custom", l: "Autre..." },
  ];

  const ChoiceRow = ({ label, options, value, onChange, green }) => (
    <div>
      <Label>{label}</Label>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
        {options.map(o => {
          const active = value === o.v;
          const accent = green ? T.green : T.navy;
          return (
            <div key={o.v} onClick={() => onChange(o.v)} style={{
              padding: "7px 12px", borderRadius: 10, cursor: "pointer", fontSize: 12, fontWeight: 600,
              background: active ? `${accent}15` : T.bg,
              border: `2px solid ${active ? accent : T.border}`,
              color: active ? accent : T.textMid,
              transition: "all 0.12s", display: "flex", alignItems: "center", gap: 5,
            }}>
              {o.icon && <span>{o.icon}</span>}{o.l}
            </div>
          );
        })}
      </div>
    </div>
  );

  const typeAccent = isTrail ? T.green : T.navy;

  return (
    <Modal title={step === 1 ? (isTrail ? "🏔️ Ajouter un trail — Infos générales" : "🏁 Ajouter une course — Infos générales") : (isTrail ? "🗺️ Profil du parcours trail" : "🗺️ Profil du parcours route")} onClose={onClose} width={640}>
      {/* Progress */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {[1, 2].map(s => (
          <div key={s} onClick={() => s < step && setStep(s)} style={{
            flex: 1, height: 4, borderRadius: 2, cursor: s < step ? "pointer" : "default",
            background: s <= step ? typeAccent : T.border, transition: "background 0.2s",
          }} />
        ))}
      </div>

      {step === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Route / Trail selector */}
          <div>
            <Label>Type de course</Label>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              {[
                { trail: false, icon: "🏁", label: "Route", sub: "Asphalte, vitesse" },
                { trail: true,  icon: "🏔️", label: "Trail",  sub: "Sentier, montagne, ultra" },
              ].map(opt => (
                <div key={String(opt.trail)} onClick={() => switchType(opt.trail)} style={{
                  flex: 1, padding: "12px 16px", borderRadius: 12, cursor: "pointer",
                  border: `2px solid ${isTrail === opt.trail ? (opt.trail ? T.green : T.navy) : T.border}`,
                  background: isTrail === opt.trail ? (opt.trail ? `${T.green}12` : `${T.navy}08`) : T.surface,
                  transition: "all 0.15s",
                }}>
                  <div style={{ fontSize: 20, marginBottom: 4 }}>{opt.icon}</div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: isTrail === opt.trail ? (opt.trail ? T.green : T.navy) : T.textMid }}>{opt.label}</div>
                  <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>{opt.sub}</div>
                </div>
              ))}
            </div>
          </div>

          <Field label="Nom de la course" value={form.nom} onChange={set("nom")} placeholder={isTrail ? "ex: Écotrail Paris 30km" : "ex: Semi-marathon de Paris"} />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Date" value={form.date} onChange={set("date")} type="date" />

            {/* Distance field — different for trail vs route */}
            {isTrail ? (
              <div>
                <Label>Distance</Label>
                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                  {TRAIL_DISTANCES.map(opt => {
                    const active = opt.v === "custom" ? !TRAIL_DISTANCES.slice(0,-1).some(d => d.v === form.distance) : form.distance === opt.v;
                    return (
                      <div key={opt.v} onClick={() => {
                        if (opt.v === "custom") { setForm(f => ({ ...f, distance: customDist||"" })); }
                        else { setForm(f => ({ ...f, distance: opt.v })); setCustomDist(""); }
                      }} style={{
                        padding: "5px 10px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600,
                        background: active ? `${T.green}15` : T.bg,
                        border: `2px solid ${active ? T.green : T.border}`,
                        color: active ? T.green : T.textMid, transition: "all 0.12s",
                      }}>{opt.l}</div>
                    );
                  })}
                </div>
                {/* Custom km input */}
                {!TRAIL_DISTANCES.slice(0,-1).some(d => d.v === form.distance) && (
                  <input
                    type="number" min={1} max={500} step={0.1}
                    value={customDist}
                    onChange={e => { setCustomDist(e.target.value); setForm(f => ({ ...f, distance: e.target.value })); }}
                    placeholder="Distance en km"
                    style={{ marginTop: 8, width: "100%", padding: "8px 12px", borderRadius: 8, border: `1px solid ${T.green}`, fontSize: 13, fontFamily: T.fontData, outline: "none", boxSizing: "border-box" }}
                  />
                )}
              </div>
            ) : (
              <Field label="Distance" value={form.distance} onChange={set("distance")} options={[
                { v: "5", l: "5 km" }, { v: "10", l: "10 km" },
                { v: "21.097", l: "Semi-marathon" }, { v: "42.195", l: "Marathon" }, { v: "autre", l: "Autre" },
              ]} />
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Objectif chrono (optionnel)" value={form.objectif} onChange={set("objectif")} placeholder={isTrail ? "ex: 13:30:00" : "ex: 1:45:00"} />
            <Field label="Lieu / Ville" value={form.lieu} onChange={set("lieu")} placeholder="ex: Chamonix, FR" />
          </div>
          <Field label="Statut" value={form.statut} onChange={set("statut")} options={[
            { v: "inscrit", l: "Inscrit ✓" }, { v: "cible", l: "Objectif cible 🎯" }, { v: "planifie", l: "Planifié" },
          ]} />
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 4 }}>
            <Btn variant="ghost" onClick={onClose}>Annuler</Btn>
            <Btn variant={isTrail ? "lime" : "lime"} disabled={!form.nom || !form.date || !distNum} onClick={() => setStep(2)}>
              Suivant — Profil du parcours →
            </Btn>
          </div>
        </div>
      )}

      {step === 2 && isTrail && (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Dénivelé & Altitude — most important for trail */}
          <div style={{ padding: "14px 16px", background: `${T.green}08`, borderRadius: 12, border: `1px solid ${T.green}30` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.green, textTransform: "uppercase", letterSpacing: 1, marginBottom: 14 }}>Dénivelé & Altitude</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
              <Field label="D+" value={cp.denivele_plus} onChange={v => setCP("denivele_plus")(+v)} type="number" unit="m" />
              <Field label="D−" value={cp.denivele_minus} onChange={v => setCP("denivele_minus")(+v)} type="number" unit="m" />
              <Field label="Alt. départ" value={cp.altitude} onChange={v => setCP("altitude")(+v)} type="number" unit="m" />
              <Field label="Alt. max" value={cp.altitude_max||0} onChange={v => setCP("altitude_max")(+v)} type="number" unit="m" />
            </div>
          </div>

          {/* Technicité */}
          <div style={{ padding: "14px 16px", background: T.bg, borderRadius: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 14 }}>Technicité</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <ChoiceRow green label="Niveau technique (ITRA)" value={cp.technicite||"T2"} onChange={setCP("technicite")} options={[
                { v: "T1", l: "T1", icon: "🟢" },
                { v: "T2", l: "T2", icon: "🟡" },
                { v: "T3", l: "T3", icon: "🟠" },
                { v: "T4", l: "T4", icon: "🔴" },
                { v: "T5", l: "T5", icon: "⚫" },
              ]} />
              <div style={{ fontSize: 11, color: T.textMid, marginTop: -4 }}>
                T1 chemin facile · T2 sentier balisé · T3 technique (pierrier, hors-sentier) · T4 très technique · T5 alpinisme
              </div>
              <ChoiceRow green label="Terrain dominant" value={cp.revetement} onChange={setCP("revetement")} options={[
                { v: "terre",    l: "Terre / sentier", icon: "🌿" },
                { v: "gravier",  l: "Gravier / cailloux", icon: "🪨" },
                { v: "rocher",   l: "Rocher / pierrier", icon: "⛰️" },
                { v: "mixte",    l: "Mixte", icon: "🔀" },
                { v: "neige",    l: "Neige / verglas", icon: "❄️" },
              ]} />
              <ChoiceRow green label="Profil" value={cp.profil} onChange={setCP("profil")} options={[
                { v: "montagneux",  l: "Montagneux",    icon: "⛰️" },
                { v: "vallonne",    l: "Vallonné",      icon: "〰️" },
                { v: "plat",        l: "Rolling / plat",icon: "➖" },
                { v: "aller_retour",l: "Boucles",       icon: "🔄" },
              ]} />
            </div>
          </div>

          {/* Logistique trail */}
          <div style={{ padding: "14px 16px", background: T.bg, borderRadius: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 14 }}>Logistique</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Nombre de ravitaillements" value={cp.ravitos||0} onChange={v => setCP("ravitos")(+v)} type="number" />
                <div>
                  <Label>Course de nuit</Label>
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    {[{v:false,l:"Non",icon:"☀️"},{v:true,l:"Oui (lampe frontale)",icon:"🌙"}].map(opt => (
                      <div key={String(opt.v)} onClick={() => setCP("nuit")(opt.v)} style={{
                        flex: 1, padding: "7px 12px", borderRadius: 10, cursor: "pointer", fontSize: 12, fontWeight: 600,
                        background: cp.nuit === opt.v ? `${T.green}15` : T.bg,
                        border: `2px solid ${cp.nuit === opt.v ? T.green : T.border}`,
                        color: cp.nuit === opt.v ? T.green : T.textMid,
                        transition: "all 0.12s", display: "flex", alignItems: "center", gap: 6,
                      }}>{opt.icon} {opt.l}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Météo */}
          <div style={{ padding: "14px 16px", background: T.bg, borderRadius: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 14 }}>Conditions météo attendues</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <Label>Température prévue : <strong style={{ color: cp.temperature > 25 ? T.orange : cp.temperature > 20 ? "#E09030" : T.green }}>{cp.temperature}°C</strong></Label>
                <input type="range" min={-10} max={40} step={1} value={cp.temperature} onChange={e => setCP("temperature")(+e.target.value)}
                  style={{ width: "100%", marginTop: 8, accentColor: cp.temperature > 25 ? T.orange : T.green }} />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.textLight }}>
                  <span>❄️ -10°C</span><span>🌤️ Optimal ≈ 5-15°C</span><span>🔥 40°C</span>
                </div>
              </div>
              <ChoiceRow green label="Humidité" value={cp.humidite} onChange={setCP("humidite")} options={[
                { v: "seche",    l: "Sèche",    icon: "☀️" },
                { v: "normale",  l: "Normale",  icon: "🌤️" },
                { v: "humide",   l: "Humide",   icon: "🌧️" },
                { v: "pluie",    l: "Pluie",    icon: "⛈️" },
              ]} />
            </div>
          </div>

          {/* Tags trail */}
          <div>
            <Label>Caractéristiques du parcours</Label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
              {TAGS_TRAIL.map(tag => (
                <div key={tag} onClick={() => toggleTag(tag)} style={{
                  padding: "5px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12,
                  background: cp.tags.includes(tag) ? `${T.green}15` : T.bg,
                  border: `1px solid ${cp.tags.includes(tag) ? T.green : T.border}`,
                  color: cp.tags.includes(tag) ? T.green : T.textMid, fontWeight: 600,
                  transition: "all 0.12s",
                }}>{cp.tags.includes(tag) ? "✓ " : ""}{tag}</div>
              ))}
            </div>
          </div>

          <Field label="Notes sur le parcours (optionnel)" value={cp.note} onChange={setCP("note")} placeholder="ex: Nuit km 40-70, ravitos solides km 25/50, altitude >2500m au sommet..." />

          {/* GPX */}
          <div>
            <Label>Fichier GPX du parcours <span style={{ color: T.orange, fontSize: 11 }}>obligatoire pour valider</span></Label>
            <div style={{ marginTop: 8 }}>
              {gpxFile ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: `${T.green}12`, borderRadius: 10, border: `1px solid ${T.green}40` }}>
                  <span style={{ fontSize: 18 }}>📄</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.green }}>{gpxFile.fileName}</div>
                    {gpxFile.distance && <div style={{ fontSize: 11, color: T.textMid, fontFamily: T.fontData }}>{gpxFile.distance} km · D+ {gpxFile.elevGain}m · D- {gpxFile.elevLoss}m</div>}
                  </div>
                  <button onClick={() => setGpxFile(null)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textLight, fontSize: 16 }}>✕</button>
                </div>
              ) : (
                <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: T.bg, borderRadius: 10, border: `1.5px dashed ${T.border}`, cursor: "pointer" }}>
                  <span style={{ fontSize: 20 }}>📁</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{gpxParsing ? "⏳ Lecture…" : "Importer le GPX de la course"}</div>
                    <div style={{ fontSize: 11, color: T.textMid }}>Sans GPX la course sera enregistrée en brouillon</div>
                  </div>
                  <input type="file" accept=".gpx" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) handleGpxUpload(e.target.files[0]); }} />
                </label>
              )}
              {gpxError && <div style={{ fontSize: 12, color: T.red || T.orange, marginTop: 6 }}>{gpxError}</div>}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "space-between", paddingTop: 4, borderTop: `1px solid ${T.border}` }}>
            <div style={{display:"flex",gap:8}}>
              <Btn variant="ghost" onClick={() => setStep(1)}>← Retour</Btn>
              {isEditRace && onDelete && <Btn variant="danger" onClick={()=>{if(window.confirm("Supprimer cette course ?"))onDelete(form.id);onClose();}}>Supprimer</Btn>}
            </div>
            <Btn variant="lime" onClick={() => {
              const raceToSave = {
                ...form,
                id: form.id||Date.now(),
                isTrail: true,
                distance: distNum,
                splits: form.splits||[],
                nutrition: form.nutrition||[],
                tapering: form.tapering||[],
                gpxData: gpxFile || null,
                statut: gpxFile ? (form.statut || "inscrit") : "brouillon",
              };
              onAdd(raceToSave);
              onClose();
            }}>{isEditRace ? "Enregistrer" : "Ajouter le trail"}</Btn>
          </div>
        </div>
      )}

      {step === 2 && !isTrail && (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Terrain */}
          <div style={{ padding: "14px 16px", background: T.bg, borderRadius: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 14 }}>Terrain</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <ChoiceRow label="Profil altimétrique" value={cp.profil} onChange={setCP("profil")} options={[
                { v: "plat",         l: "Plat",          icon: "➖" },
                { v: "vallonne",     l: "Vallonné",      icon: "〰️" },
                { v: "montagneux",   l: "Montagneux",    icon: "⛰️" },
                { v: "descente",     l: "Descente nette",icon: "📉" },
              ]} />
              <ChoiceRow label="Revêtement" value={cp.revetement} onChange={setCP("revetement")} options={[
                { v: "asphalte", l: "Asphalte",  icon: "🛣️" },
                { v: "beton",    l: "Béton",     icon: "🏗️" },
                { v: "pave",     l: "Pavés",     icon: "🧱" },
                { v: "gravier",  l: "Gravier",   icon: "🪨" },
                { v: "terre",    l: "Terre/Trail",icon: "🌿" },
              ]} />
              <ChoiceRow label="Virages" value={cp.virages} onChange={setCP("virages")} options={[
                { v: "larges",  l: "Larges / fluides", icon: "↗️" },
                { v: "mixtes",  l: "Mixtes",           icon: "↕️" },
                { v: "serres",  l: "Serrés / 180°",    icon: "↩️" },
              ]} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label="D+ (montées)" value={cp.denivele_plus} onChange={v => setCP("denivele_plus")(+v)} type="number" unit="m" />
                <Field label="D− (descentes)" value={cp.denivele_minus} onChange={v => setCP("denivele_minus")(+v)} type="number" unit="m" />
                <Field label="Altitude départ" value={cp.altitude} onChange={v => setCP("altitude")(+v)} type="number" unit="m" />
              </div>
            </div>
          </div>

          {/* Météo */}
          <div style={{ padding: "14px 16px", background: T.bg, borderRadius: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 14 }}>Conditions météo attendues</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <Label>Température prévue : <strong style={{ color: cp.temperature > 25 ? T.orange : cp.temperature > 20 ? "#E09030" : T.green }}>{cp.temperature}°C</strong></Label>
                <input type="range" min={-5} max={40} step={1} value={cp.temperature} onChange={e => setCP("temperature")(+e.target.value)}
                  style={{ width: "100%", marginTop: 8, accentColor: cp.temperature > 25 ? T.orange : T.navy }} />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.textLight }}>
                  <span>❄️ -5°C</span><span>🌤️ Optimal ≈ 10-15°C</span><span>🔥 40°C</span>
                </div>
              </div>
              <ChoiceRow label="Force du vent" value={cp.vent} onChange={setCP("vent")} options={[
                { v: "nul",      l: "Nul",       icon: "🌑" },
                { v: "leger",    l: "Léger",     icon: "🍃" },
                { v: "modere",   l: "Modéré",    icon: "💨" },
                { v: "fort",     l: "Fort",      icon: "🌬️" },
                { v: "tres_fort",l: "Très fort", icon: "🌀" },
              ]} />
              <ChoiceRow label="Orientation du vent" value={cp.orientation_vent} onChange={setCP("orientation_vent")} options={[
                { v: "face",     l: "De face",    icon: "🚧" },
                { v: "lateral",  l: "Latéral",    icon: "↔️" },
                { v: "dos",      l: "Dans le dos",icon: "✅" },
                { v: "variable", l: "Variable",   icon: "🔄" },
              ]} />
              <ChoiceRow label="Humidité" value={cp.humidite} onChange={setCP("humidite")} options={[
                { v: "seche",    l: "Sèche",   icon: "☀️" },
                { v: "normale",  l: "Normale", icon: "🌤️" },
                { v: "humide",   l: "Humide",  icon: "🌧️" },
              ]} />
            </div>
          </div>

          {/* Tags libres */}
          <div>
            <Label>Caractéristiques notables</Label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
              {TAGS_ROUTE.map(tag => (
                <div key={tag} onClick={() => toggleTag(tag)} style={{
                  padding: "5px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12,
                  background: cp.tags.includes(tag) ? `${T.navy}15` : T.bg,
                  border: `1px solid ${cp.tags.includes(tag) ? T.navy : T.border}`,
                  color: cp.tags.includes(tag) ? T.navy : T.textMid, fontWeight: 600,
                  transition: "all 0.12s",
                }}>{cp.tags.includes(tag) ? "✓ " : ""}{tag}</div>
              ))}
            </div>
          </div>

          <Field label="Notes sur le parcours (optionnel)" value={cp.note} onChange={setCP("note")} placeholder="ex: Montée km 9-12, vent habituel en bord de mer, revêtement irrégulier..." />

          {/* Aperçu correction */}
          {adjusted && (
            <div style={{ padding: "16px 18px", background: `${T.navy}08`, border: `1px solid ${T.navy}20`, borderRadius: 14 }}>
              <div style={{ fontWeight: 800, fontSize: 13, color: T.navy, marginBottom: 12 }}>⚙️ Impact estimé du parcours sur votre objectif</div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, padding: "10px 14px", background: T.surface, borderRadius: 10 }}>
                <span style={{ fontSize: 13, color: T.textMid }}>Objectif annoncé</span>
                <span style={{ fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"', fontWeight: 700, fontSize: 16, color: T.navy }}>{form.objectif}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: T.surface, borderRadius: 10 }}>
                <span style={{ fontSize: 13, color: T.textMid }}>Temps réaliste corrigé</span>
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"', fontWeight: 700, fontSize: 18, color: adjusted.diffColor === T.red ? T.orange : T.green }}>{adjusted.adjTime}</span>
                  <span style={{ fontSize: 12, color: adjusted.diffColor, marginLeft: 8 }}>({adjusted.diffText})</span>
                </div>
              </div>
              <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
                {details.filter(d => Math.abs(d.delta) >= 0.05).map(d => (
                  <span key={d.label} style={{
                    fontSize: 11, padding: "3px 8px", borderRadius: 8,
                    background: d.delta > 0 ? `${T.orange}12` : d.delta < 0 ? `${T.green}12` : T.bg,
                    color: d.delta > 0 ? T.orange : d.delta < 0 ? T.green : T.textLight,
                    border: `1px solid ${d.delta > 0 ? T.orange : d.delta < 0 ? T.green : T.border}33`,
                    fontWeight: 600,
                  }}>
                    {d.icon} {d.label} {d.delta > 0 ? "+" : ""}{d.delta.toFixed(1)}%
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* GPX */}
          <div>
            <Label>Fichier GPX du parcours <span style={{ color: T.orange, fontSize: 11 }}>obligatoire pour valider</span></Label>
            <div style={{ marginTop: 8 }}>
              {gpxFile ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: `${T.green}12`, borderRadius: 10, border: `1px solid ${T.green}40` }}>
                  <span style={{ fontSize: 18 }}>📄</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.green }}>{gpxFile.fileName}</div>
                    {gpxFile.distance && <div style={{ fontSize: 11, color: T.textMid, fontFamily: T.fontData }}>{gpxFile.distance} km · D+ {gpxFile.elevGain}m · D- {gpxFile.elevLoss}m</div>}
                  </div>
                  <button onClick={() => setGpxFile(null)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textLight, fontSize: 16 }}>✕</button>
                </div>
              ) : (
                <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: T.bg, borderRadius: 10, border: `1.5px dashed ${T.border}`, cursor: "pointer" }}>
                  <span style={{ fontSize: 20 }}>📁</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{gpxParsing ? "⏳ Lecture…" : "Importer le GPX de la course"}</div>
                    <div style={{ fontSize: 11, color: T.textMid }}>Sans GPX la course sera enregistrée en brouillon</div>
                  </div>
                  <input type="file" accept=".gpx" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) handleGpxUpload(e.target.files[0]); }} />
                </label>
              )}
              {gpxError && <div style={{ fontSize: 12, color: T.red || T.orange, marginTop: 6 }}>{gpxError}</div>}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "space-between", paddingTop: 4, borderTop: `1px solid ${T.border}` }}>
            <div style={{display:"flex",gap:8}}>
              <Btn variant="ghost" onClick={() => setStep(1)}>← Retour</Btn>
              {isEditRace && onDelete && <Btn variant="danger" onClick={()=>{if(window.confirm("Supprimer cette course ?"))onDelete(form.id);onClose();}}>Supprimer</Btn>}
            </div>
            <Btn variant="lime" onClick={() => {
              const raceToSave = {
                ...form,
                id: form.id||Date.now(),
                isTrail: false,
                distance: distNum,
                splits: form.splits||[],
                nutrition: form.nutrition||[],
                tapering: form.tapering||[],
                gpxData: gpxFile || null,
                statut: gpxFile ? (form.statut || "inscrit") : "brouillon",
              };
              onAdd(raceToSave);
              onClose();
            }}>{isEditRace ? "Enregistrer" : "Ajouter la course"}</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   NAVIGATION
═══════════════════════════════════════════════════════════════════════════ */
const NAV = [
  { id: "dashboard",  label: "Tableau de bord",  icon: "⊞" },
  { id: "runs",       label: "Entraînements",    icon: "🏃" },
  { id: "charge",     label: "Charge & PMC",     icon: "📊" },
  { id: "planning",   label: "Planning",         icon: "📅" },
  { id: "predictions",label: "Prédictions",      icon: "🎯" },
  { id: "racebook",   label: "Race Book",        icon: "📖" },
  { id: "raceplan",   label: "Race Plan",        icon: "📋" },
  { id: "coach",      label: "Mes séances",       icon: "⚡" },
  { id: "profile",    label: "Profil",           icon: "👤" },
];

// Mobile bottom bar (4 primary tabs) — rest goes into the "Plus" drawer
const BOTTOM_NAV_IDS = ["dashboard", "runs", "racebook", "coach"];
const BOTTOM_NAV = NAV.filter(n => BOTTOM_NAV_IDS.includes(n.id)).map(n => ({
  ...n,
  label: { dashboard: "Home", runs: "Séances", racebook: "Book", coach: "Coach" }[n.id],
}));
const DRAWER_NAV = NAV.filter(n => !BOTTOM_NAV_IDS.includes(n.id));

/* ═══════════════════════════════════════════════════════════════════════════
   TAB: CHARGE & ACWR
═══════════════════════════════════════════════════════════════════════════ */
function AcwrGauge({ value }) {
  const pct = Math.min(Math.max((value - 0.5) / 1.2, 0), 1);
  const angle = -135 + pct * 270;
  const color = acwrColor(value);
  const [label] = acwrLabel(value);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <svg width="160" height="100" viewBox="0 0 160 100">
        <defs>
          <linearGradient id="gauge-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={T.blue} />
            <stop offset="38%" stopColor={T.green} />
            <stop offset="70%" stopColor={T.orange} />
            <stop offset="100%" stopColor={T.red} />
          </linearGradient>
        </defs>
        <path d="M 20 90 A 60 60 0 0 1 140 90" fill="none" stroke={T.border} strokeWidth="14" strokeLinecap="round"/>
        <path d="M 20 90 A 60 60 0 0 1 140 90" fill="none" stroke="url(#gauge-grad)" strokeWidth="14" strokeLinecap="round" opacity="0.85"/>
        <g transform={`rotate(${angle}, 80, 90)`}>
          <line x1="80" y1="90" x2="80" y2="40" stroke={color} strokeWidth="3" strokeLinecap="round"/>
          <circle cx="80" cy="90" r="6" fill={color}/>
        </g>
        <text x="80" y="78" textAnchor="middle" fill={color} fontSize="24" fontWeight="500" fontFamily="Barlow Condensed, sans-serif">{value.toFixed(2)}</text>
        <text x="18" y="100" fill={T.textLight} fontSize="9">0.5</text>
        <text x="130" y="100" fill={T.textLight} fontSize="9">1.7</text>
      </svg>
      <div style={{ fontSize: 13, fontWeight: 700, color }}>{label}</div>
      <div style={{ display: "flex", gap: 10, fontSize: 11 }}>
        {[[T.blue,"Sous-charge"],[T.green,"Optimal"],[T.orange,"Surcharge"],[T.red,"Danger"]].map(([c,l]) => (
          <span key={l} style={{ color: T.textLight }}><span style={{ color: c }}>■</span> {l}</span>
        ))}
      </div>
    </div>
  );
}

function TabCharge({ runs, profile }) {
  const acwr = calcACWR(runs);
  const weeklyStats = getWeeklyStats(runs);
  const chronic = weeklyStats.length >= 4 ? Math.round(weeklyStats.slice(-4).reduce((s,w)=>s+w.trimp,0)/4) : 0;
  const acute = weeklyStats.length ? weeklyStats[weeklyStats.length-1].trimp : 0;
  const { monotony, strain } = calcMonotonyStrain(runs);
  const monoColor = monotony > 2 ? T.red : monotony > 1.5 ? T.orange : T.green;
  const strainColor = strain > 3500 ? T.red : strain > 2000 ? T.orange : T.green;

  const pmcFull = calcPMC(runs, 90);
  const pmcDisplay = pmcFull.slice(-60).filter((_, i) => i % 2 === 0);
  const lastPMC = pmcFull[pmcFull.length - 1] || { ctl: 0, atl: 0, tsb: 0 };
  const tsbColor = lastPMC.tsb >= 5 ? T.green : lastPMC.tsb >= -10 ? T.blue : lastPMC.tsb >= -25 ? T.orange : T.red;
  const tsbLabel = lastPMC.tsb >= 5 ? "En forme 🟢" : lastPMC.tsb >= -10 ? "Équilibré" : lastPMC.tsb >= -25 ? "Fatigué ⚠️" : "Très fatigué 🔴";

  const efData = runs.filter(r => r.fc_moy && r.distance && r.duree && r.type !== "fractionne").map(r => ({
    date: fmtDate(r.date, { day:"2-digit", month:"short" }),
    ef: calcEF(r.distance, r.duree, r.fc_moy),
    type: r.type,
  })).filter(e => e.ef).slice(-20);

  const scatterData = runs.filter(r => r.fc_moy && r.duree && r.distance).map(r => ({
    fc: r.fc_moy, allure: +(r.duree / r.distance).toFixed(2),
    type: r.type, label: fmtDate(r.date), dist: r.distance,
    fill: getTypeInfo(r.type).color,
  }));

  const recent = runs.filter(r => daysUntil(r.date) > -28 && daysUntil(r.date) <= 0);
  const zonesDist = (() => {
    const fcMax = profile.fc_max || 192, fcR = profile.fc_repos || 52;
    const zones = [{ z:"Z1",c:T.blue,min:0,max:.6,km:0},{ z:"Z2",c:T.green,min:.6,max:.7,km:0},{ z:"Z3",c:"#CA8A04",min:.7,max:.8,km:0},{ z:"Z4",c:T.orange,min:.8,max:.9,km:0},{ z:"Z5",c:T.red,min:.9,max:1.1,km:0}];
    recent.forEach(r => {
      const hrr = (r.fc_moy - fcR) / (fcMax - fcR);
      const z = zones.find(z => hrr >= z.min && hrr < z.max);
      if (z) z.km += r.distance;
    });
    const total = zones.reduce((s,z)=>s+z.km,0)||1;
    return zones.map(z=>({ ...z, km:+z.km.toFixed(1), pct:Math.round(z.km/total*100) }));
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* KPI row — 6 cols */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 10 }}>
        {[
          { l:<InfoTip term="ACWR">ACWR</InfoTip>,           v: acwr.toFixed(2),  s: acwrLabel(acwr)[0],   c: acwrColor(acwr) },
          { l:<InfoTip term="CTL">CTL (Fitness)</InfoTip>,   v: lastPMC.ctl.toFixed(0), s: "charge chronique 42j", c: T.blue },
          { l:<InfoTip term="ATL">ATL (Fatigue)</InfoTip>,   v: lastPMC.atl.toFixed(0), s: "charge aiguë 7j",      c: T.orange },
          { l:<InfoTip term="TSB">TSB (Forme)</InfoTip>,     v: lastPMC.tsb > 0 ? `+${lastPMC.tsb.toFixed(0)}` : lastPMC.tsb.toFixed(0), s: tsbLabel, c: tsbColor },
          { l:<InfoTip term="Monotonie">Monotonie</InfoTip>, v: monotony,         s: monotony > 2 ? "⚠️ Risque" : "Normal", c: monoColor },
          { l:<InfoTip term="Strain">Strain 7j</InfoTip>,    v: strain,           s: strain > 3500 ? "⚠️ Élevé" : "Acceptable", c: strainColor },
        ].map((s,i) => (
          <Card key={i}><Label>{s.l}</Label>
            <div style={{ fontSize:22, fontWeight:700, color:s.c, fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"', margin:"6px 0 4px" }}>{s.v}</div>
            <div style={{ fontSize:11, color:T.textLight }}>{s.s}</div>
          </Card>
        ))}
      </div>

      {/* PMC */}
      <Card>
        <SectionHead title={<><InfoTip term="PMC">Performance Management Chart</InfoTip> — <InfoTip term="CTL">CTL</InfoTip> / <InfoTip term="ATL">ATL</InfoTip> / <InfoTip term="TSB">TSB</InfoTip></>} />
        <div style={{ fontSize:12, color:T.textLight, marginBottom:12 }}>
          Bleu = Fitness (CTL 42j) · Orange tirets = Fatigue (ATL 7j) · Barres = Forme (TSB). <strong>Jour de course :</strong> TSB cible entre +5 et +25.
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={pmcDisplay}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
            <XAxis dataKey="label" tick={{ fill:T.textLight, fontSize:9 }} axisLine={false} tickLine={false} interval={6}/>
            <YAxis yAxisId="load" tick={{ fill:T.textLight, fontSize:10 }} axisLine={false} tickLine={false}/>
            <YAxis yAxisId="tsb" orientation="right" tick={{ fill:T.textLight, fontSize:10 }} axisLine={false} tickLine={false}/>
            <Tooltip content={<CustomTooltip/>}/>
            <ReferenceLine yAxisId="tsb" y={0} stroke={T.border} strokeWidth={1}/>
            <ReferenceLine yAxisId="tsb" y={5} stroke={T.green} strokeDasharray="3 2" label={{ value:"+5 (Forme)", fill:T.green, fontSize:9, position:"insideTopRight" }}/>
            <ReferenceLine yAxisId="tsb" y={-25} stroke={T.red} strokeDasharray="3 2" label={{ value:"-25 (Danger)", fill:T.red, fontSize:9, position:"insideBottomRight" }}/>
            <Area yAxisId="load" type="monotone" dataKey="ctl" name="CTL (Fitness)" stroke={T.blue} fill={`${T.blue}18`} strokeWidth={2.5} dot={false}/>
            <Line yAxisId="load" type="monotone" dataKey="atl" name="ATL (Fatigue)" stroke={T.orange} strokeWidth={2} dot={false} strokeDasharray="5 2"/>
            <Bar yAxisId="tsb" dataKey="tsb" name="TSB (Forme)" radius={[3,3,0,0]} barSize={6}>
              {pmcDisplay.map((p,i) => <Cell key={i} fill={p.tsb >= 0 ? T.green : T.red} opacity={0.7}/>)}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      {/* ACWR gauge + TRIMP bars */}
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 16 }}>
        <Card style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:28, minWidth:260 }}>
          <SectionHead title={<><InfoTip term="ACWR">Rapport Aigu/Chronique (ACWR)</InfoTip></>} />
          <AcwrGauge value={acwr} />
          <div style={{ marginTop:16, width:"100%", display:"flex", flexDirection:"column", gap:6 }}>
            {[["Charge aiguë (7j)", acute, T.orange], ["Charge chronique (4S moy.)", chronic, T.blue], ["Ratio ACWR", acwr.toFixed(2), acwrColor(acwr)]].map(([k,v,c])=>(
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${T.border}` }}>
                <span style={{ fontSize:12, color:T.textMid }}>{k}</span>
                <span style={{ fontSize:13, fontWeight:800, color:c, fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{v}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <SectionHead title={<><InfoTip term="TRIMP">TRIMP</InfoTip> hebdomadaire — charge d'entraînement</>} />
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={weeklyStats}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="label" tick={{ fill:T.textLight, fontSize:11 }} axisLine={false} tickLine={false}/>
              <YAxis tick={{ fill:T.textLight, fontSize:11 }} axisLine={false} tickLine={false}/>
              <Tooltip content={<CustomTooltip/>}/>
              <ReferenceLine y={chronic} stroke={T.blue} strokeDasharray="5 3" label={{ value:`Chronique: ${chronic}`, fill:T.blue, fontSize:11, position:"insideTopRight" }}/>
              <Bar dataKey="trimp" name="TRIMP" radius={[6,6,0,0]}>
                {weeklyStats.map((w,i) => <Cell key={i} fill={w.trimp > chronic*1.3 ? T.orange : w.trimp > chronic ? T.green : T.blue}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))", gap:8, marginTop:12 }}>
            {weeklyStats.map(w => (
              <div key={w.label} style={{ background:T.bg, borderRadius:10, padding:"10px 12px", textAlign:"center" }}>
                <div style={{ fontSize:11, color:T.textLight }}>{w.label}</div>
                <div style={{ fontSize:20, fontWeight:700, color: w.trimp>chronic*1.3?T.orange:w.trimp>chronic?T.green:T.blue, fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{w.trimp}</div>
                <div style={{ fontSize:10, color:T.textLight }}>TRIMP</div>
                <div style={{ fontSize:10, color:T.textMid }}>{w.km}km · {w.sessions}s</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* EF trend */}
      <Card>
        <SectionHead title={<>📈 <InfoTip term="EF">Efficiency Factor</InfoTip> — économie de course (vitesse m/min ÷ FC moy)</>} />
        <div style={{ fontSize:12, color:T.textLight, marginBottom:12 }}>
          Un EF croissant sur des sorties comparables = amélioration de l'efficacité aérobie. Calculé sur séances non-fractionnées avec FC.
        </div>
        {efData.length < 3
          ? <EmptyState icon="📈" title="Données insuffisantes" desc="Enregistrez plus de séances avec FC moyenne pour voir votre progression EF" />
          : <ResponsiveContainer width="100%" height={160}>
              <ComposedChart data={efData}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
                <XAxis dataKey="date" tick={{ fill:T.textLight, fontSize:10 }} axisLine={false} tickLine={false}/>
                <YAxis domain={["auto","auto"]} tick={{ fill:T.textLight, fontSize:10 }} axisLine={false} tickLine={false}/>
                <Tooltip content={<CustomTooltip/>}/>
                <Area type="monotone" dataKey="ef" name="EF" stroke={T.purple} fill={`${T.purple}15`} strokeWidth={2.5} dot={{ fill:T.purple, r:4 }}/>
              </ComposedChart>
            </ResponsiveContainer>
        }
      </Card>

      {/* FC / Allure scatter */}
      <Card>
        <SectionHead title={<>Efficacité : <InfoTip term="FC moy">FC moyenne</InfoTip> vs <InfoTip term="Allure">Allure</InfoTip> (toutes sessions)</>} />
        <div style={{ fontSize:12, color:T.textLight, marginBottom:12 }}>
          Axe X = fréquence cardiaque moyenne · Axe Y = allure min/km (inversé). <strong>Idéal :</strong> bas-gauche = rapide à FC basse.
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <ScatterChart margin={{ top:5, right:20, bottom:20, left:0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
            <XAxis dataKey="fc" name="FC moy" unit=" bpm" type="number" domain={[115,185]} tick={{ fill:T.textLight, fontSize:11 }} label={{ value:"FC moyenne (bpm)", position:"insideBottom", offset:-12, fill:T.textLight, fontSize:11 }}/>
            <YAxis dataKey="allure" name="Allure" type="number" domain={[3.5,8]} reversed tickFormatter={v=>`${Math.floor(v)}:${String(Math.round((v%1)*60)).padStart(2,"0")}`} tick={{ fill:T.textLight, fontSize:11 }} label={{ value:"min/km", angle:-90, position:"insideLeft", fill:T.textLight, fontSize:11 }}/>
            <Tooltip content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0]?.payload;
              const a = d?.allure || 0;
              return <div style={{ background:T.navy, borderRadius:10, padding:"10px 14px" }}>
                <div style={{ color:"#aaa", fontSize:11, marginBottom:4 }}>{d?.label}</div>
                <div style={{ color:"#fff", fontSize:13, fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>FC: <strong style={{ color:T.orange }}>{d?.fc} bpm</strong></div>
                <div style={{ color:"#fff", fontSize:13 }}>Allure: <strong style={{ color:T.blue }}>{Math.floor(a)}:{String(Math.round((a%1)*60)).padStart(2,"0")}/km</strong></div>
                <div style={{ color:"#fff", fontSize:13 }}>Type: <strong style={{ color:getTypeInfo(d?.type)?.color }}>{getTypeInfo(d?.type)?.label}</strong></div>
                <div style={{ color:"#aaa", fontSize:11 }}>{d?.dist} km</div>
              </div>;
            }}/>
            <Scatter data={scatterData} name="Sessions">
              {scatterData.map((e,i) => <Cell key={i} fill={e.fill} opacity={0.85}/>)}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
        <div style={{ display:"flex", gap:14, flexWrap:"wrap", marginTop:8 }}>
          {RUN_TYPES.map(t => <div key={t.id} style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, color:T.textLight }}><div style={{ width:8, height:8, borderRadius:"50%", background:t.color }}/>{t.label}</div>)}
        </div>
      </Card>

      {/* Zone distribution */}
      <Card>
        <SectionHead title={<>Répartition des <InfoTip term="Zones FC">zones FC</InfoTip> — 28 derniers jours</>} />
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {zonesDist.map(z => (
            <div key={z.z} style={{ display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:28, fontSize:12, fontWeight:700, color:z.c }}>{z.z}</div>
              <div style={{ flex:1, height:26, background:T.bg, borderRadius:6, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${z.pct}%`, background:z.c, display:"flex", alignItems:"center", paddingLeft:8, transition:"width 0.5s ease" }}>
                  {z.pct > 8 && <span style={{ fontSize:11, fontWeight:700, color:"#fff" }}>{z.pct}%</span>}
                </div>
              </div>
              <div style={{ width:60, textAlign:"right", fontSize:12, color:T.textMid, fontWeight:500, fontFamily:T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{z.km} km</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop:14, background:T.bg, borderRadius:10, padding:"10px 14px", fontSize:12, color:T.textMid }}>
          💡 <strong>Règle des 80/20 :</strong> idéalement 80% en Z1-Z2 et 20% en Z3-Z5. Ratio Z1-Z2 actuel : <strong style={{ color:T.green }}>{zonesDist[0].pct + zonesDist[1].pct}%</strong>
        </div>
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ROOT APP
═══════════════════════════════════════════════════════════════════════════ */
// ── localStorage helpers ──────────────────────────────────────────────────
const LS = {
  get: (key, fallback) => {
    try {
      const v = localStorage.getItem("strata_" + key);
      if (!v) return fallback;
      const parsed = JSON.parse(v);
      // Validation basique : arrays doivent être des arrays, objets des objets
      if (Array.isArray(fallback) && !Array.isArray(parsed)) return fallback;
      if (typeof fallback === "object" && !Array.isArray(fallback) && typeof parsed !== "object") return fallback;
      return parsed;
    } catch { return fallback; }
  },
  set: (key, value) => {
    try { localStorage.setItem("strata_" + key, JSON.stringify(value)); } catch {}
  },
  clear: () => {
    ["profile","runs","races"].forEach(k =>
      localStorage.removeItem("strata_" + k)
    );
  },
};

// Nouveaux utilisateurs : profil vide, pas onboardé → onboarding s'affiche
const EMPTY_PROFILE = { onboarded: false };

// Error boundary pour données corrompues
class AppErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:T.bg, fontFamily: T.fontUI }}>
        <div style={{ background:"#fff", borderRadius:12, padding:40, maxWidth:480, textAlign:"center", boxShadow:"0 4px 24px rgba(0,0,0,0.1)" }}>
          <div style={{ fontSize:48, marginBottom:16 }}>⚠️</div>
          <h2 style={{ fontSize:20, fontWeight:700, marginBottom:12 }}>Données corrompues détectées</h2>
          <p style={{ color:"#666", fontSize:14, lineHeight:1.6, marginBottom:24 }}>
            Une erreur s&apos;est produite au chargement. Cela arrive parfois après une mise à jour.
            Vous pouvez réinitialiser les données locales pour repartir proprement.
          </p>
          <button onClick={() => { LS.clear(); window.location.reload(); }}
            style={{ background:"#C49A3C", color:"#1A1510", border:"none", borderRadius:8, padding:"12px 24px", fontSize:14, fontWeight:700, cursor:"pointer" }}>
            Réinitialiser GRAVV
          </button>
        </div>
      </div>
    );
    return this.props.children;
  }
}

// ── Login Screen ───────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const signIn = async (provider) => {
    if (!supabase) { setErr("Supabase non configuré."); return; }
    setLoading(true); setErr(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin },
    });
    if (error) { setErr(error.message); setLoading(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: T.fontUI }}>
      <div style={{ width: 380, padding: "40px 36px", background: T.surface, borderRadius: 16, border: `0.5px solid ${T.border}`, boxShadow: T.shadowLg }}>

        {/* Brand GRAVV — Login Screen */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 32 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: "#1A1510", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="20" height="24" viewBox="0 0 22 26" fill="none">
              <path d="M2 2 L11 13 L20 2" stroke="#F2ECD8" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12 L11 23 L20 12" stroke="#C49A3C" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 22, letterSpacing: "0.02em", color: "#1A1510", lineHeight: 1 }}>GRAVV</div>
            <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 9, color: "#B0A090", letterSpacing: "0.14em", textTransform: "uppercase", marginTop: 2 }}>Performance Analytics</div>
          </div>
        </div>

        <div style={{ fontSize: 20, fontWeight: 600, color: T.text, marginBottom: 6 }}>Bienvenue</div>
        <div style={{ fontSize: 13, color: T.textMid, marginBottom: 28, lineHeight: 1.6 }}>
          Connecte-toi pour accéder à tes données sur tous tes appareils.
        </div>

        {/* Boutons OAuth */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button onClick={() => signIn("google")} disabled={loading} style={{
            display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
            background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 8,
            cursor: loading ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 500,
            color: T.text, fontFamily: T.fontUI, transition: "all 0.15s",
            boxShadow: T.shadow, opacity: loading ? 0.6 : 1,
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = T.gold}
            onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuer avec Google
          </button>

          <button onClick={() => signIn("azure")} disabled={loading} style={{
            display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
            background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 8,
            cursor: loading ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 500,
            color: T.text, fontFamily: T.fontUI, transition: "all 0.15s",
            boxShadow: T.shadow, opacity: loading ? 0.6 : 1,
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = "#0078D4"}
            onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
            <svg width="18" height="18" viewBox="0 0 23 23">
              <rect x="0"  y="0"  width="11" height="11" fill="#F25022"/>
              <rect x="12" y="0"  width="11" height="11" fill="#7FBA00"/>
              <rect x="0"  y="12" width="11" height="11" fill="#00A4EF"/>
              <rect x="12" y="12" width="11" height="11" fill="#FFB900"/>
            </svg>
            Continuer avec Microsoft
          </button>
        </div>

        {err && (
          <div style={{ marginTop: 16, padding: "10px 14px", background: T.orangeLight, borderRadius: 6, fontSize: 12, color: T.orange, border: `0.5px solid ${T.orange}44` }}>
            {err}
          </div>
        )}

        <div style={{ marginTop: 24, fontSize: 11, color: T.textLight, textAlign: "center", lineHeight: 1.6 }}>
          Tes données sont privées et chiffrées.{" "}
          <br/>Aucune publicité, aucun partage à des tiers.
        </div>
      </div>
    </div>
  );
}

// ── Auth wrapper ────────────────────────────────────────────────────────────
const STRAVA_CLIENT_ID = "221959";
const SUPABASE_FUNCTIONS_URL = "https://oigjfywqnfengquyqajy.supabase.co/functions/v1";

// Note: Strava ?code= interception is handled in supabaseClient.js (before createClient runs).

const WrappedApp = () => {
  const [session,     setSession]     = useState(null);
  const [authLoading, setAuthLoading] = useState(!!supabase);
  // Pick up code stored by the synchronous interceptor above
  const [stravaCode,  setStravaCode]  = useState(() => {
    const code = sessionStorage.getItem("strava_pending_code") || null;
    if (code) sessionStorage.removeItem("strava_pending_code");
    return code;
  });

  useEffect(() => {
    if (!supabase) { setAuthLoading(false); return; }
    // Register listener BEFORE getSession so no auth event is missed
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setAuthLoading(false);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    });
    return () => subscription.unsubscribe();
  }, []);

  if (authLoading) return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontFamily: T.fontUI, color: T.textLight, fontSize: 13 }}>Chargement…</div>
    </div>
  );

  // Sans Supabase configuré → mode local direct
  if (!supabase) return (
    <AppErrorBoundary><App session={null} /></AppErrorBoundary>
  );

  // Non connecté → écran de login
  if (!session) return <LoginScreen />;

  // Connecté → app complète
  return (
    <AppErrorBoundary><App session={session} stravaCode={stravaCode} onStravaCodeUsed={() => setStravaCode(null)} /></AppErrorBoundary>
  );
};

export default WrappedApp;

function App({ session, stravaCode, onStravaCodeUsed }) {
  const userId = session?.user?.id || null;
  const [dbLoading, setDbLoading] = useState(!!userId);

  // ── État principal — Supabase est la source de vérité ─────────────────────
  const [profile,   setProfileRaw]  = useState(EMPTY_PROFILE);
  const [runs,      setRunsRaw]     = useState([]);
  const [races,     setRacesRaw]    = useState([]);
  const [tab,    setTab]      = useState("dashboard");
  const [modal,  setModal]    = useState(null);
  const [editItem, setEditItem]     = useState(null);
  const [trailPlanRace, setTrailPlanRace] = useState(null);
  const [toast,  setToast]    = useState(null);
  const [isMobile,   setIsMobile]   = useState(() => window.innerWidth < 768);
  const [showDrawer, setShowDrawer] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const h  = e => setIsMobile(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [predState, setPredStateRaw] = useState(() => LS.get("predState", {}));
  const setPredState = (v) => {
    const val = typeof v === "function" ? v(predState) : v;
    LS.set("predState", val);
    setPredStateRaw(val);
  };

  // ── Strava ────────────────────────────────────────────────────────────────
  const [stravaStatus, setStravaStatus] = useState("idle"); // idle | loading | syncing | connected | error
  const [stravaMsg,    setStravaMsg]    = useState("");
  const stravaConnected = !!(profile.strava_access_token);

  const stravaCallEdge = useCallback(async (body) => {
    if (!supabase || !session) throw new Error("Not authenticated");
    const { data: { session: s } } = await supabase.auth.getSession();
    const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/strava-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${s.access_token}`,
      },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (json.error) throw new Error(json.error);
    return json;
  }, [session]);

  // Handle Strava OAuth callback code.
  // Guard on dbLoading: ensures profile is loaded before we update it,
  // preventing EMPTY_PROFILE from overwriting real data in Supabase.
  useEffect(() => {
    if (!stravaCode || !session || dbLoading) return;
    (async () => {
      setStravaStatus("loading");
      setStravaMsg("Connexion à Strava…");
      try {
        const tokens = await stravaCallEdge({ action: "exchange", code: stravaCode });
        // Only update strava-specific fields — never spread the full profile
        // to avoid accidentally writing EMPTY_PROFILE values to the DB.
        const stravaFields = {
          strava_access_token:  tokens.access_token,
          strava_refresh_token: tokens.refresh_token,
          strava_token_expires: tokens.expires_at,
          strava_athlete_id:    tokens.athlete?.id ?? null,
        };
        setProfileRaw(prev => ({ ...prev, ...stravaFields }));
        if (userId && db) db.updateStravaTokens(userId, stravaFields);
        setStravaStatus("connected");
        setStravaMsg(`Connecté en tant que ${tokens.athlete?.firstname || "athlète"} ! Clique sur Synchroniser pour importer tes activités.`);
        onStravaCodeUsed?.();
      } catch(e) {
        setStravaStatus("error");
        setStravaMsg(e.message);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stravaCode, session, dbLoading]);

  const getValidStravaToken = useCallback(async () => {
    const now = Math.floor(Date.now() / 1000);
    if (profile.strava_token_expires && profile.strava_token_expires > now + 60) {
      return profile.strava_access_token;
    }
    // Refresh
    const tokens = await stravaCallEdge({ action: "refresh", refresh_token: profile.strava_refresh_token });
    const updated = {
      ...profile,
      strava_access_token:  tokens.access_token,
      strava_refresh_token: tokens.refresh_token,
      strava_token_expires: tokens.expires_at,
    };
    setProfile(updated);
    return tokens.access_token;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, stravaCallEdge]);

  const syncStravaActivities = useCallback(async (accessToken, refreshToken, expiresAt) => {
    setStravaStatus("syncing");
    setStravaMsg("Synchronisation des activités…");
    try {
      await withRateLimit('sync_strava', async () => {}); // rate limit gate
      // Use provided token or get valid one
      let token = accessToken;
      if (!token) token = await getValidStravaToken();

      // Find the most recent strava activity already imported, to only fetch newer ones
      const latestStravaRun = runs.filter(r => r.strava_id).sort((a,b) => b.date > a.date ? 1 : -1)[0];
      const afterTs = latestStravaRun
        ? Math.floor(parseLocalDate(latestStravaRun.date).getTime() / 1000)
        : 0;

      // Paginate all activities from Strava (max 100 per page)
      let allActivities = [];
      let page = 1;
      while (true) {
        const url = `https://www.strava.com/api/v3/athlete/activities?per_page=100&page=${page}${afterTs ? `&after=${afterTs}` : ""}`;
        const res = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
        if (!res.ok) throw new Error(`Strava API ${res.status}`);
        const batch = await res.json();
        if (!Array.isArray(batch) || batch.length === 0) break;
        allActivities = allActivities.concat(batch);
        if (batch.length < 100) break; // last page
        page++;
      }
      const activities = allActivities;

      // Map Strava activity types → GRAVV types
      const TYPE_MAP = {
        Run: "fond", TrailRun: "trail", VirtualRun: "fond",
        Ride: "velo", VirtualRide: "velo", MountainBikeRide: "vtt",
        Walk: "marche", Hike: "marche",
        Swim: "nage", OpenWaterSwim: "open_water",
        WeightTraining: "muscu", Yoga: "yoga", Workout: "muscu",
      };
      const STRAVA_TYPES_TO_IMPORT = Object.keys(TYPE_MAP);
      // Stringify both sides: Supabase returns bigint strava_id as string,
      // but Strava API returns a.id as Number — strict equality would miss all.
      const existing = new Set(runs.map(r => String(r.strava_id)).filter(Boolean));
      const newRuns = activities
        .filter(a => STRAVA_TYPES_TO_IMPORT.includes(a.type) && !existing.has(String(a.id)))
        .map(a => ({
          id: Date.now() + Math.random(),
          strava_id:  a.id,
          date:       a.start_date_local?.slice(0, 10) || "",
          distance:   +(a.distance / 1000).toFixed(2),
          duree:      Math.round(a.moving_time / 60),
          fc_moy:     a.average_heartrate ? Math.round(a.average_heartrate) : 0,
          fc_max:     a.max_heartrate ? Math.round(a.max_heartrate) : 0,
          denivele:   a.total_elevation_gain ? Math.round(a.total_elevation_gain) : 0,
          nom:        a.name || "",
          type:       TYPE_MAP[a.type] || "fond",
          ressenti:   3,
          trimp:      0,
          note:       `Importé depuis Strava · ${a.type}`,
        }));

      // Enrich TRIMP
      const enriched = newRuns.map(r => ({
        ...r,
        trimp: calcTrimp(r.duree, r.fc_moy, profile.fc_repos||52, profile.fc_max||192, r.type),
      }));

      if (enriched.length === 0) {
        setStravaStatus("connected");
        setStravaMsg("Déjà à jour — aucune nouvelle activité.");
        return;
      }

      // Save to Supabase if logged in
      const saved = [];
      if (userId && db) {
        for (const r of enriched) {
          const s = await db.addRun(userId, r);
          saved.push(s ? { ...r, id: s.id } : r);
        }
      } else {
        saved.push(...enriched);
      }

      setRuns(prev => [...prev, ...saved].sort((a,b) => a.date > b.date ? 1 : -1));
      setStravaStatus("connected");
      setStravaMsg(`${enriched.length} activité${enriched.length > 1 ? "s" : ""} importée${enriched.length > 1 ? "s" : ""} depuis Strava !`);
    } catch(e) {
      setStravaStatus("error");
      setStravaMsg(e.message);
    }
  }, [getValidStravaToken, runs, userId, profile]); // eslint-disable-line

  // ── Chargement Supabase au login ──────────────────────────────────────────
  useEffect(() => {
    if (!userId || !db) { setDbLoading(false); return; }
    (async () => {
      setDbLoading(true);
      try {
        const [prof, runsData, racesData] = await Promise.all([
          db.getProfile(userId),
          db.getRuns(userId),
          db.getRaces(userId),
        ]);
        if (prof) {
          // Auto-heal: if user has any sign of a real profile, treat as onboarded.
          // Covers corrupted profiles (prenom wiped by stale EMPTY_PROFILE write)
          // — strava_athlete_id or a non-default fc_max are reliable signals.
          const alreadyOnboarded = prof.onboarded || !!(prof.prenom)
            || !!(prof.strava_athlete_id) || (prof.fc_max && prof.fc_max !== 192);
          if (alreadyOnboarded && !prof.onboarded) {
            // Fix the flag in DB silently
            supabase.from("profiles").update({ onboarded: true }).eq("id", userId);
          }
          const mapped = {
            prenom: prof.prenom, nom: prof.nom, age: prof.age,
            sexe: prof.sexe, taille: prof.taille, poids: prof.poids,
            masseGraisseuse: prof.masse_graisseuse,
            travail: prof.travail, stress: prof.stress, sommeil_cible: prof.sommeil_cible,
            fc_max: prof.fc_max, fc_repos: prof.fc_repos, vma: prof.vma,
            objectifPrincipal: prof.objectif_principal,
            objectifChrono: prof.objectif_chrono,
            objectifDate: prof.objectif_date,
            volumeCible: prof.volume_cible,
            experienceCourse: prof.experience_course,
            niveauActivite: prof.niveau_activite,
            onboarded: alreadyOnboarded,
            // Strava tokens
            strava_athlete_id:    prof.strava_athlete_id,
            strava_access_token:  prof.strava_access_token,
            strava_refresh_token: prof.strava_refresh_token,
            strava_token_expires: prof.strava_token_expires,
          };
          setProfileRaw(mapped);
        }
        setRunsRaw(runsData || []);
        setRacesRaw(racesData || []);
      } catch(e) {
        console.error("Supabase load error:", e);
      }
      setDbLoading(false);
    })();
  }, [userId]);

  // ── Wrappers état — Supabase uniquement, pas de localStorage ────────────
  const setProfile = (v) => {
    const val = typeof v === "function" ? v(profile) : v;
    setProfileRaw(val);
    if (userId && db) db.upsertProfile(userId, val);
  };
  const setRuns      = (v) => setRunsRaw(typeof v === "function" ? v(runs) : v);
  const setRaces     = (v) => setRacesRaw(typeof v === "function" ? v(races) : v);

  // ── Loading screen ────────────────────────────────────────────────────────
  if (dbLoading) return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, fontFamily: T.fontUI }}>
      <div style={{ width: 36, height: 36, borderRadius: 9, background: "#C49A3C", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg width="18" height="22" viewBox="0 0 22 26" fill="none">
          <path d="M2 2 L11 13 L20 2" stroke="#1A1510" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 12 L11 23 L20 12" stroke="#C49A3C" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.8"/>
        </svg>
      </div>
      <div style={{ color: T.textMid, fontSize: 13 }}>Chargement de tes données…</div>
    </div>
  );

  // ── Onboarding ────────────────────────────────────────────────────────────
  if (!profile.onboarded) return (
    <Onboarding onComplete={(p) => setProfile({ ...p, onboarded: true })} />
  );

  const showToast = (msg) => { setToast(msg); };

  const addRun = async (run) => {
    try {
      await withRateLimit('add_run', async () => {
        const t = run.trimp || calcTrimp(run.duree, run.fc_moy, profile.fc_repos || 52, profile.fc_max || 192, run.type || 'fond');
        const enriched = { ...run, trimp: t };
        if (userId && db) {
          const saved = await db.addRun(userId, enriched);
          if (saved) enriched.id = saved.id;
        }
        setRuns(prev => [...prev, enriched].sort((a, b) => a.date > b.date ? 1 : -1));
        showToast("Séance enregistrée !");
      });
    } catch (e) {
      if (e instanceof RateLimitError) { showToast(e.message); }
      else throw e;
    }
  };

  // Import CSV bulk — ajoute TOUTES les activités en une seule mise à jour d'état
  const addManyRuns = (newRuns) => {
    const enriched = newRuns.map((run, i) => ({
      ...run,
      id: Date.now() + i,
      ressenti: run.ressenti || 3,
      fc_max: run.fc_max || 0,
      type: run.type || "fond",
      trimp: run.trimp || calcTrimp(run.duree, run.fc_moy, profile.fc_repos || 52, profile.fc_max || 192, run.type || "fond"),
    }));
    setRuns(prev => {
      // Dédoublonnage : on considère qu'une activité est un doublon si même date + même distance (±0.05km)
      const isDuplicate = (run) => prev.some(p =>
        p.date === run.date && Math.abs((p.distance||0) - (run.distance||0)) < 0.05
      );
      const newOnly = enriched.filter(r => !isDuplicate(r));
      const skipped = enriched.length - newOnly.length;
      if (skipped > 0) setTimeout(() => showToast(`${newOnly.length} importée(s), ${skipped} doublon(s) ignoré(s)`), 50);
      else showToast(`${newOnly.length} activité${newOnly.length > 1 ? "s" : ""} importée${newOnly.length > 1 ? "s" : ""} !`);
      return [...prev, ...newOnly].sort((a, b) => a.date > b.date ? 1 : -1);
    });
  };
  const addRace = async (r) => {
    const race = { ...r, splits: r.splits||[], nutrition: r.nutrition||[], tapering: r.tapering||[] };
    if (userId && db) {
      const saved = await db.addRace(userId, race);
      if (saved) race.id = saved.id;
    }
    setRaces(prev => [...prev, race].sort((a, b) => a.date > b.date ? 1 : -1));
    showToast("Course ajoutée !");
  };

  const acwr = calcACWR(runs);
  const weekKm = (() => {
    const now = new Date(); now.setHours(0,0,0,0);
    const monday = new Date(now); monday.setDate(now.getDate() - ((now.getDay()+6)%7));
    return runs.filter(r => { const d = new Date(r.date); return d >= monday && d <= now && getSportFromType(r.type||"fond")==="run"; }).reduce((a,b)=>a+b.distance,0).toFixed(0);
  })();

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: T.fontUI, color: T.text }}>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input, select, textarea { outline: none; }
        input::placeholder, textarea::placeholder { color: #B0A090; }
        input[type=range] { -webkit-appearance: none; height: 4px; border-radius: 2px; background: #EAE4D8; }
        input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; width: 16px; height: 16px; border-radius: 50%; background: #C49A3C; cursor: pointer; border: 2px solid #fff; box-shadow: 0 1px 4px rgba(0,0,0,0.2); }
        @keyframes popIn { from{opacity:0;transform:scale(.96) translateY(4px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes slideUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse { 0%,80%,100%{opacity:.3;transform:scale(.7)} 40%{opacity:1;transform:scale(1)} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        button { font-family: inherit; }
      `}</style>

      {/* Orange top rule — signature Strata */}
      <div style={{ height: 3, background: "#C49A3C", position: "sticky", top: 0, zIndex: 101 }} />

      {/* Top bar */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100,
        background: "rgba(246,241,232,0.97)",
        backdropFilter: "blur(12px)",
        borderBottom: "0.5px solid #EAE4D8",
        boxShadow: "0 1px 0 rgba(26,21,16,0.04)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 28px", height: 52,
        fontFamily: T.fontUI,
      }}>
        {/* Brand */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Logo GRAVV — fond sombre dans header */}
          <div style={{
            width: 28, height: 28, borderRadius: 7,
            background: "#C49A3C",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <svg width="14" height="17" viewBox="0 0 22 26" fill="none">
              <path d="M2 2 L11 13 L20 2" stroke="#1A1510" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12 L11 23 L20 12" stroke="#C49A3C" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.9"/>
            </svg>
          </div>
          <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 16, letterSpacing: "0.02em", color: "#1A1510" }}>GRAVV</span>
          {!isMobile && <><div style={{ width: 1, height: 16, background: "#EAE4D8", margin: "0 4px" }} />
          <span style={{ color: "#B0A090", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "'Bricolage Grotesque', sans-serif" }}>Performance Analytics</span></>}
        </div>
        {/* Stat chips — desktop only */}
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {!isMobile && [
            { label: "ACWR",   value: acwr.toFixed(2), color: acwrColor(acwr) },
            { label: "Volume", value: `${weekKm} km`, color: T.textMid },
          ].map((s, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 5,
              padding: "4px 10px",
              background: "#FFFFFF",
              border: "0.5px solid #EAE4D8",
              borderRadius: 4,
              boxShadow: "0 1px 0 rgba(26,21,16,0.04)",
            }}>
              <span style={{ fontSize: 9, fontWeight: 500, color: "#B0A090", letterSpacing: "0.09em", textTransform: "uppercase", fontFamily: T.fontUI }}>{s.label}</span>
              <span style={{ fontSize: 12, fontWeight: 500, color: s.color, fontFamily: T.fontData, fontVariantNumeric:"tabular-nums", fontFeatureSettings:'"tnum"' }}>{s.value}</span>
            </div>
          ))}
          <Btn size="sm" variant="lime" onClick={() => setModal("addRun")}>+ Séance</Btn>
          {session && supabase && (
            <button onClick={() => supabase.auth.signOut()} title="Se déconnecter" style={{
              background: "none", border: `0.5px solid ${T.border}`, borderRadius: 6,
              padding: "5px 10px", cursor: "pointer", fontSize: 11, color: T.textLight,
              fontFamily: T.fontUI, display: "flex", alignItems: "center", gap: 4,
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.borderStrong}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
              ⎋ Déconnexion
            </button>
          )}
        </div>
      </header>

      {/* Nav — desktop only (mobile uses bottom bar) */}
      {!isMobile && <nav style={{
        background: "rgba(246,241,232,0.97)",
        borderBottom: "0.5px solid #EAE4D8",
        display: "flex",
        padding: "0 28px",
        overflowX: "auto",
        fontFamily: T.fontUI,
      }}>
        {NAV.map(n => (
          <button
            key={n.id}
            onClick={() => setTab(n.id)}
            style={{
              padding: "12px 16px",
              border: "none",
              background: "none",
              cursor: "pointer",
              color: tab === n.id ? "#1A1510" : "#B0A090",
              fontWeight: tab === n.id ? 600 : 400,
              fontSize: 12,
              fontFamily: "inherit",
              letterSpacing: 0.2,
              borderBottom: `2px solid ${tab === n.id ? "#C49A3C" : "transparent"}`,
              borderTop: "2px solid transparent",
              transition: "color 0.12s, border-color 0.12s",
              display: "flex", alignItems: "center", gap: 5,
              whiteSpace: "nowrap",
            }}
            onMouseEnter={e => { if (tab !== n.id) e.currentTarget.style.color = "#555"; }}
            onMouseLeave={e => { if (tab !== n.id) e.currentTarget.style.color = "#B0A090"; }}
          >
            <span style={{ fontSize: 13, opacity: tab === n.id ? 1 : 0.6 }}>{n.icon}</span>
            {n.label}
          </button>
        ))}
      </nav>}

      {/* Content */}
      <main style={{ padding: isMobile ? "16px 16px 84px" : "20px clamp(12px, 3vw, 28px) 60px", maxWidth: 1280, margin: "0 auto" }}>
        {tab === "dashboard"   && <TabDashboard profile={profile} runs={runs} races={races} setModal={setModal} isMobile={isMobile} />}
        {tab === "runs"        && <TabRuns runs={runs} setRuns={setRuns} setModal={setModal} setEditItem={setEditItem} isMobile={isMobile} onDeleteRun={(id) => { setRuns(prev => prev.filter(x => x.id !== id)); if (userId && db) db.deleteRun(userId, id); showToast("Séance supprimée"); }} />}
        {tab === "charge"      && <TabCharge runs={runs} profile={profile} />}
        {tab === "planning" && <TabPlanning races={races} setRaces={setRaces} setModal={setModal} setEditItem={setEditItem} setTab={setTab}
          onDeleteRace={(id) => { setRaces(prev => prev.filter(r => r.id !== id)); if (userId && db) db.deleteRace(userId, id); showToast("Course supprimée"); }}
          onUpdateRace={(r) => { setRaces(prev => prev.map(x => x.id === r.id ? r : x)); if (userId && db) db.updateRace(userId, r); }}
          onOpenTrailPlan={(race) => { setTrailPlanRace(race); setTab("predictions"); }}/>}
        {tab === "predictions" && <TabPredictions profile={profile} runs={runs} races={races} addRace={addRace} onUpdateRace={(r) => { setRaces(prev => prev.map(x => x.id === r.id ? r : x)); if (userId && db) db.updateRace(userId, r); }} trailPlanRace={trailPlanRace} clearTrailPlanRace={()=>setTrailPlanRace(null)} predState={predState} setPredState={setPredState} />}
        {tab === "racebook"   && <TabRaceBook races={races} profile={profile} addRace={addRace} session={session} onUpdateRace={(r) => { setRaces(prev => prev.map(x => x.id === r.id ? r : x)); if (userId && db) db.updateRace(userId, r); }} />}
        {tab === "raceplan"   && <TabRacePlan races={races} profile={profile} onUpdateRace={(r) => { setRaces(prev => prev.map(x => x.id === r.id ? r : x)); if (userId && db) db.updateRace(userId, r); }} />}
        {tab === "coach"       && <CoachPage profile={profile} runs={runs} sleep={[]} races={races} addRun={addRun} />}
        {tab === "profile"     && <TabProfile profile={profile} setProfile={setProfile} stravaConnected={stravaConnected} stravaStatus={stravaStatus} stravaMsg={stravaMsg} onStravaSync={() => syncStravaActivities()} />}
      </main>

      {/* ── Mobile: bottom tab bar + drawer ────────────────────────────────── */}
      {isMobile && (<>
        {/* Drawer overlay */}
        {showDrawer && (
          <div style={{ position: "fixed", inset: 0, zIndex: 299, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <div onClick={() => setShowDrawer(false)}
              style={{ position: "absolute", inset: 0, background: "rgba(26,21,16,0.45)" }} />
            <div style={{ position: "relative", background: T.surface, borderRadius: "16px 16px 0 0",
              paddingBottom: "calc(64px + env(safe-area-inset-bottom, 0px))", boxShadow: T.shadowLg,
              animation: "slideUp 0.2s ease" }}>
              <div style={{ width: 36, height: 4, background: T.border, borderRadius: 2, margin: "12px auto 4px" }} />
              {DRAWER_NAV.map(n => (
                <button key={n.id} onClick={() => { setTab(n.id); setShowDrawer(false); }}
                  style={{ display: "flex", alignItems: "center", gap: 14, width: "100%",
                    padding: "13px 24px", background: tab === n.id ? T.goldLight : "none",
                    border: "none", cursor: "pointer", fontFamily: T.fontUI,
                    fontSize: 14, fontWeight: tab === n.id ? 600 : 400,
                    color: tab === n.id ? T.navy : T.text }}>
                  <span style={{ fontSize: 18, width: 28, textAlign: "center" }}>{n.icon}</span>
                  {n.label}
                </button>
              ))}
              {session && supabase && (
                <button onClick={() => supabase.auth.signOut()}
                  style={{ display: "flex", alignItems: "center", gap: 14, width: "100%",
                    padding: "13px 24px", background: "none", border: "none", cursor: "pointer",
                    fontFamily: T.fontUI, fontSize: 14, color: T.textLight, borderTop: `0.5px solid ${T.border}`, marginTop: 4 }}>
                  <span style={{ fontSize: 18, width: 28, textAlign: "center" }}>⎋</span>
                  Déconnexion
                </button>
              )}
            </div>
          </div>
        )}
        {/* Bottom tab bar */}
        <nav style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200,
          background: T.surface, borderTop: `0.5px solid ${T.border}`,
          display: "flex", paddingBottom: "env(safe-area-inset-bottom, 0px)",
          boxShadow: "0 -1px 0 rgba(26,21,16,0.06), 0 -4px 16px rgba(26,21,16,0.07)" }}>
          {BOTTOM_NAV.map(n => {
            const active = tab === n.id && !showDrawer;
            return (
              <button key={n.id} onClick={() => { setTab(n.id); setShowDrawer(false); }}
                style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                  gap: 2, padding: "8px 0 6px", background: "none", border: "none", cursor: "pointer" }}>
                <span style={{ fontSize: 21, lineHeight: 1, opacity: active ? 1 : 0.4 }}>{n.icon}</span>
                <span style={{ fontSize: 9, fontWeight: active ? 700 : 400, fontFamily: T.fontUI,
                  color: active ? T.gold : T.textMid, letterSpacing: 0.3 }}>{n.label}</span>
              </button>
            );
          })}
          <button onClick={() => setShowDrawer(v => !v)}
            style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
              gap: 2, padding: "8px 0 6px", background: "none", border: "none", cursor: "pointer" }}>
            <span style={{ fontSize: 21, lineHeight: 1, opacity: showDrawer ? 1 : 0.4 }}>☰</span>
            <span style={{ fontSize: 9, fontWeight: showDrawer ? 700 : 400, fontFamily: T.fontUI,
              color: showDrawer ? T.gold : T.textMid, letterSpacing: 0.3 }}>Plus</span>
          </button>
        </nav>
      </>)}

      {/* Modals */}
      {modal === "addRun" && <ModalAddRun
        onClose={() => { setModal(null); setEditItem(null); }}
        initialData={editItem}
        onAdd={(r) => {
          if (editItem) {
            const t = calcTrimp(r.duree, r.fc_moy, profile.fc_repos||52, profile.fc_max||192, r.type||"fond");
            const updated = {...r, trimp: t};
            setRuns(prev => prev.map(x => x.id === r.id ? updated : x));
            if (userId && db) db.updateRun(userId, updated);
            showToast("Séance modifiée !");
          } else { addRun(r); }
        }}
        onDelete={(id) => {
          setRuns(prev => prev.filter(x => x.id !== id));
          if (userId && db) db.deleteRun(userId, id);
          showToast("Séance supprimée");
        }}
        onAddMany={addManyRuns}
      />}
      {modal === "addRace"     && <ModalAddRace onClose={() => { setModal(null); setEditItem(null); }}
        initialData={editItem}
        onAdd={async (r) => {
          if (editItem) {
            setRaces(prev => prev.map(x => x.id === r.id ? r : x));
            if (userId && db) db.updateRace(userId, r);
          } else {
            await addRace(r);
          }
        }}
        onDelete={async (id) => {
          setRaces(prev => prev.filter(x => x.id !== id));
          if (userId && db) db.deleteRace(userId, id);
          showToast("Course supprimée");
        }}
      />}

      {/* Toast */}
      {toast && <Toast msg={toast} onClose={() => setToast(null)} />}
    </div>
  );
}